import React, { useState, useEffect } from 'react';
import { Building2, TrendingUp, AlertTriangle, CheckCircle, FileText, Users, AlertCircle, ArrowLeft, Download, Trash2, Eye, Plus } from 'lucide-react';
import toast, { Toaster } from 'react-hot-toast';
import {
  getAllBuildings,
  getPendingVerifications,
  approveImprovement,
  rejectImprovement,
  loginWithEmail,
  logout,
  onAuthChange,
  resetPassword,
  getBuildingsByManager,
  createImprovement,
  submitBuildingClaim,
  getMyBuildingClaims,
  getPendingBuildingClaims,
  approveBuildingClaim,
  rejectBuildingClaim,
  getUserAccountType,
  getBuildingsByLender,
  addBuildingToPortfolio,
  removeBuildingFromPortfolio,
  uploadPropertyDocument,
  getPropertyDocuments,
  deletePropertyDocument,
  saveInspectionNote,
  getInspectionNotes,
  saveDemoRequest,
  signUpB2B,
} from './firebaseB2BService';
import { calculateBuildingRiskScore } from './riskScoringEngine.js';
import SnapshotPurchaseModal from './SnapshotPurchaseModal';
import { TableSkeleton, StatsGridSkeleton, BuildingListSkeleton } from './components/SkeletonLoaders';
import {
  RiskScoreTrendChart,
  PortfolioRiskDistributionChart,
  IssuesByCategoryChart,
  // GeographicComparisonChart, // reserved for future use
  RemediationTimeChart
} from './components/Charts';
import Navigation from './components/Navigation';
import PCATracker from './components/PCATracker';
import './App.css';
import logo from './Assets/ToiceLogoTransparent.png';

function ToiceB2BPlatform() {
  const [currentView, setCurrentView] = useState(() => {
    const hash = window.location.hash.slice(1);
    return hash || 'landing';
  });
  const [userType, setUserType] = useState(null); // used in demo mode
  const [buildings, setBuildings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [verifications, setVerifications] = useState([]);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [user, setUser] = useState(null);
  const [accountType, setAccountType] = useState(null);
  const [selectedBuilding, setSelectedBuilding] = useState(null);
  const [buildingDocuments, setBuildingDocuments] = useState([]);
  const [inspectionNotes, setInspectionNotes] = useState([]);
  const [demoMode, setDemoMode] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [myClaims, setMyClaims] = useState([]);
  const [buildingClaims, setBuildingClaims] = useState([]); // admin: all pending claims

  // Sync currentView → URL hash so pages have shareable URLs (e.g. /#tenants)
  useEffect(() => {
    window.location.hash = currentView === 'landing' ? '' : currentView;
  }, [currentView]);

  // Sync browser back/forward → currentView
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.slice(1);
      setCurrentView(hash || 'landing');
    };
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const colors = {
    primary: '#178B9B',
    orange: '#FF8533',
    success: '#10b981',
    warning: '#f59e0b'
  };

  // ========== DEMO MODE DATA ==========
  const DEMO_BUILDINGS = [
    {
      address: '245 Beacon Street, Boston, MA 02116',
      city: 'Boston',
      state: 'MA',
      numberOfUnits: 42,
      reviews: [
        { category: 'Mold', rating: 1, description: 'Black mold visible in bathroom ceiling and spreading to bedroom wall', unitNumber: '3B', createdAt: { seconds: 1708300800 }, userName: 'Anonymous Tenant' },
        { category: 'Plumbing', rating: 2, description: 'Kitchen sink has been leaking for 3 weeks despite maintenance requests', unitNumber: '5A', createdAt: { seconds: 1709510400 }, userName: 'Anonymous Tenant' },
        { category: 'Heating/Cooling', rating: 2, description: 'Radiator in living room does not turn on. Apartment stays below 60F at night', unitNumber: '2C', createdAt: { seconds: 1710720000 }, userName: 'Anonymous Tenant' },
        { category: 'Safety Issue', rating: 1, description: 'Fire escape ladder on 4th floor is rusted and detached from the wall', unitNumber: '4A', createdAt: { seconds: 1711929600 }, userName: 'Anonymous Tenant' },
        { category: 'Pest Issue', rating: 2, description: 'Cockroach infestation in kitchen. Seen in multiple units on this floor', unitNumber: '1D', createdAt: { seconds: 1713139200 }, userName: 'Anonymous Tenant' },
        { category: 'Maintenance', rating: 3, description: 'Hallway lights on 3rd floor have been out for over a month', unitNumber: '3A', createdAt: { seconds: 1714348800 }, userName: 'Anonymous Tenant' }
      ],
      improvements: [
        { description: 'Completed mold remediation in unit 3B and adjacent units', status: 'completed', remediationDays: 14, createdAt: { seconds: 1712188800 } },
        { description: 'Replaced radiator units on floors 2 and 3', status: 'completed', remediationDays: 21, createdAt: { seconds: 1713398400 } },
        { description: 'Scheduled pest control service for entire building', status: 'approved', createdAt: { seconds: 1714608000 } }
      ],
      sponsor: 'Beacon Capital Partners',
      loanClosedYear: 2021,
      loanMaturesYear: 2028,
      loanAgreement: {
        immediateRepairs: [
          { item: 'Mold remediation in units 3B, 3C, and 4B', estimatedCost: 45000, timeframe: '60 days' },
          { item: 'Fire escape structural repair — 4th floor', estimatedCost: 28000, timeframe: '30 days' },
          { item: 'Pest control treatment — full building', estimatedCost: 8500, timeframe: '14 days' }
        ],
        replacementReserves: [
          { item: 'Roof membrane replacement', estimatedCost: 120000, year: 'Year 1' },
          { item: 'Boiler system upgrade', estimatedCost: 85000, year: 'Year 3' },
          { item: 'Common area flooring replacement', estimatedCost: 35000, year: 'Year 2' },
          { item: 'Elevator modernization', estimatedCost: 150000, year: 'Year 5' }
        ]
      }
    },
    {
      address: '780 Atlantic Avenue, Boston, MA 02111',
      city: 'Boston',
      state: 'MA',
      numberOfUnits: 88,
      reviews: [
        { category: 'Plumbing', rating: 1, description: 'Major water leak from ceiling in unit below. Damage to drywall and flooring', unitNumber: '12F', createdAt: { seconds: 1709164800 }, userName: 'Anonymous Tenant' },
        { category: 'Moisture', rating: 2, description: 'Condensation on all windows causing water damage to window sills', unitNumber: '8B', createdAt: { seconds: 1710374400 }, userName: 'Anonymous Tenant' },
        { category: 'Heating/Cooling', rating: 3, description: 'AC unit rattles loudly and only cools to 78F on hot days', unitNumber: '15A', createdAt: { seconds: 1711584000 }, userName: 'Anonymous Tenant' },
        { category: 'Security Concern', rating: 2, description: 'Front entrance keypad has been broken for 2 weeks. Anyone can enter', unitNumber: '1A', createdAt: { seconds: 1712793600 }, userName: 'Anonymous Tenant' },
        { category: 'Maintenance', rating: 4, description: 'Elevator out of service frequently. Was down 3 times last month', unitNumber: '10C', createdAt: { seconds: 1714003200 }, userName: 'Anonymous Tenant' }
      ],
      improvements: [
        { description: 'Repaired water leak and replaced damaged ceiling drywall', status: 'completed', remediationDays: 7, createdAt: { seconds: 1711238400 } },
        { description: 'Installed new electronic keypad entry system', status: 'completed', remediationDays: 5, createdAt: { seconds: 1713830400 } }
      ],
      sponsor: 'Atlantic Realty Trust',
      loanClosedYear: 2022,
      loanMaturesYear: 2029,
      loanAgreement: {
        immediateRepairs: [
          { item: 'Water damage ceiling repair — unit 12F', estimatedCost: 12000, timeframe: '21 days' },
          { item: 'Window condensation mitigation — floors 7-10', estimatedCost: 34000, timeframe: '45 days' },
          { item: 'Security keypad replacement — all entrances', estimatedCost: 18000, timeframe: '14 days' },
          { item: 'Elevator mechanical overhaul', estimatedCost: 52000, timeframe: '90 days' }
        ],
        replacementReserves: [
          { item: 'HVAC system replacement', estimatedCost: 210000, year: 'Year 2' },
          { item: 'Parking garage waterproofing', estimatedCost: 95000, year: 'Year 1' },
          { item: 'Exterior facade repointing', estimatedCost: 175000, year: 'Year 3' },
          { item: 'Plumbing riser replacement', estimatedCost: 130000, year: 'Year 4' },
          { item: 'Common area renovation', estimatedCost: 60000, year: 'Year 2' }
        ]
      }
    },
    {
      address: '1420 Tremont Street, Boston, MA 02120',
      city: 'Boston',
      state: 'MA',
      numberOfUnits: 24,
      reviews: [
        { category: 'Mold', rating: 1, description: 'Severe mold in basement storage units affecting personal belongings', unitNumber: 'B1', createdAt: { seconds: 1708646400 }, userName: 'Anonymous Tenant' },
        { category: 'Mold', rating: 1, description: 'Mold growing behind bathroom wall tile, visible from grout lines', unitNumber: '2A', createdAt: { seconds: 1709856000 }, userName: 'Anonymous Tenant' },
        { category: 'Pest Issue', rating: 1, description: 'Rat seen in hallway near trash room. Multiple residents have reported', unitNumber: '1B', createdAt: { seconds: 1711065600 }, userName: 'Anonymous Tenant' },
        { category: 'Safety Issue', rating: 1, description: 'Smoke detectors in hallways are not functioning. No batteries', unitNumber: '3A', createdAt: { seconds: 1712275200 }, userName: 'Anonymous Tenant' },
        { category: 'Structural Issue', rating: 2, description: 'Large crack running along exterior wall visible from inside apartment', unitNumber: '3B', createdAt: { seconds: 1713484800 }, userName: 'Anonymous Tenant' },
        { category: 'Plumbing', rating: 2, description: 'Toilet runs constantly. Water bill is extremely high', unitNumber: '2B', createdAt: { seconds: 1714694400 }, userName: 'Anonymous Tenant' },
        { category: 'Heating/Cooling', rating: 1, description: 'Boiler failed. No hot water for entire building for 4 days', unitNumber: '1A', createdAt: { seconds: 1715904000 }, userName: 'Anonymous Tenant' },
        { category: 'Trip Hazard', rating: 3, description: 'Carpet in main hallway is torn and bunched up near stairwell', unitNumber: '2A', createdAt: { seconds: 1717113600 }, userName: 'Anonymous Tenant' }
      ],
      improvements: [
        { description: 'Emergency boiler repair completed', status: 'completed', remediationDays: 4, createdAt: { seconds: 1716249600 } }
      ],
      sponsor: 'Beacon Capital Partners',
      loanClosedYear: 2020,
      loanMaturesYear: 2027,
      loanAgreement: {
        immediateRepairs: [
          { item: 'Smoke detector replacement — all common areas', estimatedCost: 6500, timeframe: '7 days' },
          { item: 'Structural crack assessment and repair — unit 3B', estimatedCost: 22000, timeframe: '30 days' }
        ],
        replacementReserves: [
          { item: 'Boiler system full replacement', estimatedCost: 65000, year: 'Year 1' },
          { item: 'Basement mold abatement and waterproofing', estimatedCost: 48000, year: 'Year 1' },
          { item: 'Exterior stairwell reconstruction', estimatedCost: 38000, year: 'Year 3' }
        ]
      }
    },
    {
      address: '55 Summer Street, Cambridge, MA 02138',
      city: 'Cambridge',
      state: 'MA',
      numberOfUnits: 36,
      reviews: [
        { category: 'Maintenance', rating: 3, description: 'Paint peeling in stairwell. Looks bad but no other issues', unitNumber: '4A', createdAt: { seconds: 1710028800 }, userName: 'Anonymous Tenant' },
        { category: 'Noise', rating: 3, description: 'Construction noise from adjacent building during early morning hours', unitNumber: '6B', createdAt: { seconds: 1712620800 }, userName: 'Anonymous Tenant' },
        { category: 'Heating/Cooling', rating: 4, description: 'Thermostat sometimes takes a while to respond but heating works', unitNumber: '2C', createdAt: { seconds: 1715212800 }, userName: 'Anonymous Tenant' }
      ],
      improvements: [
        { description: 'Repainted all common area stairwells', status: 'completed', remediationDays: 10, createdAt: { seconds: 1713484800 } },
        { description: 'Installed new smart thermostats in all units', status: 'completed', remediationDays: 30, createdAt: { seconds: 1716076800 } },
        { description: 'Sound insulation improvements approved for affected units', status: 'pending', createdAt: { seconds: 1716940800 } }
      ],
      sponsor: 'Atlantic Realty Trust',
      loanClosedYear: 2023,
      loanMaturesYear: 2030,
      loanAgreement: {
        immediateRepairs: [
          { item: 'Thermostat system calibration — all units', estimatedCost: 9500, timeframe: '21 days' },
          { item: 'Sound insulation — units 6A-6D', estimatedCost: 16000, timeframe: '30 days' }
        ],
        replacementReserves: [
          { item: 'Interior paint and common area refresh', estimatedCost: 22000, year: 'Year 1' },
          { item: 'Window replacement — north-facing units', estimatedCost: 78000, year: 'Year 2' },
          { item: 'Landscaping and exterior improvements', estimatedCost: 15000, year: 'Year 1' },
          { item: 'Laundry room equipment replacement', estimatedCost: 12000, year: 'Year 3' }
        ]
      }
    },
    {
      address: '320 Commonwealth Avenue, Boston, MA 02115',
      city: 'Boston',
      state: 'MA',
      numberOfUnits: 60,
      reviews: [
        { category: 'Plumbing', rating: 2, description: 'Low water pressure on floors 5 and 6. Showers barely functional', unitNumber: '5D', createdAt: { seconds: 1709510400 }, userName: 'Anonymous Tenant' },
        { category: 'Maintenance', rating: 3, description: 'Lobby carpet is stained and worn. Needs replacement', unitNumber: '1A', createdAt: { seconds: 1711929600 }, userName: 'Anonymous Tenant' },
        { category: 'Heating/Cooling', rating: 2, description: 'Central AC system leaks water into hallway on hot days', unitNumber: '4B', createdAt: { seconds: 1714348800 }, userName: 'Anonymous Tenant' },
        { category: 'Unauthorized Entry', rating: 2, description: 'Parking garage gate stays open for too long. Non-residents enter freely', unitNumber: '2A', createdAt: { seconds: 1716768000 }, userName: 'Anonymous Tenant' }
      ],
      improvements: [
        { description: 'Water pressure booster pump installed for upper floors', status: 'completed', remediationDays: 12, createdAt: { seconds: 1712793600 } },
        { description: 'AC condensate drain repair approved', status: 'approved', createdAt: { seconds: 1715990400 } }
      ],
      sponsor: 'Commonwealth Development Group',
      loanClosedYear: 2022,
      loanMaturesYear: 2029,
      loanAgreement: {
        immediateRepairs: [
          { item: 'Water pressure booster — floors 5-6', estimatedCost: 18000, timeframe: '21 days' },
          { item: 'AC condensate drain line repair', estimatedCost: 8500, timeframe: '14 days' },
          { item: 'Parking garage gate mechanism replacement', estimatedCost: 14000, timeframe: '30 days' }
        ],
        replacementReserves: [
          { item: 'Lobby and common area carpet replacement', estimatedCost: 28000, year: 'Year 1' },
          { item: 'Central AC compressor replacement', estimatedCost: 95000, year: 'Year 2' },
          { item: 'Parking garage resurfacing', estimatedCost: 55000, year: 'Year 3' },
          { item: 'Fire suppression system update', estimatedCost: 42000, year: 'Year 1' },
          { item: 'Balcony waterproofing — all units', estimatedCost: 68000, year: 'Year 4' }
        ]
      }
    }
  ];

  const getDemoBuildingsWithScores = () => {
    return DEMO_BUILDINGS.map(building => {
      const scoreData = calculateBuildingRiskScore(building);
      return {
        ...building,
        riskScore: scoreData.riskScore,
        riskLevel: scoreData.riskLevel,
        ...scoreData
      };
    });
  };

  const enterDemoMode = (role) => {
    const demoBuildingsWithScores = getDemoBuildingsWithScores();
    setBuildings(demoBuildingsWithScores);
    setDemoMode(true);
    setLoading(false);
    setError(null);
    if (role === 'lender') {
      setAccountType('lender');
      setCurrentView('lender-dashboard');
    } else {
      setAccountType('pm');
      setCurrentView('pm-dashboard');
    }
  };

  const exitDemoMode = () => {
    setDemoMode(false);
    setBuildings([]);
    setSelectedBuilding(null);
    setBuildingDocuments([]);
    setInspectionNotes([]);
    setAccountType(null);
    setCurrentView('landing');
  };
  // ========== END DEMO MODE DATA ==========

  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = onAuthChange(async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        const type = await getUserAccountType(currentUser.uid);
        setAccountType(type);
        // Always redirect to the right dashboard after login (avoids stale-closure bug
        // where currentView captured at mount would never equal 'login')
        setCurrentView(
          type === 'lender' ? 'lender-dashboard'
          : type === 'admin' ? 'admin'
          : 'pm-dashboard'
        );
      } else {
        setAccountType(null);
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (currentView === 'dashboard') {
      console.log('Dashboard view activated, loading buildings...');
      if (buildings.length === 0) {
        loadBuildings();
      }
    }
  }, [currentView]);

  useEffect(() => {
    if (currentView === 'admin') {
      loadVerifications();
    }
  }, [currentView]);

  useEffect(() => {
    if (currentView === 'pm-dashboard' && user && !demoMode) {
      loadManagerBuildings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentView, user]);

  useEffect(() => {
    if (currentView === 'lender-dashboard' && user && !demoMode) {
      loadLenderBuildings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentView, user]);

  useEffect(() => {
    if (currentView === 'building-detail' && selectedBuilding && !demoMode) {
      loadBuildingDetails(selectedBuilding.address);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentView, selectedBuilding]);

  // Auto-exit demo mode when navigating to non-demo views
  useEffect(() => {
    if (demoMode && !['pm-dashboard', 'lender-dashboard', 'building-detail', 'demo'].includes(currentView)) {
      setDemoMode(false);
      setBuildings([]);
      setSelectedBuilding(null);
      setAccountType(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentView, demoMode]);

  const loadLenderBuildings = async () => {
    try {
      setLoading(true);
      setError(null);
      const buildingsData = await getBuildingsByLender(user.uid);

      const buildingsWithScores = buildingsData.map(building => {
        const scoreData = calculateBuildingRiskScore(building);
        return {
          ...building,
          riskScore: scoreData.riskScore,
          riskLevel: scoreData.riskLevel,
          ...scoreData
        };
      });

      setBuildings(buildingsWithScores);
      setLoading(false);
    } catch (err) {
      console.error('Error loading lender buildings:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  const loadBuildingDetails = async (address) => {
    try {
      const docs = await getPropertyDocuments(address);
      setBuildingDocuments(docs);
      const notes = await getInspectionNotes(address);
      setInspectionNotes(notes);
    } catch (err) {
      console.error('Error loading building details:', err);
    }
  };

  const loadBuildings = async () => {
    try {
      console.log('loadBuildings called');
      setLoading(true);
      setError(null);
      const buildingsData = await getAllBuildings();
      console.log('Buildings data fetched:', buildingsData?.length || 0, 'buildings');

      const buildingsWithScores = buildingsData.map(building => {
        const scoreData = calculateBuildingRiskScore(building);
        return {
          ...building,
          riskScore: scoreData.riskScore,
          riskLevel: scoreData.riskLevel,
          ...scoreData
        };
      });

      setBuildings(buildingsWithScores);
      setLoading(false);
      console.log('Buildings loaded successfully');
    } catch (err) {
      console.error('Error loading buildings:', err);
      setError(err.message);
      setLoading(false);
      // Set empty array so dashboard still renders
      setBuildings([]);
    }
  };

  const loadVerifications = async () => {
    try {
      setLoading(true);
      const pendingData = await getPendingVerifications();
      setVerifications(pendingData);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const handleApprove = async (improvementId) => {
    try {
      await approveImprovement(improvementId);
      toast.success('Approved! Risk score updated.');
      await loadVerifications();
    } catch (err) {
      toast.error('Error: ' + err.message);
    }
  };

  const handleReject = async (improvementId) => {
    const reason = prompt('Reason for rejection:');
    if (reason) {
      try {
        await rejectImprovement(improvementId, reason);
        toast.success('Rejected. Landlord notified.');
        await loadVerifications();
      } catch (err) {
        toast.error('Error: ' + err.message);
      }
    }
  };

  const loadManagerBuildings = async () => {
    try {
      setLoading(true);
      setError(null);
      // Load claims in parallel with buildings
      const [buildingsData, claimsData] = await Promise.all([
        getBuildingsByManager(user.uid),
        getMyBuildingClaims(user.uid),
      ]);
      setMyClaims(claimsData);

      const buildingsWithScores = buildingsData.map(building => {
        const scoreData = calculateBuildingRiskScore(building);
        return {
          ...building,
          riskScore: scoreData.riskScore,
          riskLevel: scoreData.riskLevel,
          ...scoreData
        };
      });

      setBuildings(buildingsWithScores);
      setLoading(false);
    } catch (err) {
      console.error('Error loading buildings:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      setUser(null);
      setAccountType(null);
      setBuildings([]);
      setSelectedBuilding(null);
      setBuildingDocuments([]);
      setInspectionNotes([]);
      setCurrentView('landing');
    } catch (err) {
      alert('Error signing out: ' + err.message);
    }
  };

  function ScheduleDemoModal() {
    const [formData, setFormData] = useState({
      name: '',
      email: '',
      company: '',
      product: '',
      preferredTime: ''
    });
    const [submitting, setSubmitting] = useState(false);

    const handleChange = (field, value) => {
      setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      if (!formData.name.trim() || !formData.email.trim()) {
        toast.error('Please fill in your name and email');
        return;
      }
      try {
        setSubmitting(true);
        await saveDemoRequest(formData);
        toast.success('Demo request submitted! We will contact you within 24 hours.');
        setShowScheduleModal(false);
      } catch (err) {
        toast.error('Failed to submit. Please try again.');
      } finally {
        setSubmitting(false);
      }
    };

    const inputStyle = {
      width: '100%',
      padding: '0.75rem',
      border: '1px solid #d1d5db',
      borderRadius: '0.5rem',
      fontSize: '0.95rem',
      outline: 'none',
      boxSizing: 'border-box'
    };
    const labelStyle = { display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '0.35rem' };

    return (
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 200, padding: '1rem'
      }} onClick={() => setShowScheduleModal(false)}>
        <div style={{
          backgroundColor: 'white', borderRadius: '1rem', padding: '2.5rem',
          maxWidth: '480px', width: '100%', maxHeight: '90vh', overflowY: 'auto'
        }} onClick={e => e.stopPropagation()}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#0f172a', marginBottom: '0.5rem' }}>Schedule a Demo</h2>
          <p style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: '1.5rem' }}>
            Tell us a bit about yourself and we'll set up a personalized walkthrough.
          </p>

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div>
              <label style={labelStyle}>Full Name *</label>
              <input type="text" value={formData.name} onChange={e => handleChange('name', e.target.value)} placeholder="Jane Smith" style={inputStyle} />
            </div>

            <div>
              <label style={labelStyle}>Work Email *</label>
              <input type="email" value={formData.email} onChange={e => handleChange('email', e.target.value)} placeholder="jane@company.com" style={inputStyle} />
            </div>

            <div>
              <label style={labelStyle}>Company</label>
              <input type="text" value={formData.company} onChange={e => handleChange('company', e.target.value)} placeholder="Acme Property Management" style={inputStyle} />
            </div>

            <div>
              <label style={labelStyle}>Product Interest</label>
              <select value={formData.product} onChange={e => handleChange('product', e.target.value)} style={inputStyle}>
                <option value="">Select a product...</option>
                <option value="Portfolio Intelligence">Portfolio Intelligence (Lenders/Investors)</option>
                <option value="PM Dashboard">Property Manager Dashboard</option>
                <option value="Snapshot Report">Single Building Snapshot Report</option>
                <option value="Multiple / Not Sure">Multiple / Not Sure Yet</option>
              </select>
            </div>

            <div>
              <label style={labelStyle}>Preferred Time</label>
              <select value={formData.preferredTime} onChange={e => handleChange('preferredTime', e.target.value)} style={inputStyle}>
                <option value="">Select a time...</option>
                <option value="Morning (9am-12pm ET)">Morning (9am-12pm ET)</option>
                <option value="Afternoon (12pm-3pm ET)">Afternoon (12pm-3pm ET)</option>
                <option value="Late Afternoon (3pm-5pm ET)">Late Afternoon (3pm-5pm ET)</option>
                <option value="Flexible">Flexible</option>
              </select>
            </div>

            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.5rem' }}>
              <button
                type="button"
                onClick={() => setShowScheduleModal(false)}
                style={{ flex: 1, padding: '0.75rem', backgroundColor: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '0.5rem', fontSize: '1rem', fontWeight: '600', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                style={{ flex: 1, padding: '0.75rem', backgroundColor: colors.orange, color: 'white', border: 'none', borderRadius: '0.5rem', fontSize: '1rem', fontWeight: '600', cursor: submitting ? 'not-allowed' : 'pointer', opacity: submitting ? 0.7 : 1 }}
              >
                {submitting ? 'Submitting...' : 'Request Demo'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  function DemoPage() {
    const cardStyle = {
      flex: '1',
      minWidth: '280px',
      maxWidth: '480px',
      backgroundColor: 'rgba(30, 41, 59, 0.7)',
      border: '1px solid #334155',
      borderRadius: '1rem',
      padding: '2.5rem',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
      transition: 'transform 0.2s, border-color 0.2s'
    };

    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(to bottom right, #0f172a, #1e293b, #0f172a)',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <header style={{ backgroundColor: 'rgba(15, 23, 42, 0.5)', borderBottom: '1px solid #334155', padding: '1rem' }}>
          <div style={{ maxWidth: '1280px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div
              style={{ display: 'flex', alignItems: 'center', gap: '1rem', cursor: 'pointer' }}
              onClick={() => setCurrentView('landing')}
            >
              <img src={logo} alt="Toice Logo" style={{ height: '80px', width: 'auto' }} />
              <div>
                <h1 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'white', margin: 0 }}>Toice Risk Intelligence</h1>
                <p style={{ fontSize: '0.75rem', color: '#94a3b8', margin: 0 }}>Risk Analytics Platform</p>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '1rem' }}>
              <button onClick={() => setCurrentView('landing')} style={{ padding: '0.5rem 1rem', color: '#cbd5e1', background: 'none', border: 'none', cursor: 'pointer', fontWeight: '500' }}>
                Back to Home
              </button>
              <button onClick={() => setCurrentView('login')} style={{ padding: '0.5rem 1.5rem', backgroundColor: colors.primary, color: 'white', border: 'none', borderRadius: '0.5rem', cursor: 'pointer', fontWeight: '600' }}>
                Sign In
              </button>
            </div>
          </div>
        </header>

        <section style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '4rem 1.5rem' }}>
          <h2 style={{ fontSize: '2.5rem', fontWeight: 'bold', color: 'white', marginBottom: '1rem', textAlign: 'center' }}>
            Try <span style={{ color: colors.primary }}>Toice Risk Intelligence</span>
          </h2>
          <p style={{ fontSize: '1.15rem', color: '#94a3b8', marginBottom: '3rem', textAlign: 'center', maxWidth: '600px' }}>
            Explore the platform with sample data. No login required.
          </p>

          <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap', justifyContent: 'center', maxWidth: '1000px' }}>
            {/* PM Card */}
            <div
              style={cardStyle}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = colors.primary; e.currentTarget.style.transform = 'translateY(-4px)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#334155'; e.currentTarget.style.transform = 'translateY(0)'; }}
            >
              <div style={{ width: '64px', height: '64px', borderRadius: '50%', backgroundColor: 'rgba(23, 139, 155, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1.5rem' }}>
                <Building2 size={32} style={{ color: colors.primary }} />
              </div>
              <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'white', marginBottom: '0.75rem' }}>Property Manager</h3>
              <p style={{ color: '#94a3b8', lineHeight: '1.7', marginBottom: '2rem', fontSize: '0.95rem' }}>
                See how PMs track tenant issues, upload repair documentation, and reduce risk scores across their portfolio.
              </p>
              <button
                onClick={() => enterDemoMode('pm')}
                style={{ padding: '0.875rem 2rem', backgroundColor: colors.primary, color: 'white', border: 'none', borderRadius: '0.5rem', fontSize: '1rem', fontWeight: '600', cursor: 'pointer', width: '100%' }}
              >
                Try PM Dashboard
              </button>
            </div>

            {/* Lender Card */}
            <div
              style={cardStyle}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = colors.orange; e.currentTarget.style.transform = 'translateY(-4px)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#334155'; e.currentTarget.style.transform = 'translateY(0)'; }}
            >
              <div style={{ width: '64px', height: '64px', borderRadius: '50%', backgroundColor: 'rgba(255, 133, 51, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1.5rem' }}>
                <TrendingUp size={32} style={{ color: colors.orange }} />
              </div>
              <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'white', marginBottom: '0.75rem' }}>Lender / Investor</h3>
              <p style={{ color: '#94a3b8', lineHeight: '1.7', marginBottom: '2rem', fontSize: '0.95rem' }}>
                Monitor portfolio risk, view building details, and access risk intelligence reports across your investments.
              </p>
              <button
                onClick={() => enterDemoMode('lender')}
                style={{ padding: '0.875rem 2rem', backgroundColor: colors.orange, color: 'white', border: 'none', borderRadius: '0.5rem', fontSize: '1rem', fontWeight: '600', cursor: 'pointer', width: '100%' }}
              >
                Try Lender Dashboard
              </button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  function LandingPage() {
    const [showDemoDropdown, setShowDemoDropdown] = useState(false);

    // Close dropdown on outside click
    React.useEffect(() => {
      if (!showDemoDropdown) return;
      const handler = () => setShowDemoDropdown(false);
      document.addEventListener('click', handler);
      return () => document.removeEventListener('click', handler);
    }, [showDemoDropdown]);

    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(to bottom right, #0f172a, #1e293b, #0f172a)',
        backgroundImage: `
          linear-gradient(to bottom right, #0f172a, #1e293b, #0f172a),
          repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(23, 139, 155, 0.03) 35px, rgba(23, 139, 155, 0.03) 70px),
          repeating-linear-gradient(-45deg, transparent, transparent 35px, rgba(255, 133, 51, 0.02) 35px, rgba(255, 133, 51, 0.02) 70px)
        `
      }}>
        <header style={{ backgroundColor: 'rgba(15, 23, 42, 0.5)', borderBottom: '1px solid #334155', padding: '1rem' }}>
          <div style={{ maxWidth: '1280px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <img src={logo} alt="Toice Logo" style={{ height: '96px', width: 'auto' }} />
              <div>
                <h1 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'white', margin: 0 }}>Toice Risk Intelligence</h1>
                <p style={{ fontSize: '0.75rem', color: '#94a3b8', margin: 0 }}>Real Tenant Data • Risk Analytics</p>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
              <button onClick={() => setCurrentView('tenants')} style={{ padding: '0.5rem 1rem', color: '#cbd5e1', background: 'none', border: 'none', cursor: 'pointer', fontWeight: '500' }}>
                For Tenants
              </button>
              {user ? (
                <>
                  <span style={{ color: '#94a3b8', fontSize: '0.875rem' }}>{user.email}</span>
                  <button onClick={() => setCurrentView(accountType === 'lender' ? 'lender-dashboard' : 'pm-dashboard')} style={{ padding: '0.5rem 1.25rem', backgroundColor: colors.primary, color: 'white', border: 'none', borderRadius: '0.5rem', cursor: 'pointer', fontWeight: '600' }}>
                    Dashboard
                  </button>
                  <button onClick={handleLogout} style={{ padding: '0.5rem 1rem', color: '#94a3b8', background: 'none', border: '1px solid #334155', borderRadius: '0.5rem', cursor: 'pointer', fontWeight: '500', fontSize: '0.875rem' }}>
                    Sign Out
                  </button>
                </>
              ) : (
                <button onClick={() => setCurrentView('login')} style={{ padding: '0.5rem 1rem', color: '#cbd5e1', background: 'none', border: 'none', cursor: 'pointer', fontWeight: '500' }}>
                  Sign In
                </button>
              )}
                <div style={{ position: 'relative' }}>
                <button
                  onClick={(e) => { e.stopPropagation(); setShowDemoDropdown(v => !v); }}
                  style={{ padding: '0.5rem 1.25rem', backgroundColor: 'rgba(124, 58, 237, 0.15)', color: '#a78bfa', border: '1px solid #7c3aed', borderRadius: '0.5rem', cursor: 'pointer', fontWeight: '600', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}
                >
                  Interactive Demo <span style={{ fontSize: '0.65rem', marginTop: '1px' }}>▾</span>
                </button>
                {showDemoDropdown && (
                  <div onClick={e => e.stopPropagation()} style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '0.625rem', padding: '0.5rem', minWidth: '200px', zIndex: 100, boxShadow: '0 8px 24px rgba(0,0,0,0.4)' }}>
                    <p style={{ color: '#64748b', fontSize: '0.7rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0.25rem 0.5rem 0.5rem', padding: 0 }}>Try as…</p>
                    <button
                      onClick={() => { setShowDemoDropdown(false); enterDemoMode('pm'); }}
                      style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', width: '100%', padding: '0.6rem 0.75rem', background: 'none', border: 'none', borderRadius: '0.375rem', cursor: 'pointer', color: 'white', fontSize: '0.875rem', fontWeight: '500', textAlign: 'left' }}
                      onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(23,139,155,0.15)'}
                      onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <Building2 size={15} style={{ color: colors.primary, flexShrink: 0 }} />
                      <div>
                        <div style={{ fontWeight: '600', color: 'white' }}>Property Manager</div>
                        <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Manage buildings & remediations</div>
                      </div>
                    </button>
                    <button
                      onClick={() => { setShowDemoDropdown(false); enterDemoMode('lender'); }}
                      style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', width: '100%', padding: '0.6rem 0.75rem', background: 'none', border: 'none', borderRadius: '0.375rem', cursor: 'pointer', color: 'white', fontSize: '0.875rem', fontWeight: '500', textAlign: 'left' }}
                      onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(255,133,51,0.12)'}
                      onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <TrendingUp size={15} style={{ color: colors.orange, flexShrink: 0 }} />
                      <div>
                        <div style={{ fontWeight: '600', color: 'white' }}>Lender / Investor</div>
                        <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Portfolio risk & analytics</div>
                      </div>
                    </button>
                    <div style={{ borderTop: '1px solid #334155', margin: '0.4rem 0.5rem', paddingTop: '0.4rem' }}>
                      <p style={{ color: '#475569', fontSize: '0.7rem', textAlign: 'center', margin: 0 }}>Sample data · No account needed</p>
                    </div>
                  </div>
                )}
              </div>
              <button onClick={() => setCurrentView('pricing')} style={{ padding: '0.5rem 1.5rem', backgroundColor: colors.primary, color: 'white', border: 'none', borderRadius: '0.5rem', cursor: 'pointer', fontWeight: '600' }}>
                Products & Pricing
              </button>
            </div>
          </div>
        </header>

        <section style={{ maxWidth: '1280px', margin: '0 auto', padding: '5rem 1.5rem', textAlign: 'center' }}>
          <h2 style={{ fontSize: '3rem', fontWeight: 'bold', color: 'white', marginBottom: '1.5rem' }}>
            Real Tenant Data. Better Decisions.
            <br />
            <span style={{ color: colors.primary }}>Toice Risk Intelligence</span>
          </h2>
          <p style={{ fontSize: '1.25rem', color: '#cbd5e1', marginBottom: '2rem', maxWidth: '48rem', margin: '0 auto 2rem' }}>
            Access verified building condition data from actual residents. Make informed lending, insurance, and property management decisions with real-time risk scoring.
          </p>
          <div className="hero-buttons" style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button onClick={() => setCurrentView('pricing')} style={{ padding: '1rem 2rem', backgroundColor: colors.primary, color: 'white', border: 'none', borderRadius: '0.5rem', fontSize: '1.125rem', fontWeight: '600', cursor: 'pointer' }}>
              See Pricing
            </button>
            <button onClick={() => { setCurrentView('dashboard'); setUserType('demo'); }} style={{ padding: '1rem 2rem', backgroundColor: '#475569', color: 'white', border: 'none', borderRadius: '0.5rem', fontSize: '1.125rem', fontWeight: '600', cursor: 'pointer' }}>
              View Live Data
            </button>
            <button onClick={async () => {
              try {
                const { pdf } = await import('@react-pdf/renderer');
                const BuildingReportPDF = (await import('./BuildingReportPDF')).default;
                const { calculateBuildingRiskScore } = await import('./riskScoringEngine.js');

                // Example building data
                const exampleBuilding = {
                  address: '123 Example Street, Boston, MA 02101',
                  numberOfUnits: 50,
                  reviews: [
                    { category: 'Mold', rating: 1 },
                    { category: 'Heating/Cooling', rating: 2 },
                    { category: 'Plumbing', rating: 3 },
                    { category: 'Maintenance', rating: 4 },
                    { category: 'Pest Issue', rating: 2 }
                  ],
                  improvements: [
                    { description: 'Fixed HVAC system in Building A', status: 'completed' },
                    { description: 'Repaired leaking pipes in units 301-305', status: 'completed' }
                  ]
                };

                const riskData = calculateBuildingRiskScore(exampleBuilding);
                const blob = await pdf(<BuildingReportPDF building={exampleBuilding} riskData={riskData} />).toBlob();
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `Toice_Example_Report_${new Date().toISOString().split('T')[0]}.pdf`;
                link.click();
                URL.revokeObjectURL(url);
              } catch (err) {
                console.error('Error generating example PDF:', err);
                toast.error('Failed to generate example PDF. Please try again.');
              }
            }} style={{ padding: '1rem 2rem', backgroundColor: '#059669', color: 'white', border: 'none', borderRadius: '0.5rem', fontSize: '1.125rem', fontWeight: '600', cursor: 'pointer' }}>
              Example PDF Report
            </button>
            <button onClick={() => setShowScheduleModal(true)} style={{ padding: '1rem 2rem', backgroundColor: colors.orange, color: 'white', border: 'none', borderRadius: '0.5rem', fontSize: '1.125rem', fontWeight: '600', cursor: 'pointer' }}>
              Schedule Demo
            </button>
            <button onClick={() => setCurrentView('demo')} style={{ padding: '1rem 2rem', backgroundColor: '#7c3aed', color: 'white', border: 'none', borderRadius: '0.5rem', fontSize: '1.125rem', fontWeight: '600', cursor: 'pointer' }}>
              Interactive Demo
            </button>
          </div>
        </section>

        <section style={{ maxWidth: '1280px', margin: '0 auto', padding: '4rem 1.5rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem' }}>
          <div style={{ backgroundColor: 'rgba(30, 41, 59, 0.5)', border: '1px solid #334155', borderRadius: '0.75rem', padding: '1.5rem' }}>
            <Building2 style={{ color: colors.primary, marginBottom: '1rem' }} size={32} />
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'white', marginBottom: '0.25rem' }}>Live</div>
            <div style={{ color: '#94a3b8', fontSize: '0.875rem' }}>Real tenant data</div>
          </div>
          <div style={{ backgroundColor: 'rgba(30, 41, 59, 0.5)', border: '1px solid #334155', borderRadius: '0.75rem', padding: '1.5rem' }}>
            <CheckCircle style={{ color: colors.primary, marginBottom: '1rem' }} size={32} />
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'white', marginBottom: '0.25rem' }}>Verified</div>
            <div style={{ color: '#94a3b8', fontSize: '0.875rem' }}>Location-verified reviews</div>
          </div>
          <div style={{ backgroundColor: 'rgba(30, 41, 59, 0.5)', border: '1px solid #334155', borderRadius: '0.75rem', padding: '1.5rem' }}>
            <AlertTriangle style={{ color: colors.primary, marginBottom: '1rem' }} size={32} />
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'white', marginBottom: '0.25rem' }}>Data-Driven</div>
            <div style={{ color: '#94a3b8', fontSize: '0.875rem' }}>Proprietary risk analytics</div>
          </div>
          <div style={{ backgroundColor: 'rgba(30, 41, 59, 0.5)', border: '1px solid #334155', borderRadius: '0.75rem', padding: '1.5rem' }}>
            <TrendingUp style={{ color: colors.primary, marginBottom: '1rem' }} size={32} />
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'white', marginBottom: '0.25rem' }}>Actionable</div>
            <div style={{ color: '#94a3b8', fontSize: '0.875rem' }}>Clear recommendations</div>
          </div>
        </section>

        {/* Footer */}
        <footer style={{ borderTop: '1px solid #334155', padding: '3rem 1.5rem', marginTop: '4rem' }}>
          <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '2rem' }}>
              <div>
                <p style={{ color: '#94a3b8', fontSize: '0.875rem', margin: 0 }}>
                  © 2026 Toice LLC. All rights reserved.
                </p>
              </div>
              <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
                <a href="/privacy" onClick={(e) => { e.preventDefault(); setCurrentView('privacy'); }} style={{ color: '#cbd5e1', fontSize: '0.875rem', textDecoration: 'none', cursor: 'pointer' }}>
                  Privacy Policy
                </a>
                <a href="/terms" onClick={(e) => { e.preventDefault(); setCurrentView('terms'); }} style={{ color: '#cbd5e1', fontSize: '0.875rem', textDecoration: 'none', cursor: 'pointer' }}>
                  Terms & Conditions
                </a>
              </div>
            </div>
          </div>
        </footer>
      </div>
    );
  }

  function TenantsPage() {
    return (
      <div style={{ minHeight: '100vh', background: 'linear-gradient(to bottom right, #0f172a, #1e293b, #0f172a)' }}>
        <Navigation currentView={currentView} setCurrentView={setCurrentView} user={user} colors={colors} onLogout={() => logout()} accountType={accountType} demoMode={demoMode} onExitDemo={exitDemoMode} />

        {/* Hero */}
        <section style={{ maxWidth: '1280px', margin: '0 auto', padding: '5rem 1.5rem', textAlign: 'center' }}>
          <h2 style={{ fontSize: '2.75rem', fontWeight: 'bold', color: 'white', marginBottom: '1.5rem' }}>
            Your Voice. Your Building.
            <br />
            <span style={{ color: colors.primary }}>Your Power to Change It.</span>
          </h2>
          <p style={{ fontSize: '1.25rem', color: '#cbd5e1', maxWidth: '48rem', margin: '0 auto 2rem' }}>
            Toice gives tenants a direct line to hold property managers accountable. Report building conditions, track whether issues get fixed, and make your voice count in the buildings where you live.
          </p>
        </section>

        {/* Why Tenants Use Toice */}
        <section style={{ maxWidth: '1280px', margin: '0 auto', padding: '0 1.5rem 4rem' }}>
          <h3 style={{ fontSize: '1.75rem', fontWeight: 'bold', color: 'white', textAlign: 'center', marginBottom: '2rem' }}>Why Tenants Use Toice</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem' }}>

            <div style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '0.75rem', padding: '2rem' }}>
              <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>&#128227;</div>
              <h4 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: 'white', marginBottom: '0.75rem' }}>Report Building Conditions</h4>
              <p style={{ color: '#94a3b8', lineHeight: '1.7' }}>Document maintenance issues, safety hazards, and building conditions directly from your phone. Your reports are timestamped, categorized, and tracked so nothing falls through the cracks.</p>
            </div>

            <div style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '0.75rem', padding: '2rem' }}>
              <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>&#128200;</div>
              <h4 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: 'white', marginBottom: '0.75rem' }}>Real Data Creates Real Accountability</h4>
              <p style={{ color: '#94a3b8', lineHeight: '1.7' }}>Your reports feed directly into building risk scores that lenders, insurers, and investors rely on. When you report a problem, the people with financial leverage over your landlord see it. That is how things get fixed.</p>
            </div>

            <div style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '0.75rem', padding: '2rem' }}>
              <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>&#128274;</div>
              <h4 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: 'white', marginBottom: '0.75rem' }}>Anonymous and Protected</h4>
              <p style={{ color: '#94a3b8', lineHeight: '1.7' }}>Your identity is never shared with your property manager or landlord. Report freely without fear of retaliation. Toice protects your privacy while amplifying your voice.</p>
            </div>

            <div style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '0.75rem', padding: '2rem' }}>
              <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>&#128197;</div>
              <h4 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: 'white', marginBottom: '0.75rem' }}>Track Progress Over Time</h4>
              <p style={{ color: '#94a3b8', lineHeight: '1.7' }}>See whether reported issues are being addressed. Toice tracks building conditions over time so you can see if your building is getting better or getting worse, backed by data rather than promises.</p>
            </div>

            <div style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '0.75rem', padding: '2rem' }}>
              <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>&#127968;</div>
              <h4 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: 'white', marginBottom: '0.75rem' }}>Know Before You Sign a Lease</h4>
              <p style={{ color: '#94a3b8', lineHeight: '1.7' }}>Moving into a new building? See the most common issues tenants have reported before you sign. Find out what current and past residents say about maintenance responsiveness, safety concerns, and building conditions so you know what you are walking into.</p>
            </div>

            <div style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '0.75rem', padding: '2rem' }}>
              <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>&#128588;</div>
              <h4 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: 'white', marginBottom: '0.75rem' }}>Completely Free for Tenants</h4>
              <p style={{ color: '#94a3b8', lineHeight: '1.7' }}>Toice is free for tenants. No subscription, no fees, no catch. Download the app, find your building, and start reporting. Your experience as a tenant has value and Toice makes sure it is heard.</p>
            </div>

          </div>
        </section>

        {/* How It Works */}
        <section style={{ maxWidth: '1280px', margin: '0 auto', padding: '0 1.5rem 4rem' }}>
          <h3 style={{ fontSize: '1.75rem', fontWeight: 'bold', color: 'white', textAlign: 'center', marginBottom: '2rem' }}>How It Works</h3>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '3rem', flexWrap: 'wrap', maxWidth: '900px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', flex: '1', minWidth: '200px' }}>
              <div style={{ width: '64px', height: '64px', borderRadius: '50%', backgroundColor: colors.primary, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem', fontWeight: 'bold', margin: '0 auto 1rem' }}>1</div>
              <h4 style={{ color: 'white', fontWeight: 'bold', marginBottom: '0.5rem' }}>Download the App</h4>
              <p style={{ color: '#94a3b8', fontSize: '0.95rem' }}>Get Toice free on iOS or Android</p>
            </div>
            <div style={{ textAlign: 'center', flex: '1', minWidth: '200px' }}>
              <div style={{ width: '64px', height: '64px', borderRadius: '50%', backgroundColor: colors.primary, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem', fontWeight: 'bold', margin: '0 auto 1rem' }}>2</div>
              <h4 style={{ color: 'white', fontWeight: 'bold', marginBottom: '0.5rem' }}>Find Your Building</h4>
              <p style={{ color: '#94a3b8', fontSize: '0.95rem' }}>Search by address and verify you live there</p>
            </div>
            <div style={{ textAlign: 'center', flex: '1', minWidth: '200px' }}>
              <div style={{ width: '64px', height: '64px', borderRadius: '50%', backgroundColor: colors.primary, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem', fontWeight: 'bold', margin: '0 auto 1rem' }}>3</div>
              <h4 style={{ color: 'white', fontWeight: 'bold', marginBottom: '0.5rem' }}>Report Conditions</h4>
              <p style={{ color: '#94a3b8', fontSize: '0.95rem' }}>Submit reports that drive real accountability</p>
            </div>
          </div>
        </section>

        {/* QR Codes / App Store Links */}
        <section style={{ maxWidth: '800px', margin: '0 auto', padding: '0 1.5rem 5rem' }}>
          <div style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '1rem', padding: '3rem', textAlign: 'center' }}>
            <h3 style={{ fontSize: '1.75rem', fontWeight: 'bold', color: 'white', marginBottom: '0.75rem' }}>Download Toice</h3>
            <p style={{ color: '#94a3b8', marginBottom: '2rem', fontSize: '1.1rem' }}>Scan the QR code or click to download. Free for all tenants.</p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '3rem', flexWrap: 'wrap' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ width: '180px', height: '180px', backgroundColor: 'rgba(255, 255, 255, 0.1)', border: '2px dashed rgba(255, 255, 255, 0.3)', borderRadius: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem' }}>
                  <span style={{ color: '#64748b', fontSize: '0.875rem' }}>App Store QR Code</span>
                </div>
                <p style={{ color: 'white', fontWeight: '600' }}>iOS (App Store)</p>
                <p style={{ color: '#64748b', fontSize: '0.8rem' }}>Coming Soon</p>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ width: '180px', height: '180px', backgroundColor: 'rgba(255, 255, 255, 0.1)', border: '2px dashed rgba(255, 255, 255, 0.3)', borderRadius: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem' }}>
                  <span style={{ color: '#64748b', fontSize: '0.875rem' }}>Google Play QR Code</span>
                </div>
                <p style={{ color: 'white', fontWeight: '600' }}>Android (Google Play)</p>
                <p style={{ color: '#64748b', fontSize: '0.8rem' }}>Coming Soon</p>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer style={{ borderTop: '1px solid #334155', padding: '3rem 1.5rem' }}>
          <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '2rem' }}>
              <p style={{ color: '#94a3b8', fontSize: '0.875rem', margin: 0 }}>
                © 2026 Toice LLC. All rights reserved.
              </p>
              <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
                <a href="/privacy" onClick={(e) => { e.preventDefault(); setCurrentView('privacy'); }} style={{ color: '#cbd5e1', fontSize: '0.875rem', textDecoration: 'none', cursor: 'pointer' }}>Privacy Policy</a>
                <a href="/terms" onClick={(e) => { e.preventDefault(); setCurrentView('terms'); }} style={{ color: '#cbd5e1', fontSize: '0.875rem', textDecoration: 'none', cursor: 'pointer' }}>Terms & Conditions</a>
              <a href="/delete-account" onClick={(e) => { e.preventDefault(); setCurrentView('delete-account'); }} style={{ color: '#cbd5e1', fontSize: '0.875rem', textDecoration: 'none', cursor: 'pointer' }}>Delete Account</a>
              </div>
            </div>
          </div>
        </footer>
      </div>
    );
  }

  function PrivacyPolicyPage() {
    const sectionStyle = { marginBottom: '2rem' };
    const headingStyle = { fontSize: '1.25rem', fontWeight: 'bold', color: '#0f172a', marginBottom: '0.75rem' };
    const textStyle = { color: '#475569', lineHeight: '1.8', marginBottom: '1rem' };

    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
        <Navigation currentView={currentView} setCurrentView={setCurrentView} user={user} colors={colors} onLogout={() => logout()} accountType={accountType} demoMode={demoMode} onExitDemo={exitDemoMode} />

        <div style={{ maxWidth: '800px', margin: '0 auto', padding: '3rem 1.5rem' }}>
          <h1 style={{ fontSize: '2rem', fontWeight: 'bold', color: '#0f172a', marginBottom: '0.5rem' }}>Privacy Policy</h1>
          <p style={{ color: '#64748b', marginBottom: '2.5rem' }}>Last updated: June 2026</p>

          <div style={sectionStyle}>
            <h2 style={headingStyle}>1. Introduction</h2>
            <p style={textStyle}>Toice LLC ("Toice," "we," "our," or "us") operates the Toice Risk Intelligence platform, including our website and mobile application. This Privacy Policy explains how we collect, use, disclose, and protect your information when you use our services.</p>
            <p style={textStyle}><strong>Age Requirement:</strong> The Toice mobile application is intended for users who are 13 years of age or older. We do not knowingly collect personal information from children under 13. If we learn that we have collected information from a child under 13, we will delete it promptly.</p>
          </div>

          <div style={sectionStyle}>
            <h2 style={headingStyle}>2. Information We Collect</h2>
            <p style={textStyle}><strong>Account Information:</strong> When you create an account, we collect your name, email address, and account type (tenant, property manager, lender, or inspector).</p>
            <p style={textStyle}><strong>Building Condition Reports:</strong> When tenants submit building condition reports, we collect the report content, category, severity, and timestamp. Tenant identity is anonymized and never shared with property managers, landlords, or building owners.</p>
            <p style={textStyle}><strong>Location Data:</strong> The Toice mobile app requests access to your device's location to verify that you are physically present at the building you are reporting on. Location data is used solely for verification purposes and is not stored or shared beyond confirming your proximity to a reported address.</p>
            <p style={textStyle}><strong>Camera and Photos:</strong> The Toice mobile app requests access to your device's camera and photo library to allow you to attach photos to building condition reports. Photos you submit are stored securely and associated with your report. They are shared only in anonymized form as part of building condition data.</p>
            <p style={textStyle}><strong>Property Data:</strong> For property managers and lenders, we collect building addresses, portfolio information, and property management data that you provide.</p>
            <p style={textStyle}><strong>Usage Data:</strong> We automatically collect information about how you interact with our services, including pages visited, features used, and device information.</p>
            <p style={textStyle}><strong>Payment Information:</strong> Payment processing is handled by Stripe. We do not store credit card numbers or full payment details on our servers.</p>
          </div>

          <div style={sectionStyle}>
            <h2 style={headingStyle}>3. How We Use Your Information</h2>
            <p style={textStyle}>We use collected information to provide and improve our services, generate building risk scores and analytics, process transactions, communicate with you about your account, and comply with legal obligations. Tenant reports are aggregated and anonymized before being incorporated into building risk scores.</p>
          </div>

          <div style={sectionStyle}>
            <h2 style={headingStyle}>4. Tenant Anonymity</h2>
            <p style={textStyle}>Toice is committed to protecting tenant privacy. Building condition reports submitted by tenants are anonymized. Property managers, landlords, building owners, lenders, and insurers cannot see which tenant submitted a specific report. We will never disclose a tenant's identity in connection with a building condition report unless required by law.</p>
          </div>

          <div style={sectionStyle}>
            <h2 style={headingStyle}>5. Information Sharing</h2>
            <p style={textStyle}>We do not sell your personal information. We may share anonymized and aggregated building condition data with property managers, lenders, insurers, and investors as part of our risk analytics services. We may share information with service providers who assist in operating our platform, and when required by law or to protect our rights.</p>
          </div>

          <div style={sectionStyle}>
            <h2 style={headingStyle}>6. Data Security</h2>
            <p style={textStyle}>We implement industry-standard security measures to protect your information, including encryption in transit and at rest, secure authentication, and access controls. However, no method of electronic transmission or storage is completely secure, and we cannot guarantee absolute security.</p>
          </div>

          <div style={sectionStyle}>
            <h2 style={headingStyle}>7. Data Retention</h2>
            <p style={textStyle}>We retain your information for as long as your account is active or as needed to provide services. Building condition report data may be retained in anonymized form indefinitely as part of historical building risk analytics. You may request deletion of your account and personal data by contacting us.</p>
          </div>

          <div style={sectionStyle}>
            <h2 style={headingStyle}>8. Your Rights</h2>
            <p style={textStyle}>You may access, update, or delete your personal information at any time through your account settings or by contacting us at toiceriskintelligence@gmail.com. You may opt out of non-essential communications. California residents and residents of other jurisdictions with applicable privacy laws may have additional rights.</p>
          </div>

          <div style={sectionStyle}>
            <h2 style={headingStyle}>9. Changes to This Policy</h2>
            <p style={textStyle}>We may update this Privacy Policy from time to time. We will notify you of material changes by posting the updated policy on our website and updating the "Last updated" date. Your continued use of our services after changes constitutes acceptance of the updated policy.</p>
          </div>

          <div style={sectionStyle}>
            <h2 style={headingStyle}>10. Contact Us</h2>
            <p style={textStyle}>If you have questions about this Privacy Policy, contact us at <a href="mailto:toiceriskintelligence@gmail.com" style={{ color: colors.primary }}>toiceriskintelligence@gmail.com</a>.</p>
          </div>
        </div>

        <footer style={{ borderTop: '1px solid #e2e8f0', padding: '2rem 1.5rem', backgroundColor: '#0f172a' }}>
          <div style={{ maxWidth: '1280px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
            <p style={{ color: '#94a3b8', fontSize: '0.875rem', margin: 0 }}>© 2026 Toice LLC. All rights reserved.</p>
            <div style={{ display: 'flex', gap: '2rem' }}>
              <a href="/privacy" onClick={(e) => { e.preventDefault(); setCurrentView('privacy'); }} style={{ color: '#cbd5e1', fontSize: '0.875rem', textDecoration: 'none', cursor: 'pointer' }}>Privacy Policy</a>
              <a href="/terms" onClick={(e) => { e.preventDefault(); setCurrentView('terms'); }} style={{ color: '#cbd5e1', fontSize: '0.875rem', textDecoration: 'none', cursor: 'pointer' }}>Terms & Conditions</a>
              <a href="/delete-account" onClick={(e) => { e.preventDefault(); setCurrentView('delete-account'); }} style={{ color: '#cbd5e1', fontSize: '0.875rem', textDecoration: 'none', cursor: 'pointer' }}>Delete Account</a>
            </div>
          </div>
        </footer>
      </div>
    );
  }

  function DeleteAccountPage() {
    const sectionStyle = { marginBottom: '2rem' };
    const headingStyle = { fontSize: '1.25rem', fontWeight: 'bold', color: '#0f172a', marginBottom: '0.75rem' };
    const textStyle = { color: '#475569', lineHeight: '1.8', marginBottom: '1rem' };

    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
        <Navigation currentView={currentView} setCurrentView={setCurrentView} user={user} colors={colors} onLogout={() => logout()} accountType={accountType} demoMode={demoMode} onExitDemo={exitDemoMode} />

        <div style={{ maxWidth: '800px', margin: '0 auto', padding: '3rem 1.5rem' }}>
          <h1 style={{ fontSize: '2rem', fontWeight: 'bold', color: '#0f172a', marginBottom: '0.5rem' }}>Delete Your Account</h1>
          <p style={{ color: '#64748b', marginBottom: '2.5rem' }}>Last updated: June 2026</p>

          <div style={{ backgroundColor: '#fff7ed', border: '1px solid #fdba74', borderRadius: '0.75rem', padding: '1.5rem', marginBottom: '2.5rem' }}>
            <p style={{ color: '#9a3412', margin: 0, fontWeight: '600', marginBottom: '0.5rem' }}>Before you delete your account</p>
            <p style={{ color: '#9a3412', margin: 0, fontSize: '0.9rem' }}>Deletion is permanent and cannot be undone. All your data will be removed as described below.</p>
          </div>

          <div style={sectionStyle}>
            <h2 style={headingStyle}>How to Request Account Deletion</h2>
            <p style={textStyle}>You can request deletion of your Toice account and associated data in either of the following ways:</p>
            <div style={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '0.75rem', padding: '1.5rem', marginBottom: '1rem' }}>
              <p style={{ fontWeight: '700', color: '#0f172a', marginBottom: '0.5rem' }}>Option 1 — In the Toice app</p>
              <ol style={{ color: '#475569', lineHeight: '2', paddingLeft: '1.5rem', margin: 0 }}>
                <li>Open the Toice app and sign in</li>
                <li>Tap <strong>My Account</strong> in the bottom navigation</li>
                <li>Scroll to the bottom and tap <strong>Delete Account</strong></li>
                <li>Confirm when prompted</li>
              </ol>
            </div>
            <div style={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '0.75rem', padding: '1.5rem' }}>
              <p style={{ fontWeight: '700', color: '#0f172a', marginBottom: '0.5rem' }}>Option 2 — Email request</p>
              <p style={{ color: '#475569', margin: 0 }}>
                Send an email to <a href="mailto:toiceriskintelligence@gmail.com" style={{ color: colors.primary, fontWeight: '600' }}>toiceriskintelligence@gmail.com</a> with the subject line <strong>"Account Deletion Request"</strong> and include the email address associated with your account. We will process your request within 30 days.
              </p>
            </div>
          </div>

          <div style={sectionStyle}>
            <h2 style={headingStyle}>What Gets Deleted</h2>
            <p style={textStyle}>When your account is deleted, the following data is permanently removed:</p>
            <ul style={{ color: '#475569', lineHeight: '2', paddingLeft: '1.5rem' }}>
              <li>Your name, email address, and phone number</li>
              <li>Your account credentials and login information</li>
              <li>Any photos you uploaded as part of building reports</li>
              <li>Your lease verification documents</li>
            </ul>
          </div>

          <div style={sectionStyle}>
            <h2 style={headingStyle}>What Is Retained</h2>
            <p style={textStyle}>To preserve the integrity of building risk scores that other users rely on, anonymized building condition report data (issue category, severity rating, and timestamp) is retained in aggregated form after account deletion. This data contains no personally identifiable information and cannot be linked back to you.</p>
            <p style={textStyle}>Retention period for anonymized report data: indefinite, as part of historical building analytics.</p>
          </div>

          <div style={sectionStyle}>
            <h2 style={headingStyle}>Questions</h2>
            <p style={textStyle}>Contact us at <a href="mailto:toiceriskintelligence@gmail.com" style={{ color: colors.primary }}>toiceriskintelligence@gmail.com</a> with any questions about your data.</p>
          </div>
        </div>

        <footer style={{ borderTop: '1px solid #e2e8f0', padding: '2rem 1.5rem', backgroundColor: '#0f172a' }}>
          <div style={{ maxWidth: '1280px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
            <p style={{ color: '#94a3b8', fontSize: '0.875rem', margin: 0 }}>© 2026 Toice LLC. All rights reserved.</p>
            <div style={{ display: 'flex', gap: '2rem' }}>
              <a href="/privacy" onClick={(e) => { e.preventDefault(); setCurrentView('privacy'); }} style={{ color: '#cbd5e1', fontSize: '0.875rem', textDecoration: 'none', cursor: 'pointer' }}>Privacy Policy</a>
              <a href="/terms" onClick={(e) => { e.preventDefault(); setCurrentView('terms'); }} style={{ color: '#cbd5e1', fontSize: '0.875rem', textDecoration: 'none', cursor: 'pointer' }}>Terms & Conditions</a>
              <a href="/delete-account" onClick={(e) => { e.preventDefault(); setCurrentView('delete-account'); }} style={{ color: '#cbd5e1', fontSize: '0.875rem', textDecoration: 'none', cursor: 'pointer' }}>Delete Account</a>
            </div>
          </div>
        </footer>
      </div>
    );
  }

  function TermsPage() {
    const sectionStyle = { marginBottom: '2rem' };
    const headingStyle = { fontSize: '1.25rem', fontWeight: 'bold', color: '#0f172a', marginBottom: '0.75rem' };
    const textStyle = { color: '#475569', lineHeight: '1.8', marginBottom: '1rem' };

    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
        <Navigation currentView={currentView} setCurrentView={setCurrentView} user={user} colors={colors} onLogout={() => logout()} accountType={accountType} demoMode={demoMode} onExitDemo={exitDemoMode} />

        <div style={{ maxWidth: '800px', margin: '0 auto', padding: '3rem 1.5rem' }}>
          <h1 style={{ fontSize: '2rem', fontWeight: 'bold', color: '#0f172a', marginBottom: '0.5rem' }}>Terms & Conditions</h1>
          <p style={{ color: '#64748b', marginBottom: '2.5rem' }}>Last updated: March 2026</p>

          <div style={sectionStyle}>
            <h2 style={headingStyle}>1. Acceptance of Terms</h2>
            <p style={textStyle}>By accessing or using the Toice Risk Intelligence platform, including our website and mobile application (collectively, the "Services"), you agree to be bound by these Terms & Conditions. If you do not agree to these terms, do not use our Services.</p>
          </div>

          <div style={sectionStyle}>
            <h2 style={headingStyle}>2. Description of Services</h2>
            <p style={textStyle}>Toice provides a risk analytics platform that collects building condition data from tenants and generates risk scores and reports for property managers, lenders, insurers, and investors. Our Services include a mobile application for tenants to submit building condition reports and a web platform for commercial users to access risk analytics.</p>
          </div>

          <div style={sectionStyle}>
            <h2 style={headingStyle}>3. User Accounts</h2>
            <p style={textStyle}>You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You must provide accurate and complete information when creating an account. You must notify us immediately of any unauthorized use of your account.</p>
          </div>

          <div style={sectionStyle}>
            <h2 style={headingStyle}>4. Tenant Users</h2>
            <p style={textStyle}>Tenant accounts are free. By submitting building condition reports, you represent that you are or were a resident of the building you are reporting on, that your reports are truthful and accurate to the best of your knowledge, and that you will not submit false or misleading reports. Toice reserves the right to remove reports that are determined to be fraudulent, malicious, or in violation of these terms.</p>
          </div>

          <div style={sectionStyle}>
            <h2 style={headingStyle}>5. Commercial Users</h2>
            <p style={textStyle}>Property managers, lenders, insurers, and investors ("Commercial Users") access Toice services through paid subscriptions. Subscription fees, billing cycles, and plan features are described on our pricing page. Subscriptions renew automatically unless canceled. You may cancel your subscription at any time, with cancellation taking effect at the end of your current billing period.</p>
          </div>

          <div style={sectionStyle}>
            <h2 style={headingStyle}>6. Data and Risk Scores</h2>
            <p style={textStyle}>Toice risk scores and analytics are generated from tenant-submitted building condition reports and publicly available data. Risk scores are provided for informational purposes only and should not be the sole basis for lending, insurance, investment, or property management decisions. Toice does not guarantee the accuracy, completeness, or reliability of risk scores or any data provided through our Services.</p>
          </div>

          <div style={sectionStyle}>
            <h2 style={headingStyle}>7. Intellectual Property</h2>
            <p style={textStyle}>All content, features, and functionality of the Toice platform, including risk scoring algorithms, analytics methodologies, software, text, graphics, and logos, are the exclusive property of Toice LLC and are protected by copyright, trademark, and other intellectual property laws. You may not reproduce, distribute, modify, or create derivative works from any part of our Services without our prior written consent.</p>
          </div>

          <div style={sectionStyle}>
            <h2 style={headingStyle}>8. Prohibited Conduct</h2>
            <p style={textStyle}>You agree not to use our Services to submit false, misleading, or defamatory building condition reports, to harass, threaten, or intimidate any user, to attempt to identify anonymous tenant reporters, to scrape, mine, or extract data from our platform, to interfere with or disrupt the operation of our Services, or to violate any applicable law or regulation.</p>
          </div>

          <div style={sectionStyle}>
            <h2 style={headingStyle}>9. Limitation of Liability</h2>
            <p style={textStyle}>To the maximum extent permitted by law, Toice LLC shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including loss of profits, data, or goodwill, arising out of or related to your use of our Services. Our total liability for any claim arising from these terms or your use of our Services shall not exceed the amount you paid to Toice in the twelve months preceding the claim.</p>
          </div>

          <div style={sectionStyle}>
            <h2 style={headingStyle}>10. Disclaimer of Warranties</h2>
            <p style={textStyle}>Our Services are provided "as is" and "as available" without warranties of any kind, whether express or implied, including implied warranties of merchantability, fitness for a particular purpose, and non-infringement. Toice does not warrant that our Services will be uninterrupted, error-free, or secure.</p>
          </div>

          <div style={sectionStyle}>
            <h2 style={headingStyle}>11. Indemnification</h2>
            <p style={textStyle}>You agree to indemnify, defend, and hold harmless Toice LLC, its officers, directors, employees, and agents from any claims, liabilities, damages, losses, and expenses arising out of or related to your use of our Services, your violation of these terms, or your violation of any rights of another party.</p>
          </div>

          <div style={sectionStyle}>
            <h2 style={headingStyle}>12. Modifications to Terms</h2>
            <p style={textStyle}>We reserve the right to modify these Terms & Conditions at any time. We will notify you of material changes by posting the updated terms on our website. Your continued use of our Services after changes constitutes acceptance of the updated terms.</p>
          </div>

          <div style={sectionStyle}>
            <h2 style={headingStyle}>13. Governing Law</h2>
            <p style={textStyle}>These Terms & Conditions are governed by and construed in accordance with the laws of the District of Columbia, without regard to its conflict of law provisions. Any disputes arising from these terms shall be resolved in the courts of the District of Columbia.</p>
          </div>

          <div style={sectionStyle}>
            <h2 style={headingStyle}>14. Contact Us</h2>
            <p style={textStyle}>If you have questions about these Terms & Conditions, contact us at <a href="mailto:toiceriskintelligence@gmail.com" style={{ color: colors.primary }}>toiceriskintelligence@gmail.com</a>.</p>
          </div>
        </div>

        <footer style={{ borderTop: '1px solid #e2e8f0', padding: '2rem 1.5rem', backgroundColor: '#0f172a' }}>
          <div style={{ maxWidth: '1280px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
            <p style={{ color: '#94a3b8', fontSize: '0.875rem', margin: 0 }}>© 2026 Toice LLC. All rights reserved.</p>
            <div style={{ display: 'flex', gap: '2rem' }}>
              <a href="/privacy" onClick={(e) => { e.preventDefault(); setCurrentView('privacy'); }} style={{ color: '#cbd5e1', fontSize: '0.875rem', textDecoration: 'none', cursor: 'pointer' }}>Privacy Policy</a>
              <a href="/terms" onClick={(e) => { e.preventDefault(); setCurrentView('terms'); }} style={{ color: '#cbd5e1', fontSize: '0.875rem', textDecoration: 'none', cursor: 'pointer' }}>Terms & Conditions</a>
              <a href="/delete-account" onClick={(e) => { e.preventDefault(); setCurrentView('delete-account'); }} style={{ color: '#cbd5e1', fontSize: '0.875rem', textDecoration: 'none', cursor: 'pointer' }}>Delete Account</a>
            </div>
          </div>
        </footer>
      </div>
    );
  }

  function Dashboard() {
    // Calculate comprehensive stats - with safe defaults
    const safeBuildings = Array.isArray(buildings) ? buildings : [];
    const uniqueCities = new Set(safeBuildings.map(b => b?.city).filter(Boolean));
    const totalReports = safeBuildings.reduce((sum, b) => sum + (b?.reviews?.length || 0), 0);
    const uniqueStates = new Set(safeBuildings.map(b => b?.state).filter(Boolean));

    // Calculate critical buildings percentage
    const criticalBuildings = safeBuildings.filter(b => b?.riskScore >= 70).length;
    const criticalPercentage = safeBuildings.length > 0 ? Math.round((criticalBuildings / safeBuildings.length) * 100) : 0;

    const stats = {
      totalBuildings: safeBuildings.length,
      totalCities: uniqueCities.size,
      totalStates: uniqueStates.size,
      totalReports: totalReports,
      activeIssues: safeBuildings.reduce((sum, b) => sum + (b?.totalIssues || 0), 0),
      avgRiskScore: safeBuildings.length > 0 ? Math.round(safeBuildings.reduce((sum, b) => sum + (b?.riskScore || 0), 0) / safeBuildings.length) : 0,
      improvements: safeBuildings.reduce((sum, b) => sum + (b?.improvementCount || 0), 0),
      verifiedReports: safeBuildings.reduce((sum, b) => sum + (b?.reviews?.filter(r => r?.verified).length || 0), 0),
      criticalBuildings: criticalBuildings,
      criticalPercentage: criticalPercentage
    };

    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
        <Navigation
          currentView={currentView}
          setCurrentView={setCurrentView}
          user={user}
          colors={colors}
          accountType={accountType}
          demoMode={demoMode}
          onExitDemo={exitDemoMode}
        />

        <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '2rem' }}>
          {loading && (
            <div>
              <StatsGridSkeleton />
              <div style={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '0.5rem', padding: '1.5rem' }}>
                <TableSkeleton rows={5} columns={4} />
              </div>
            </div>
          )}
          
          {error && (
            <div style={{ backgroundColor: '#fee2e2', border: '1px solid #ef4444', borderRadius: '0.5rem', padding: '1rem', marginBottom: '2rem' }}>
              <p style={{ color: '#991b1b', margin: 0, fontWeight: '600' }}>Error: {error}</p>
              <button onClick={loadBuildings} style={{ marginTop: '0.5rem', padding: '0.5rem 1rem', backgroundColor: '#ef4444', color: 'white', border: 'none', borderRadius: '0.375rem', cursor: 'pointer' }}>
                Retry
              </button>
            </div>
          )}

          {!loading && !error && (
            <>
              {/* Platform Overview Header */}
              <div style={{ marginBottom: '2rem', textAlign: 'center' }}>
                <div style={{ display: 'inline-block', backgroundColor: colors.orange, color: 'white', padding: '0.5rem 1.5rem', borderRadius: '0.5rem', fontWeight: 'bold', fontSize: '0.875rem', marginBottom: '1rem' }}>
                  DEMO - Platform Data
                </div>
                <h2 style={{ fontSize: '2rem', fontWeight: 'bold', color: '#0f172a', marginBottom: '0.5rem' }}>
                  Platform Overview
                </h2>
                <p style={{ fontSize: '1rem', color: '#64748b' }}>
                  Real-time data from verified tenant reports across the country
                </p>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
                <div style={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '0.5rem', padding: '1.5rem', textAlign: 'center' }}>
                  <div style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '0.5rem', fontWeight: '600' }}>Buildings Tracked</div>
                  <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: colors.primary }}>{stats.totalBuildings}</div>
                  <div style={{ fontSize: '0.75rem', color: '#10b981', marginTop: '0.25rem' }}>Across the country</div>
                </div>
                <div style={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '0.5rem', padding: '1.5rem', textAlign: 'center' }}>
                  <div style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '0.5rem', fontWeight: '600' }}>Reviews Submitted</div>
                  <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: colors.primary }}>{stats.totalReports}</div>
                  <div style={{ fontSize: '0.75rem', color: '#10b981', marginTop: '0.25rem' }}>{stats.verifiedReports} verified by residents</div>
                </div>
                <div style={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '0.5rem', padding: '1.5rem', textAlign: 'center' }}>
                  <div style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '0.5rem', fontWeight: '600' }}>Cities Covered</div>
                  <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: colors.primary }}>{stats.totalCities}</div>
                  <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.25rem' }}>Across {stats.totalStates} states</div>
                </div>
                <div style={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '0.5rem', padding: '1.5rem', textAlign: 'center' }}>
                  <div style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '0.5rem', fontWeight: '600' }}>Critical Buildings</div>
                  <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#ef4444' }}>{stats.criticalPercentage}%</div>
                  <div style={{ fontSize: '0.75rem', color: '#ef4444', marginTop: '0.25rem' }}>{stats.criticalBuildings} flagged as high-risk</div>
                </div>
                <div style={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '0.5rem', padding: '1.5rem', textAlign: 'center' }}>
                  <div style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '0.5rem', fontWeight: '600' }}>Platform Avg Risk</div>
                  <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: colors.orange }}>{stats.avgRiskScore}</div>
                  <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.25rem' }}>Toice Risk Score</div>
                </div>
                <div style={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '0.5rem', padding: '1.5rem', textAlign: 'center' }}>
                  <div style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '0.5rem', fontWeight: '600' }}>Active Issues</div>
                  <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: colors.orange }}>{stats.activeIssues}</div>
                  <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.25rem' }}>Verified complaints</div>
                </div>
              </div>

              {/* Charts Section */}
              {safeBuildings.length > 0 && (
                <>
                  <div className="chart-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
                    <PortfolioRiskDistributionChart
                      data={{
                        critical: safeBuildings.filter(b => b?.riskScore >= 80).length,
                        high: safeBuildings.filter(b => b?.riskScore >= 60 && b?.riskScore < 80).length,
                        moderate: safeBuildings.filter(b => b?.riskScore >= 40 && b?.riskScore < 60).length,
                        low: safeBuildings.filter(b => b?.riskScore < 40).length
                      }}
                    />
                    <IssuesByCategoryChart
                      data={(() => {
                        const categoryCounts = {};
                        safeBuildings.forEach(building => {
                          if (building?.reviews) {
                            building.reviews.forEach(review => {
                              const category = review?.category || 'Other';
                              categoryCounts[category] = (categoryCounts[category] || 0) + 1;
                            });
                          }
                        });
                        return Object.entries(categoryCounts)
                          .map(([category, count]) => ({ category, count }))
                          .sort((a, b) => b.count - a.count)
                          .slice(0, 10);
                      })()}
                    />
                  </div>

                  <div style={{ marginBottom: '2rem' }}>
                    <RiskScoreTrendChart />
                  </div>
                </>
              )}

              <div style={{
                background: 'linear-gradient(135deg, #178B9B 0%, #0f6674 100%)',
                borderRadius: '0.75rem',
                padding: '3rem 2rem',
                textAlign: 'center'
              }}>
                <h3 style={{ fontSize: '1.75rem', fontWeight: 'bold', marginBottom: '1rem', color: 'white' }}>
                  Want Building-Level Details?
                </h3>
                <p style={{ fontSize: '1.125rem', marginBottom: '2rem', opacity: 0.95, maxWidth: '600px', margin: '0 auto 2rem', color: 'white' }}>
                  Sign up for Property Manager Dashboard or request a Risk Intelligence Report to see detailed data for specific buildings.
                </p>
                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                  <button
                    onClick={() => setCurrentView('login')}
                    style={{
                      padding: '0.75rem 2rem',
                      backgroundColor: 'white',
                      color: colors.primary,
                      border: 'none',
                      borderRadius: '0.5rem',
                      fontSize: '1rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                    }}
                  >
                    Sign In as Property Manager
                  </button>
                  <button
                    onClick={() => setShowPurchaseModal(true)}
                    style={{
                      padding: '0.75rem 2rem',
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      color: 'white',
                      border: '2px solid white',
                      borderRadius: '0.5rem',
                      fontSize: '1rem',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    Purchase Risk Report
                  </button>
                </div>
                {false && buildings.length > 0 && (
                  <div style={{ overflowX: 'auto' }}>
                    <table className="data-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                          <th style={{ textAlign: 'left', padding: '0.75rem', fontSize: '0.875rem', fontWeight: '600', color: '#475569' }}>Address</th>
                          <th style={{ textAlign: 'left', padding: '0.75rem', fontSize: '0.875rem', fontWeight: '600', color: '#475569' }}>Risk Score</th>
                          <th style={{ textAlign: 'left', padding: '0.75rem', fontSize: '0.875rem', fontWeight: '600', color: '#475569' }}>Issues</th>
                          <th style={{ textAlign: 'left', padding: '0.75rem', fontSize: '0.875rem', fontWeight: '600', color: '#475569' }}>Level</th>
                        </tr>
                      </thead>
                      <tbody>
                        {buildings.map((building, idx) => (
                          <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                            <td style={{ padding: '1rem 0.75rem', fontSize: '0.875rem', color: '#0f172a', fontWeight: '500' }}>{building.address}</td>
                            <td style={{ padding: '1rem 0.75rem' }}>
                              <span style={{ 
                                padding: '0.375rem 0.75rem', 
                                borderRadius: '9999px', 
                                fontSize: '0.875rem', 
                                fontWeight: '700',
                                backgroundColor: building.riskScore >= 70 ? '#fee2e2' : building.riskScore >= 50 ? '#fed7aa' : '#d1fae5',
                                color: building.riskScore >= 70 ? '#991b1b' : building.riskScore >= 50 ? '#9a3412' : '#065f46'
                              }}>
                                {building.riskScore}
                              </span>
                            </td>
                            <td style={{ padding: '1rem 0.75rem', fontSize: '0.875rem', color: '#0f172a' }}>{building.totalIssues || 0}</td>
                            <td style={{ padding: '1rem 0.75rem' }}>
                              <span style={{ 
                                padding: '0.25rem 0.75rem', 
                                borderRadius: '0.375rem', 
                                fontSize: '0.75rem', 
                                fontWeight: '600',
                                backgroundColor: building.riskLevel === 'CRITICAL' ? '#fee2e2' : building.riskLevel === 'HIGH' ? '#fed7aa' : '#d1fae5',
                                color: building.riskLevel === 'CRITICAL' ? '#991b1b' : building.riskLevel === 'HIGH' ? '#9a3412' : '#065f46'
                              }}>
                                {building.riskLevel || 'LOW'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  function AdminPanel() {
    if (!user) {
      return (
        <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ textAlign: 'center', maxWidth: '400px' }}>
            <AlertCircle size={48} style={{ color: '#64748b', marginBottom: '1rem' }} />
            <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#0f172a', marginBottom: '0.5rem' }}>Admin Access Required</h2>
            <p style={{ color: '#64748b', marginBottom: '1.5rem' }}>Please sign in with an admin account to access this page.</p>
            <button onClick={() => setCurrentView('login')} style={{ padding: '0.75rem 2rem', backgroundColor: colors.primary, color: 'white', border: 'none', borderRadius: '0.5rem', fontSize: '1rem', fontWeight: '600', cursor: 'pointer' }}>
              Sign In
            </button>
          </div>
        </div>
      );
    }

    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
        <Navigation
          currentView={currentView}
          setCurrentView={setCurrentView}
          user={user}
          colors={colors}
          accountType={accountType}
          demoMode={demoMode}
          onExitDemo={exitDemoMode}
        />

        <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '2rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
            <div style={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '0.5rem', padding: '1.5rem' }}>
              <div style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '0.5rem' }}>Pending Verifications</div>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#0f172a' }}>{verifications.length}</div>
            </div>
          </div>

          <div style={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '0.5rem', padding: '1.5rem' }}>
            <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#0f172a', marginBottom: '1rem' }}>Pending Verifications</h3>
            {loading && <TableSkeleton rows={3} columns={3} />}
            {verifications.length === 0 && !loading && (
              <p style={{ textAlign: 'center', color: '#64748b', padding: '2rem' }}>No pending verifications</p>
            )}
            {verifications.length > 0 && (
              <table className="data-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                    <th style={{ textAlign: 'left', padding: '0.75rem', fontSize: '0.875rem', fontWeight: '600', color: '#475569' }}>Building</th>
                    <th style={{ textAlign: 'left', padding: '0.75rem', fontSize: '0.875rem', fontWeight: '600', color: '#475569' }}>Type</th>
                    <th style={{ textAlign: 'left', padding: '0.75rem', fontSize: '0.875rem', fontWeight: '600', color: '#475569' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {verifications.map((item) => (
                    <tr key={item.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '1rem 0.75rem', fontSize: '0.875rem', color: '#0f172a' }}>{item.buildingAddress}</td>
                      <td style={{ padding: '1rem 0.75rem' }}>
                        <span style={{ padding: '0.25rem 0.75rem', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: '600', backgroundColor: '#dbeafe', color: '#1e40af' }}>
                          {item.type || 'Improvement'}
                        </span>
                      </td>
                      <td style={{ padding: '1rem 0.75rem' }}>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <button
                            onClick={() => handleApprove(item.id)}
                            style={{ padding: '0.5rem 1rem', backgroundColor: colors.success, color: 'white', border: 'none', borderRadius: '0.375rem', fontSize: '0.875rem', fontWeight: '600', cursor: 'pointer' }}
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => handleReject(item.id)}
                            style={{ padding: '0.5rem 1rem', backgroundColor: '#ef4444', color: 'white', border: 'none', borderRadius: '0.375rem', fontSize: '0.875rem', fontWeight: '600', cursor: 'pointer' }}
                          >
                            Reject
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* ── Building Ownership Claims ── */}
          <BuildingClaimsAdminSection />
        </div>
      </div>
    );
  }

  function BuildingClaimsAdminSection() {
    const [claims, setClaims] = useState([]);
    const [claimsLoading, setClaimsLoading] = useState(true);

    const load = async () => {
      try {
        setClaimsLoading(true);
        const data = await getPendingBuildingClaims();
        setClaims(data);
      } catch (err) {
        toast.error('Failed to load claims: ' + err.message);
      } finally {
        setClaimsLoading(false);
      }
    };

    React.useEffect(() => { load(); }, []);

    const handleApprove = async (claimId) => {
      try {
        await approveBuildingClaim(claimId);
        toast.success('Building claim approved — property added to PM dashboard');
        load();
      } catch (err) {
        toast.error('Error: ' + err.message);
      }
    };

    const handleReject = async (claimId) => {
      const reason = window.prompt('Reason for rejection (optional):') ?? '';
      try {
        await rejectBuildingClaim(claimId, reason);
        toast.success('Claim rejected');
        load();
      } catch (err) {
        toast.error('Error: ' + err.message);
      }
    };

    return (
      <div style={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '0.5rem', padding: '1.5rem', marginTop: '2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#0f172a', margin: 0 }}>
            Building Ownership Claims
            {claims.length > 0 && (
              <span style={{ marginLeft: '0.5rem', padding: '0.2rem 0.6rem', borderRadius: '9999px', fontSize: '0.7rem', fontWeight: '700', backgroundColor: '#fef3c7', color: '#92400e' }}>
                {claims.length} pending
              </span>
            )}
          </h3>
          <button onClick={load} style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: '0.375rem', padding: '0.4rem 0.75rem', fontSize: '0.75rem', cursor: 'pointer', color: '#64748b' }}>
            Refresh
          </button>
        </div>

        {claimsLoading && <p style={{ color: '#64748b', textAlign: 'center', padding: '1rem' }}>Loading…</p>}
        {!claimsLoading && claims.length === 0 && (
          <p style={{ textAlign: 'center', color: '#64748b', padding: '2rem', margin: 0 }}>No pending building claims</p>
        )}
        {!claimsLoading && claims.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                <th style={{ textAlign: 'left', padding: '0.75rem', color: '#475569', fontWeight: '600' }}>Address</th>
                <th style={{ textAlign: 'left', padding: '0.75rem', color: '#475569', fontWeight: '600' }}>PM Email</th>
                <th style={{ textAlign: 'left', padding: '0.75rem', color: '#475569', fontWeight: '600' }}>Doc Type</th>
                <th style={{ textAlign: 'left', padding: '0.75rem', color: '#475569', fontWeight: '600' }}>Document</th>
                <th style={{ textAlign: 'left', padding: '0.75rem', color: '#475569', fontWeight: '600' }}>Submitted</th>
                <th style={{ textAlign: 'left', padding: '0.75rem', color: '#475569', fontWeight: '600' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {claims.map(c => (
                <tr key={c.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '1rem 0.75rem', fontWeight: '500', color: '#0f172a' }}>{c.address}</td>
                  <td style={{ padding: '1rem 0.75rem', color: '#475569' }}>{c.userEmail}</td>
                  <td style={{ padding: '1rem 0.75rem' }}>
                    <span style={{ padding: '0.2rem 0.6rem', borderRadius: '9999px', fontSize: '0.7rem', fontWeight: '700', backgroundColor: '#dbeafe', color: '#1e40af' }}>
                      {{'psa':'PSA','title':'Title/Deed','mgmt':'Mgmt Agreement','tax':'Tax Record','lease':'Lease','other':'Other'}[c.docType] || c.docType}
                    </span>
                  </td>
                  <td style={{ padding: '1rem 0.75rem' }}>
                    {c.docURL
                      ? <a href={c.docURL} target="_blank" rel="noopener noreferrer" style={{ color: colors.primary, display: 'inline-flex', alignItems: 'center', gap: '0.3rem', fontWeight: '500' }}><Eye size={14} /> View</a>
                      : <span style={{ color: '#94a3b8' }}>No file</span>
                    }
                  </td>
                  <td style={{ padding: '1rem 0.75rem', color: '#64748b', fontSize: '0.8rem' }}>
                    {c.submittedAt?.seconds ? new Date(c.submittedAt.seconds * 1000).toLocaleDateString() : '—'}
                  </td>
                  <td style={{ padding: '1rem 0.75rem' }}>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button onClick={() => handleApprove(c.id)} style={{ padding: '0.4rem 0.875rem', backgroundColor: colors.success, color: 'white', border: 'none', borderRadius: '0.375rem', fontSize: '0.8rem', fontWeight: '600', cursor: 'pointer' }}>
                        Approve
                      </button>
                      <button onClick={() => handleReject(c.id)} style={{ padding: '0.4rem 0.875rem', backgroundColor: '#ef4444', color: 'white', border: 'none', borderRadius: '0.375rem', fontSize: '0.8rem', fontWeight: '600', cursor: 'pointer' }}>
                        Reject
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    );
  }

  function LoginPage() {
    const [mode, setMode] = useState('login'); // 'login' | 'signup'
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [name, setName] = useState('');
    const [signUpAccountType, setSignUpAccountType] = useState('property_manager');
    const [loginError, setLoginError] = useState(null);
    const [loggingIn, setLoggingIn] = useState(false);
    const [signUpSuccess, setSignUpSuccess] = useState(false);
    const [showForgotPassword, setShowForgotPassword] = useState(false);
    const [resetEmail, setResetEmail] = useState('');
    const [resetSent, setResetSent] = useState(false);
    const [resetError, setResetError] = useState(null);

    const handleResetPassword = async (e) => {
      e.preventDefault();
      setResetError(null);
      try {
        await resetPassword(resetEmail);
        setResetSent(true);
      } catch (err) {
        setResetError(err.message);
      }
    };

    const handleLogin = async (e) => {
      e.preventDefault();
      setLoggingIn(true);
      setLoginError(null);
      try {
        await loginWithEmail(email, password);
        // Auth state change will automatically redirect to dashboard
      } catch (err) {
        setLoginError(err.message);
        setLoggingIn(false);
      }
    };

    const handleSignUp = async (e) => {
      e.preventDefault();
      setLoginError(null);
      if (!name.trim()) { setLoginError('Please enter your full name'); return; }
      if (password.length < 6) { setLoginError('Password must be at least 6 characters'); return; }
      if (password !== confirmPassword) { setLoginError('Passwords do not match'); return; }
      setLoggingIn(true);
      try {
        await signUpB2B(email, password, name, signUpAccountType);
        setSignUpSuccess(true);
      } catch (err) {
        setLoginError(err.message);
      } finally {
        setLoggingIn(false);
      }
    };

    return (
      <div style={{ minHeight: '100vh', background: 'linear-gradient(to bottom right, #0f172a, #1e293b, #0f172a)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
        <div style={{ maxWidth: '450px', width: '100%' }}>
          <button onClick={() => setCurrentView('landing')} style={{ color: '#94a3b8', background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem', marginBottom: '2rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            ← Back to Home
          </button>

          <div style={{ backgroundColor: 'rgba(30, 41, 59, 0.9)', borderRadius: '1rem', padding: '2.5rem', border: '1px solid #334155' }}>
            <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
              <div style={{ width: '64px', height: '64px', borderRadius: '0.75rem', backgroundColor: colors.primary, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem' }}>
                <Building2 style={{ color: 'white' }} size={36} />
              </div>
              <h2 style={{ fontSize: '1.75rem', fontWeight: 'bold', color: 'white', marginBottom: '0.5rem' }}>
                {mode === 'login' ? 'Sign In' : 'Create Account'}
              </h2>
              <p style={{ color: '#94a3b8', fontSize: '0.875rem' }}>Property Managers, Lenders & Investors</p>
            </div>

            {/* Mode toggle */}
            <div style={{ display: 'flex', backgroundColor: 'rgba(15,23,42,0.5)', borderRadius: '0.5rem', padding: '0.25rem', marginBottom: '1.75rem' }}>
              {['login', 'signup'].map(m => (
                <button
                  key={m}
                  type="button"
                  onClick={() => { setMode(m); setLoginError(null); setSignUpSuccess(false); }}
                  style={{
                    flex: 1, padding: '0.5rem', border: 'none', cursor: 'pointer',
                    borderRadius: '0.375rem', fontSize: '0.875rem', fontWeight: '600',
                    backgroundColor: mode === m ? colors.primary : 'transparent',
                    color: mode === m ? 'white' : '#94a3b8',
                    transition: 'all 0.15s',
                  }}
                >
                  {m === 'login' ? 'Sign In' : 'Sign Up'}
                </button>
              ))}
            </div>

            {/* Sign-up success state */}
            {signUpSuccess ? (
              <div style={{ textAlign: 'center', padding: '1.5rem' }}>
                <CheckCircle size={48} style={{ color: colors.success, margin: '0 auto 1rem' }} />
                <h3 style={{ color: 'white', marginBottom: '0.5rem' }}>Account Created!</h3>
                <p style={{ color: '#94a3b8', fontSize: '0.875rem', marginBottom: '1.5rem' }}>
                  Check your email to verify your account, then sign in below.
                </p>
                <button
                  onClick={() => { setMode('login'); setSignUpSuccess(false); setPassword(''); setConfirmPassword(''); }}
                  style={{ width: '100%', padding: '0.875rem', backgroundColor: colors.primary, color: 'white', border: 'none', borderRadius: '0.5rem', cursor: 'pointer', fontWeight: '600' }}
                >
                  Go to Sign In
                </button>
              </div>
            ) : mode === 'signup' ? (
              /* ── Sign-up form ── */
              <form onSubmit={handleSignUp}>
                {/* Account type selector */}
                <div style={{ marginBottom: '1.25rem' }}>
                  <label style={{ display: 'block', color: '#cbd5e1', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>
                    I am a:
                  </label>
                  <div style={{ display: 'flex', gap: '0.75rem' }}>
                    {[['property_manager', 'Property Manager'], ['lender', 'Lender / Investor']].map(([val, label]) => (
                      <button
                        key={val}
                        type="button"
                        onClick={() => setSignUpAccountType(val)}
                        style={{
                          flex: 1, padding: '0.625rem', border: `2px solid ${signUpAccountType === val ? colors.primary : '#475569'}`,
                          borderRadius: '0.5rem', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '600',
                          backgroundColor: signUpAccountType === val ? `${colors.primary}20` : 'transparent',
                          color: signUpAccountType === val ? colors.primary : '#94a3b8',
                        }}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', color: '#cbd5e1', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>Full Name</label>
                  <input type="text" value={name} onChange={e => setName(e.target.value)} required placeholder="Jane Smith"
                    style={{ width: '100%', padding: '0.75rem', border: '1px solid #475569', borderRadius: '0.5rem', backgroundColor: 'rgba(15,23,42,0.5)', color: 'white', fontSize: '1rem', boxSizing: 'border-box' }} />
                </div>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', color: '#cbd5e1', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>Email Address</label>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)} required placeholder="jane@company.com"
                    style={{ width: '100%', padding: '0.75rem', border: '1px solid #475569', borderRadius: '0.5rem', backgroundColor: 'rgba(15,23,42,0.5)', color: 'white', fontSize: '1rem', boxSizing: 'border-box' }} />
                </div>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', color: '#cbd5e1', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>Password</label>
                  <input type="password" value={password} onChange={e => setPassword(e.target.value)} required placeholder="Min. 6 characters"
                    style={{ width: '100%', padding: '0.75rem', border: '1px solid #475569', borderRadius: '0.5rem', backgroundColor: 'rgba(15,23,42,0.5)', color: 'white', fontSize: '1rem', boxSizing: 'border-box' }} />
                </div>
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', color: '#cbd5e1', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>Confirm Password</label>
                  <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required placeholder="Re-enter password"
                    style={{ width: '100%', padding: '0.75rem', border: '1px solid #475569', borderRadius: '0.5rem', backgroundColor: 'rgba(15,23,42,0.5)', color: 'white', fontSize: '1rem', boxSizing: 'border-box' }} />
                </div>

                {loginError && (
                  <div style={{ backgroundColor: '#fee2e2', border: '1px solid #ef4444', borderRadius: '0.5rem', padding: '0.75rem', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <AlertCircle size={16} style={{ color: '#dc2626', flexShrink: 0 }} />
                    <p style={{ color: '#991b1b', margin: 0, fontSize: '0.875rem' }}>{loginError}</p>
                  </div>
                )}

                <button type="submit" disabled={loggingIn}
                  style={{ width: '100%', padding: '0.875rem', backgroundColor: colors.primary, color: 'white', border: 'none', borderRadius: '0.5rem', cursor: loggingIn ? 'not-allowed' : 'pointer', fontWeight: '700', fontSize: '1rem', opacity: loggingIn ? 0.7 : 1 }}>
                  {loggingIn ? 'Creating Account...' : 'Create Account'}
                </button>

                <p style={{ color: '#64748b', fontSize: '0.75rem', textAlign: 'center', marginTop: '1rem' }}>
                  Tenant? Download the Toice app to sign up.
                </p>
              </form>
            ) : (
              /* ── Sign-in form ── */
              <form onSubmit={handleLogin}>
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', color: '#cbd5e1', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>
                  Email Address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="manager@property.com"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #475569',
                    borderRadius: '0.5rem',
                    backgroundColor: 'rgba(15, 23, 42, 0.5)',
                    color: 'white',
                    fontSize: '1rem',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', color: '#cbd5e1', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>
                  Password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="••••••••"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #475569',
                    borderRadius: '0.5rem',
                    backgroundColor: 'rgba(15, 23, 42, 0.5)',
                    color: 'white',
                    fontSize: '1rem',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {loginError && (
                <div style={{
                  backgroundColor: '#fee2e2',
                  border: '1px solid #ef4444',
                  borderRadius: '0.5rem',
                  padding: '0.75rem',
                  marginBottom: '1.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <AlertCircle size={18} style={{ color: '#dc2626' }} />
                  <p style={{ color: '#991b1b', margin: 0, fontSize: '0.875rem' }}>{loginError}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={loggingIn}
                style={{
                  width: '100%',
                  padding: '0.875rem',
                  backgroundColor: loggingIn ? '#64748b' : colors.primary,
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.5rem',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: loggingIn ? 'not-allowed' : 'pointer',
                  marginBottom: '1rem'
                }}
              >
                {loggingIn ? 'Signing In...' : 'Sign In'}
              </button>

              <p style={{ color: '#94a3b8', fontSize: '0.875rem', textAlign: 'center', margin: 0, marginBottom: '0.75rem' }}>
                <button onClick={() => setShowForgotPassword(true)} style={{ color: colors.primary, background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.875rem', textDecoration: 'none' }}>
                  Forgot your password?
                </button>
              </p>
            </form>
            )}

            {/* ── Interactive Demo divider ── */}
            {!signUpSuccess && (
              <div style={{ marginTop: '1.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                  <div style={{ flex: 1, height: '1px', backgroundColor: '#334155' }} />
                  <span style={{ color: '#64748b', fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>or try interactive demo</span>
                  <div style={{ flex: 1, height: '1px', backgroundColor: '#334155' }} />
                </div>
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  <button
                    type="button"
                    onClick={() => enterDemoMode('pm')}
                    style={{
                      flex: 1, padding: '0.625rem 0.5rem',
                      backgroundColor: 'rgba(23, 139, 155, 0.12)',
                      color: colors.primary,
                      border: `1px solid ${colors.primary}`,
                      borderRadius: '0.5rem', fontSize: '0.8rem', fontWeight: '600',
                      cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem'
                    }}
                  >
                    <Building2 size={14} /> Property Manager
                  </button>
                  <button
                    type="button"
                    onClick={() => enterDemoMode('lender')}
                    style={{
                      flex: 1, padding: '0.625rem 0.5rem',
                      backgroundColor: 'rgba(255, 133, 51, 0.1)',
                      color: colors.orange,
                      border: `1px solid ${colors.orange}`,
                      borderRadius: '0.5rem', fontSize: '0.8rem', fontWeight: '600',
                      cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem'
                    }}
                  >
                    <TrendingUp size={14} /> Lender / Investor
                  </button>
                </div>
                <p style={{ color: '#475569', fontSize: '0.7rem', textAlign: 'center', marginTop: '0.625rem', marginBottom: 0 }}>
                  No account needed · Sample data · Full feature preview
                </p>
              </div>
            )}

            {showForgotPassword && (
              <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
                <div style={{ backgroundColor: '#1e293b', borderRadius: '1rem', padding: '2rem', border: '1px solid #334155', maxWidth: '400px', width: '90%' }}>
                  <h3 style={{ color: 'white', marginTop: 0, marginBottom: '0.5rem' }}>Reset Password</h3>
                  {resetSent ? (
                    <>
                      <p style={{ color: '#10b981', fontSize: '0.875rem' }}>Password reset email sent. Check your inbox.</p>
                      <button onClick={() => { setShowForgotPassword(false); setResetSent(false); setResetEmail(''); }} style={{ width: '100%', padding: '0.75rem', backgroundColor: colors.primary, color: 'white', border: 'none', borderRadius: '0.5rem', cursor: 'pointer', fontWeight: '600' }}>
                        Back to Sign In
                      </button>
                    </>
                  ) : (
                    <form onSubmit={handleResetPassword}>
                      <p style={{ color: '#94a3b8', fontSize: '0.875rem', marginBottom: '1rem' }}>Enter your email and we'll send you a reset link.</p>
                      <input
                        type="email"
                        value={resetEmail}
                        onChange={(e) => setResetEmail(e.target.value)}
                        required
                        placeholder="your@email.com"
                        style={{ width: '100%', padding: '0.75rem', border: '1px solid #475569', borderRadius: '0.5rem', backgroundColor: 'rgba(15, 23, 42, 0.5)', color: 'white', fontSize: '1rem', boxSizing: 'border-box', marginBottom: '1rem' }}
                      />
                      {resetError && <p style={{ color: '#ef4444', fontSize: '0.875rem', marginBottom: '1rem' }}>{resetError}</p>}
                      <div style={{ display: 'flex', gap: '0.75rem' }}>
                        <button type="button" onClick={() => { setShowForgotPassword(false); setResetError(null); }} style={{ flex: 1, padding: '0.75rem', backgroundColor: 'transparent', color: '#94a3b8', border: '1px solid #475569', borderRadius: '0.5rem', cursor: 'pointer' }}>
                          Cancel
                        </button>
                        <button type="submit" style={{ flex: 1, padding: '0.75rem', backgroundColor: colors.primary, color: 'white', border: 'none', borderRadius: '0.5rem', cursor: 'pointer', fontWeight: '600' }}>
                          Send Reset Link
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Shared claim form used inside PM dashboard ──────────────────────────────
  function ClaimBuildingSection() {
    const emptyForm = () => ({ street: '', city: '', state: '', zip: '', docType: 'psa', file: null, submitting: false });
    const [forms, setForms] = useState([emptyForm()]);
    const [claims, setClaims] = useState(myClaims);

    React.useEffect(() => { setClaims(myClaims); }, []);

    const updateForm = (idx, field, value) => {
      setForms(prev => prev.map((f, i) => i === idx ? { ...f, [field]: value } : f));
    };

    const addForm = () => setForms(prev => [...prev, emptyForm()]);
    const removeForm = (idx) => setForms(prev => prev.length === 1 ? [emptyForm()] : prev.filter((_, i) => i !== idx));

    const handleSubmitClaim = async (e, idx) => {
      e.preventDefault();
      const f = forms[idx];
      const address = [f.street, f.city, f.state, f.zip].filter(Boolean).join(', ');
      if (!f.street.trim()) { toast.error('Please enter a street address'); return; }
      updateForm(idx, 'submitting', true);
      try {
        await submitBuildingClaim(user.uid, user.email, address, f.docType, f.file);
        const updated = await getMyBuildingClaims(user.uid);
        setMyClaims(updated);
        setClaims(updated);
        removeForm(idx);
        toast.success("Claim submitted! We'll review your document within 1–2 business days.");
      } catch (err) {
        toast.error('Failed to submit: ' + err.message);
        updateForm(idx, 'submitting', false);
      }
    };

    const statusBadge = (status) => {
      const map = {
        pending:  { bg: '#fef3c7', color: '#92400e', label: 'Under Review' },
        approved: { bg: '#d1fae5', color: '#065f46', label: 'Approved' },
        rejected: { bg: '#fee2e2', color: '#991b1b', label: 'Rejected' },
      };
      const s = map[status] || map.pending;
      return (
        <span style={{ padding: '0.2rem 0.6rem', borderRadius: '9999px', fontSize: '0.7rem', fontWeight: '700', backgroundColor: s.bg, color: s.color }}>
          {s.label}
        </span>
      );
    };

    const inputStyle = { width: '100%', padding: '0.7rem', border: '1px solid #d1d5db', borderRadius: '0.5rem', fontSize: '0.875rem', boxSizing: 'border-box' };
    const labelStyle = { display: 'block', fontSize: '0.8rem', fontWeight: '600', color: '#374151', marginBottom: '0.4rem' };

    return (
      <div style={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '0.75rem', padding: '1.5rem', marginBottom: '2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
          <div>
            <h3 style={{ fontSize: '1rem', fontWeight: '700', color: '#0f172a', margin: 0 }}>Claim a Building</h3>
            <p style={{ color: '#64748b', fontSize: '0.8rem', margin: '0.2rem 0 0' }}>Upload proof of ownership to add a property to your dashboard</p>
          </div>
          <button
            onClick={addForm}
            style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.6rem 1.25rem', backgroundColor: colors.primary, color: 'white', border: 'none', borderRadius: '0.5rem', fontWeight: '600', fontSize: '0.875rem', cursor: 'pointer' }}
          >
            <Plus size={15} /> Add Building
          </button>
        </div>

        {/* Existing claims table */}
        {claims.length > 0 && (
          <div style={{ marginBottom: '1.5rem' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <th style={{ textAlign: 'left', padding: '0.5rem 0.75rem', color: '#64748b', fontWeight: '600', fontSize: '0.75rem' }}>Address</th>
                  <th style={{ textAlign: 'left', padding: '0.5rem 0.75rem', color: '#64748b', fontWeight: '600', fontSize: '0.75rem' }}>Document</th>
                  <th style={{ textAlign: 'left', padding: '0.5rem 0.75rem', color: '#64748b', fontWeight: '600', fontSize: '0.75rem' }}>Status</th>
                  <th style={{ textAlign: 'left', padding: '0.5rem 0.75rem', color: '#64748b', fontWeight: '600', fontSize: '0.75rem' }}>Submitted</th>
                </tr>
              </thead>
              <tbody>
                {claims.map(c => (
                  <tr key={c.id} style={{ borderBottom: '1px solid #f8fafc' }}>
                    <td style={{ padding: '0.75rem', color: '#0f172a', fontWeight: '500' }}>{c.address}</td>
                    <td style={{ padding: '0.75rem' }}>
                      {c.docURL
                        ? <a href={c.docURL} target="_blank" rel="noopener noreferrer" style={{ color: colors.primary, fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}><FileText size={13} /> {c.docFileName || 'View Doc'}</a>
                        : <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>No file</span>
                      }
                    </td>
                    <td style={{ padding: '0.75rem' }}>{statusBadge(c.status)}</td>
                    <td style={{ padding: '0.75rem', color: '#64748b', fontSize: '0.8rem' }}>
                      {c.submittedAt?.seconds ? new Date(c.submittedAt.seconds * 1000).toLocaleDateString() : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {claims.some(c => c.status === 'rejected') && (
              <div style={{ marginTop: '0.75rem', padding: '0.75rem 1rem', backgroundColor: '#fff7ed', border: '1px solid #fdba74', borderRadius: '0.5rem', fontSize: '0.8rem', color: '#9a3412' }}>
                One or more claims were rejected. Please re-submit with a clearer document or a different address.
              </div>
            )}
          </div>
        )}

        {/* Claim forms — one per pending submission */}
        {forms.map((f, idx) => (
          <form key={idx} onSubmit={e => handleSubmitClaim(e, idx)} style={{ border: '1px solid #e2e8f0', borderRadius: '0.625rem', padding: '1.25rem', marginBottom: idx < forms.length - 1 ? '1rem' : 0, backgroundColor: '#f8fafc' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>Building {forms.length > 1 ? idx + 1 : ''}</span>
              <button type="button" onClick={() => removeForm(idx)} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: '0.8rem', textDecoration: 'underline' }}>Remove</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={labelStyle}>Street Address *</label>
                <input type="text" value={f.street} onChange={e => updateForm(idx, 'street', e.target.value)} placeholder="123 Main St" required style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>City *</label>
                <input type="text" value={f.city} onChange={e => updateForm(idx, 'city', e.target.value)} placeholder="Boston" required style={inputStyle} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                <div>
                  <label style={labelStyle}>State *</label>
                  <input type="text" value={f.state} onChange={e => updateForm(idx, 'state', e.target.value)} placeholder="MA" maxLength={2} required style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>ZIP</label>
                  <input type="text" value={f.zip} onChange={e => updateForm(idx, 'zip', e.target.value)} placeholder="02101" maxLength={5} style={inputStyle} />
                </div>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1rem' }}>
              <div>
                <label style={labelStyle}>Document Type</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', paddingTop: '0.4rem' }}>
                  {[['psa', 'PSA'], ['title', 'Title / Deed'], ['mgmt', 'Management Agreement'], ['tax', 'Tax Record'], ['lease', 'Lease Agreement'], ['other', 'Other']].map(([val, label]) => (
                    <label key={val} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', fontSize: '0.8rem', color: f.docType === val ? colors.primary : '#374151', fontWeight: f.docType === val ? '600' : '400' }}>
                      <input type="radio" name={`docType-${idx}`} value={val} checked={f.docType === val} onChange={() => updateForm(idx, 'docType', val)} style={{ accentColor: colors.primary }} />
                      {label}
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label style={labelStyle}>Upload Document <span style={{ color: '#94a3b8', fontWeight: '400' }}>(optional for demo)</span></label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.65rem 0.875rem', border: '1px dashed #d1d5db', borderRadius: '0.5rem', cursor: 'pointer', fontSize: '0.8rem', color: f.file ? colors.primary : '#64748b', backgroundColor: f.file ? 'rgba(23,139,155,0.05)' : '#f9fafb' }}>
                  <FileText size={15} />
                  {f.file ? f.file.name : 'Choose PDF, JPG, or PNG… (skip for demo)'}
                  <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={e => updateForm(idx, 'file', e.target.files[0])} style={{ display: 'none' }} />
                </label>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
              <button
                type="submit"
                disabled={f.submitting}
                style={{ padding: '0.7rem 1.75rem', backgroundColor: f.submitting ? '#94a3b8' : colors.primary, color: 'white', border: 'none', borderRadius: '0.5rem', fontWeight: '600', fontSize: '0.875rem', cursor: f.submitting ? 'not-allowed' : 'pointer' }}
              >
                {f.submitting ? 'Submitting…' : 'Submit Claim'}
              </button>
              <p style={{ color: '#94a3b8', fontSize: '0.75rem', margin: 0 }}>Reviewed within 1–2 business days</p>
            </div>
          </form>
        ))}
      </div>
    );
  }

  function PropertyManagerDashboard() {
    const stats = {
      totalBuildings: buildings.length,
      activeIssues: buildings.reduce((sum, b) => sum + (b.totalIssues || 0), 0),
      avgRiskScore: buildings.length > 0 ? Math.round(buildings.reduce((sum, b) => sum + (b.riskScore || 0), 0) / buildings.length) : 0,
      improvements: buildings.reduce((sum, b) => sum + (b.improvementCount || 0), 0)
    };

    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
        <Navigation
          currentView={currentView}
          setCurrentView={setCurrentView}
          user={user}
          colors={colors}
          onLogout={handleLogout}
          accountType={accountType}
          demoMode={demoMode}
          onExitDemo={exitDemoMode}
        />

        <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '2rem' }}>
          {loading && (
            <div>
              <StatsGridSkeleton />
              <BuildingListSkeleton count={3} />
            </div>
          )}

          {error && (
            <div style={{ backgroundColor: '#fee2e2', border: '1px solid #ef4444', borderRadius: '0.5rem', padding: '1rem', marginBottom: '2rem' }}>
              <p style={{ color: '#991b1b', margin: 0, fontWeight: '600' }}>Error: {error}</p>
              <button onClick={loadManagerBuildings} style={{ marginTop: '0.5rem', padding: '0.5rem 1rem', backgroundColor: '#ef4444', color: 'white', border: 'none', borderRadius: '0.375rem', cursor: 'pointer' }}>
                Retry
              </button>
            </div>
          )}

          {!loading && !error && (
            <>
              <ClaimBuildingSection />
              {buildings.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '4rem', backgroundColor: 'white', borderRadius: '0.75rem', border: '1px solid #e2e8f0' }}>
                  <Building2 size={64} style={{ margin: '0 auto 1.5rem', color: '#cbd5e1' }} />
                  <h3 style={{ fontSize: '1.5rem', fontWeight: '600', color: '#0f172a', marginBottom: '0.5rem' }}>No Approved Properties Yet</h3>
                  <p style={{ color: '#64748b', fontSize: '0.875rem' }}>
                    Submit a claim above. Once we verify your ownership document, your building will appear here.
                  </p>
                </div>
              ) : (
                <>
                  <div className="stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
                    <div style={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '0.5rem', padding: '1.5rem' }}>
                      <div style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '0.5rem' }}>Your Buildings</div>
                      <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#0f172a' }}>{stats.totalBuildings}</div>
                      <div style={{ fontSize: '0.75rem', color: '#10b981', marginTop: '0.25rem' }}>Actively monitored</div>
                    </div>
                    <div style={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '0.5rem', padding: '1.5rem' }}>
                      <div style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '0.5rem' }}>Active Issues</div>
                      <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#0f172a' }}>{stats.activeIssues}</div>
                      <div style={{ fontSize: '0.75rem', color: stats.activeIssues > 10 ? '#ef4444' : '#10b981', marginTop: '0.25rem' }}>
                        Tenant reports
                      </div>
                    </div>
                    <div style={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '0.5rem', padding: '1.5rem' }}>
                      <div style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '0.5rem' }}>Avg Risk Score</div>
                      <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: stats.avgRiskScore >= 70 ? '#dc2626' : stats.avgRiskScore >= 50 ? '#ea580c' : '#10b981' }}>
                        {stats.avgRiskScore}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.25rem' }}>Toice Risk Intelligence</div>
                    </div>
                    <div style={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '0.5rem', padding: '1.5rem' }}>
                      <div style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '0.5rem' }}>Improvements</div>
                      <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#0f172a' }}>{stats.improvements}</div>
                      <div style={{ fontSize: '0.75rem', color: '#10b981', marginTop: '0.25rem' }}>Documented</div>
                    </div>
                  </div>

                  {/* PM Charts Section */}
                  <div className="chart-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
                    <IssuesByCategoryChart
                      data={(() => {
                        const categoryCounts = {};
                        buildings.forEach(building => {
                          if (building.reviews) {
                            building.reviews.forEach(review => {
                              const category = review.category || 'Other';
                              categoryCounts[category] = (categoryCounts[category] || 0) + 1;
                            });
                          }
                        });
                        return Object.entries(categoryCounts)
                          .map(([category, count]) => ({ category, count }))
                          .sort((a, b) => b.count - a.count)
                          .slice(0, 10);
                      })()}
                    />
                    <RemediationTimeChart />
                  </div>

                  <div style={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '0.5rem', padding: '1.5rem' }}>
                    <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#0f172a', marginBottom: '1rem' }}>Your Properties</h3>
                    <div className="table-scroll" style={{ overflowX: 'auto' }}>
                      <table className="data-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                            <th style={{ textAlign: 'left', padding: '0.75rem', fontSize: '0.875rem', fontWeight: '600', color: '#475569' }}>Address</th>
                            <th style={{ textAlign: 'left', padding: '0.75rem', fontSize: '0.875rem', fontWeight: '600', color: '#475569' }}>Risk Score</th>
                            <th style={{ textAlign: 'left', padding: '0.75rem', fontSize: '0.875rem', fontWeight: '600', color: '#475569' }}>Issues</th>
                            <th style={{ textAlign: 'left', padding: '0.75rem', fontSize: '0.875rem', fontWeight: '600', color: '#475569' }}>Level</th>
                          </tr>
                        </thead>
                        <tbody>
                          {buildings.map((building, idx) => (
                            <tr key={idx} onClick={() => { setSelectedBuilding(building); setCurrentView('building-detail'); }} style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer', transition: 'background-color 0.15s' }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                              <td style={{ padding: '1rem 0.75rem', fontSize: '0.875rem', color: '#0f172a', fontWeight: '500' }}>{building.address}</td>
                              <td style={{ padding: '1rem 0.75rem' }}>
                                <span style={{
                                  padding: '0.375rem 0.75rem',
                                  borderRadius: '9999px',
                                  fontSize: '0.875rem',
                                  fontWeight: '700',
                                  backgroundColor: building.riskScore >= 70 ? '#fee2e2' : building.riskScore >= 50 ? '#fed7aa' : '#d1fae5',
                                  color: building.riskScore >= 70 ? '#991b1b' : building.riskScore >= 50 ? '#9a3412' : '#065f46'
                                }}>
                                  {building.riskScore}
                                </span>
                              </td>
                              <td style={{ padding: '1rem 0.75rem', fontSize: '0.875rem', color: '#0f172a' }}>{building.totalIssues || 0}</td>
                              <td style={{ padding: '1rem 0.75rem' }}>
                                <span style={{
                                  padding: '0.25rem 0.75rem',
                                  borderRadius: '0.375rem',
                                  fontSize: '0.75rem',
                                  fontWeight: '600',
                                  backgroundColor: building.riskLevel === 'CRITICAL' ? '#fee2e2' : building.riskLevel === 'HIGH' ? '#fed7aa' : '#d1fae5',
                                  color: building.riskLevel === 'CRITICAL' ? '#991b1b' : building.riskLevel === 'HIGH' ? '#9a3412' : '#065f46'
                                }}>
                                  {building.riskLevel || 'LOW'}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  // ==================== LENDER DASHBOARD ====================
  function LenderDashboard() {
    const [searchAddress, setSearchAddress] = useState('');
    const [addingBuilding, setAddingBuilding] = useState(false);
    const [filterSponsor, setFilterSponsor] = useState('');
    const [filterState, setFilterState] = useState('');
    const [filterLoanClosed, setFilterLoanClosed] = useState('');
    const [filterLoanMatures, setFilterLoanMatures] = useState('');

    // Derive unique filter options from data
    const sponsors = [...new Set(buildings.map(b => b.sponsor).filter(Boolean))];
    const states = [...new Set(buildings.map(b => b.state).filter(Boolean))];
    const loanClosedYears = [...new Set(buildings.map(b => b.loanClosedYear).filter(Boolean))].sort();
    const loanMaturesYears = [...new Set(buildings.map(b => b.loanMaturesYear).filter(Boolean))].sort();

    const filteredBuildings = buildings.filter(b => {
      if (filterSponsor && b.sponsor !== filterSponsor) return false;
      if (filterState && b.state !== filterState) return false;
      if (filterLoanClosed && b.loanClosedYear !== Number(filterLoanClosed)) return false;
      if (filterLoanMatures && b.loanMaturesYear !== Number(filterLoanMatures)) return false;
      return true;
    });

    const stats = {
      totalBuildings: buildings.length,
      avgRiskScore: buildings.length > 0 ? Math.round(buildings.reduce((sum, b) => sum + (b.riskScore || 0), 0) / buildings.length) : 0,
      totalIssues: buildings.reduce((sum, b) => sum + (b.totalIssues || 0), 0),
      documentsUploaded: 0
    };

    const handleAddBuilding = async () => {
      if (demoMode) { toast('Sign up to use this feature', { icon: '🔒' }); return; }
      if (!searchAddress.trim()) return;
      try {
        setAddingBuilding(true);
        await addBuildingToPortfolio(user.uid, searchAddress.trim());
        toast.success('Building added to portfolio');
        setSearchAddress('');
        await loadLenderBuildings();
      } catch (err) {
        toast.error('Error: ' + err.message);
      } finally {
        setAddingBuilding(false);
      }
    };

    const handleRemoveBuilding = async (address) => {
      if (demoMode) { toast('Sign up to use this feature', { icon: '🔒' }); return; }
      if (!window.confirm(`Remove "${address}" from your portfolio?`)) return;
      try {
        await removeBuildingFromPortfolio(user.uid, address);
        toast.success('Building removed from portfolio');
        await loadLenderBuildings();
      } catch (err) {
        toast.error('Error: ' + err.message);
      }
    };

    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
        <Navigation
          currentView={currentView}
          setCurrentView={setCurrentView}
          user={user}
          colors={colors}
          onLogout={handleLogout}
          accountType={accountType}
          demoMode={demoMode}
          onExitDemo={exitDemoMode}
        />

        <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '2rem' }}>
          <div style={{ marginBottom: '2rem' }}>
            <h2 style={{ fontSize: '1.75rem', fontWeight: 'bold', color: '#0f172a', marginBottom: '0.25rem' }}>Lender Portfolio</h2>
            <p style={{ color: '#64748b', fontSize: '0.875rem' }}>Monitor risk across your watched properties</p>
          </div>

          {loading && (
            <div>
              <StatsGridSkeleton />
              <BuildingListSkeleton count={3} />
            </div>
          )}

          {error && (
            <div style={{ backgroundColor: '#fee2e2', border: '1px solid #ef4444', borderRadius: '0.5rem', padding: '1rem', marginBottom: '2rem' }}>
              <p style={{ color: '#991b1b', margin: 0, fontWeight: '600' }}>Error: {error}</p>
              <button onClick={loadLenderBuildings} style={{ marginTop: '0.5rem', padding: '0.5rem 1rem', backgroundColor: '#ef4444', color: 'white', border: 'none', borderRadius: '0.375rem', cursor: 'pointer' }}>
                Retry
              </button>
            </div>
          )}

          {!loading && !error && (
            <>
              {/* Add Building to Portfolio */}
              <div style={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '0.5rem', padding: '1.5rem', marginBottom: '2rem' }}>
                <h3 style={{ fontSize: '1rem', fontWeight: '600', color: '#0f172a', marginBottom: '1rem' }}>Add Building to Portfolio</h3>
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  <input
                    type="text"
                    value={searchAddress}
                    onChange={(e) => setSearchAddress(e.target.value)}
                    placeholder="Enter building address..."
                    style={{ flex: 1, padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '0.875rem' }}
                    onKeyDown={(e) => e.key === 'Enter' && handleAddBuilding()}
                  />
                  <button
                    onClick={handleAddBuilding}
                    disabled={addingBuilding || !searchAddress.trim()}
                    style={{ padding: '0.75rem 1.5rem', backgroundColor: colors.primary, color: 'white', border: 'none', borderRadius: '0.375rem', cursor: 'pointer', fontWeight: '600', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '0.5rem', opacity: addingBuilding ? 0.7 : 1 }}
                  >
                    <Plus size={16} /> Add
                  </button>
                </div>
              </div>

              {/* Stats Grid */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
                <div style={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '0.5rem', padding: '1.5rem' }}>
                  <div style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '0.5rem' }}>Watched Buildings</div>
                  <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#0f172a' }}>{stats.totalBuildings}</div>
                  <div style={{ fontSize: '0.75rem', color: '#10b981', marginTop: '0.25rem' }}>In your portfolio</div>
                </div>
                <div style={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '0.5rem', padding: '1.5rem' }}>
                  <div style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '0.5rem' }}>Avg Risk Score</div>
                  <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: stats.avgRiskScore >= 70 ? '#dc2626' : stats.avgRiskScore >= 50 ? '#ea580c' : '#10b981' }}>
                    {stats.avgRiskScore}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.25rem' }}>Toice Risk Intelligence</div>
                </div>
                <div style={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '0.5rem', padding: '1.5rem' }}>
                  <div style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '0.5rem' }}>Total Issues</div>
                  <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#0f172a' }}>{stats.totalIssues}</div>
                  <div style={{ fontSize: '0.75rem', color: stats.totalIssues > 10 ? '#ef4444' : '#10b981', marginTop: '0.25rem' }}>Across all buildings</div>
                </div>
              </div>

              {buildings.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '4rem', backgroundColor: 'white', borderRadius: '0.75rem', border: '1px solid #e2e8f0' }}>
                  <Eye size={64} style={{ margin: '0 auto 1.5rem', color: '#cbd5e1' }} />
                  <h3 style={{ fontSize: '1.5rem', fontWeight: '600', color: '#0f172a', marginBottom: '0.5rem' }}>No Buildings in Portfolio</h3>
                  <p style={{ color: '#64748b', fontSize: '0.875rem' }}>
                    Add building addresses above to start monitoring their risk data.
                  </p>
                </div>
              ) : (
                <div style={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '0.5rem', padding: '1.5rem' }}>
                  <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#0f172a', marginBottom: '1rem' }}>Portfolio Buildings</h3>
                  <div style={{ overflowX: 'auto' }}>
                    <table className="data-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                          <th style={{ textAlign: 'left', padding: '0.75rem', fontSize: '0.875rem', fontWeight: '600', color: '#475569' }}>Address</th>
                          <th style={{ textAlign: 'left', padding: '0.75rem', fontSize: '0.875rem', fontWeight: '600', color: '#475569' }}>Risk Score</th>
                          <th style={{ textAlign: 'left', padding: '0.75rem', fontSize: '0.875rem', fontWeight: '600', color: '#475569' }}>Reviews</th>
                          <th style={{ textAlign: 'left', padding: '0.75rem', fontSize: '0.875rem', fontWeight: '600', color: '#475569' }}>Level</th>
                          <th style={{ textAlign: 'left', padding: '0.75rem', fontSize: '0.875rem', fontWeight: '600', color: '#475569' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {buildings.map((building, idx) => (
                          <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer', transition: 'background-color 0.15s' }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                            <td onClick={() => { setSelectedBuilding(building); setCurrentView('building-detail'); }} style={{ padding: '1rem 0.75rem', fontSize: '0.875rem', color: colors.primary, fontWeight: '500', textDecoration: 'underline' }}>{building.address}</td>
                            <td onClick={() => { setSelectedBuilding(building); setCurrentView('building-detail'); }} style={{ padding: '1rem 0.75rem' }}>
                              <span style={{
                                padding: '0.375rem 0.75rem',
                                borderRadius: '9999px',
                                fontSize: '0.875rem',
                                fontWeight: '700',
                                backgroundColor: building.riskScore >= 70 ? '#fee2e2' : building.riskScore >= 50 ? '#fed7aa' : '#d1fae5',
                                color: building.riskScore >= 70 ? '#991b1b' : building.riskScore >= 50 ? '#9a3412' : '#065f46'
                              }}>
                                {building.riskScore}
                              </span>
                            </td>
                            <td onClick={() => { setSelectedBuilding(building); setCurrentView('building-detail'); }} style={{ padding: '1rem 0.75rem', fontSize: '0.875rem', color: '#0f172a' }}>{building.reviews?.length || 0}</td>
                            <td onClick={() => { setSelectedBuilding(building); setCurrentView('building-detail'); }} style={{ padding: '1rem 0.75rem' }}>
                              <span style={{
                                padding: '0.25rem 0.75rem',
                                borderRadius: '0.375rem',
                                fontSize: '0.75rem',
                                fontWeight: '600',
                                backgroundColor: building.riskLevel === 'CRITICAL' ? '#fee2e2' : building.riskLevel === 'HIGH' ? '#fed7aa' : '#d1fae5',
                                color: building.riskLevel === 'CRITICAL' ? '#991b1b' : building.riskLevel === 'HIGH' ? '#9a3412' : '#065f46'
                              }}>
                                {building.riskLevel || 'LOW'}
                              </span>
                            </td>
                            <td style={{ padding: '1rem 0.75rem' }}>
                              <button onClick={(e) => { e.stopPropagation(); handleRemoveBuilding(building.address); }} style={{ padding: '0.375rem 0.75rem', backgroundColor: '#fee2e2', color: '#991b1b', border: 'none', borderRadius: '0.375rem', cursor: 'pointer', fontSize: '0.75rem', fontWeight: '500' }}>
                                Remove
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* ===== PORTFOLIO ANALYTICS ===== */}
              {buildings.length > 0 && (
                <div style={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '0.5rem', padding: '1.5rem', marginTop: '2rem' }}>
                  <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#0f172a', marginBottom: '1rem' }}>Portfolio Analytics</h3>

                  {/* Filter Row */}
                  <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
                    <select value={filterSponsor} onChange={(e) => setFilterSponsor(e.target.value)} style={{ padding: '0.5rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '0.875rem', color: '#475569', backgroundColor: 'white' }}>
                      <option value="">All Sponsors</option>
                      {sponsors.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <select value={filterState} onChange={(e) => setFilterState(e.target.value)} style={{ padding: '0.5rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '0.875rem', color: '#475569', backgroundColor: 'white' }}>
                      <option value="">All States</option>
                      {states.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <select value={filterLoanClosed} onChange={(e) => setFilterLoanClosed(e.target.value)} style={{ padding: '0.5rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '0.875rem', color: '#475569', backgroundColor: 'white' }}>
                      <option value="">Loan Closed (All)</option>
                      {loanClosedYears.map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                    <select value={filterLoanMatures} onChange={(e) => setFilterLoanMatures(e.target.value)} style={{ padding: '0.5rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '0.875rem', color: '#475569', backgroundColor: 'white' }}>
                      <option value="">Loan Matures (All)</option>
                      {loanMaturesYears.map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                  </div>

                  <p style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '1rem' }}>
                    Showing {filteredBuildings.length} of {buildings.length} buildings
                  </p>

                  <div style={{ overflowX: 'auto' }}>
                    <table className="data-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                          <th style={{ textAlign: 'left', padding: '0.75rem', fontSize: '0.875rem', fontWeight: '600', color: '#475569' }}>Building</th>
                          <th style={{ textAlign: 'left', padding: '0.75rem', fontSize: '0.875rem', fontWeight: '600', color: '#475569' }}>Sponsor</th>
                          <th style={{ textAlign: 'left', padding: '0.75rem', fontSize: '0.875rem', fontWeight: '600', color: '#475569' }}>State</th>
                          <th style={{ textAlign: 'center', padding: '0.75rem', fontSize: '0.875rem', fontWeight: '600', color: '#475569' }}>Risk Score</th>
                          <th style={{ textAlign: 'center', padding: '0.75rem', fontSize: '0.875rem', fontWeight: '600', color: '#475569' }}>Avg Remediation</th>
                          <th style={{ textAlign: 'center', padding: '0.75rem', fontSize: '0.875rem', fontWeight: '600', color: '#475569' }}>Loan Closed</th>
                          <th style={{ textAlign: 'center', padding: '0.75rem', fontSize: '0.875rem', fontWeight: '600', color: '#475569' }}>Loan Matures</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredBuildings.map((b, idx) => (
                          <tr
                            key={idx}
                            style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer', transition: 'background-color 0.15s' }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                            onClick={() => { setSelectedBuilding(b); setCurrentView('building-detail'); }}
                          >
                            <td style={{ padding: '0.75rem', fontSize: '0.875rem', color: colors.primary, fontWeight: '500' }}>{b.address}</td>
                            <td style={{ padding: '0.75rem', fontSize: '0.875rem', color: '#0f172a' }}>{b.sponsor || '—'}</td>
                            <td style={{ padding: '0.75rem', fontSize: '0.875rem', color: '#0f172a' }}>{b.state || '—'}</td>
                            <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                              <span style={{
                                padding: '0.25rem 0.625rem',
                                borderRadius: '9999px',
                                fontSize: '0.8rem',
                                fontWeight: '700',
                                backgroundColor: b.riskScore >= 70 ? '#fee2e2' : b.riskScore >= 50 ? '#fed7aa' : '#d1fae5',
                                color: b.riskScore >= 70 ? '#991b1b' : b.riskScore >= 50 ? '#9a3412' : '#065f46'
                              }}>
                                {b.riskScore}
                              </span>
                            </td>
                            <td style={{ padding: '0.75rem', fontSize: '0.875rem', color: '#0f172a', textAlign: 'center' }}>
                              {b.avgRemediationTime != null ? `${b.avgRemediationTime} days` : 'N/A'}
                            </td>
                            <td style={{ padding: '0.75rem', fontSize: '0.875rem', color: '#0f172a', textAlign: 'center' }}>{b.loanClosedYear || '—'}</td>
                            <td style={{ padding: '0.75rem', fontSize: '0.875rem', color: '#0f172a', textAlign: 'center' }}>{b.loanMaturesYear || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  // ==================== BUILDING DETAIL VIEW ====================
  function BuildingDetailView() {
    const [activeTab, setActiveTab] = useState('overview');
    const [docType, setDocType] = useState('PCR');
    const [uploading, setUploading] = useState(false);
    const [inspectionRating, setInspectionRating] = useState(3);
    const [inspectionUnits, setInspectionUnits] = useState('');
    const [inspectionComments, setInspectionComments] = useState('');
    const [savingInspection, setSavingInspection] = useState(false);
    const [remediationOpenIdx, setRemediationOpenIdx] = useState(null);
    const [remediationDesc, setRemediationDesc] = useState('');
    const [remediationFile, setRemediationFile] = useState(null);

    if (!selectedBuilding) return null;

    const building = selectedBuilding;
    const baseTabs = [
      { key: 'overview', label: 'Overview' },
      { key: 'reviews', label: 'Reviews' },
      { key: 'improvements', label: 'Improvements' },
      { key: 'pca-tracker', label: 'PCA Tracker' },
      { key: 'documents', label: 'Documents' },
      { key: 'inspection', label: 'Inspection' },
      { key: 'photos', label: 'Photos' }
    ];
    const tabs = baseTabs;

    const documentTypes = [
      { key: 'PCR', label: 'Property Condition Report' },
      { key: 'ESA_Phase_I', label: 'Environmental Site Assessment Phase I' },
      { key: 'ESA_Phase_II', label: 'Environmental Site Assessment Phase II' },
      { key: 'Zoning', label: 'Zoning Report' },
      { key: 'Radon', label: 'Radon Report' },
      { key: 'Appraisal', label: 'Appraisal' }
    ];

    const handleBackToDashboard = () => {
      setSelectedBuilding(null);
      setActiveTab('overview');
      if (accountType === 'lender') {
        setCurrentView('lender-dashboard');
      } else {
        setCurrentView('pm-dashboard');
      }
    };

    const handleFileUpload = async (e) => {
      if (demoMode) { toast('Sign up to use this feature', { icon: '🔒' }); e.target.value = ''; return; }
      const file = e.target.files[0];
      if (!file) return;

      // 50MB limit
      if (file.size > 50 * 1024 * 1024) {
        toast.error('File size must be under 50MB');
        return;
      }

      try {
        setUploading(true);
        await uploadPropertyDocument(building.address, file, docType, user.uid);
        toast.success('Document uploaded successfully');
        const docs = await getPropertyDocuments(building.address);
        setBuildingDocuments(docs);
      } catch (err) {
        toast.error('Upload failed: ' + err.message);
      } finally {
        setUploading(false);
        e.target.value = '';
      }
    };

    const handleDeleteDocument = async (docItem) => {
      if (demoMode) { toast('Sign up to use this feature', { icon: '🔒' }); return; }
      if (!window.confirm(`Delete "${docItem.fileName}"?`)) return;
      try {
        await deletePropertyDocument(docItem.id, docItem.storagePath);
        toast.success('Document deleted');
        const docs = await getPropertyDocuments(building.address);
        setBuildingDocuments(docs);
      } catch (err) {
        toast.error('Delete failed: ' + err.message);
      }
    };

    const handleSaveInspection = async () => {
      if (demoMode) { toast('Sign up to use this feature', { icon: '🔒' }); return; }
      if (!inspectionComments.trim()) {
        toast.error('Please add inspection comments');
        return;
      }
      try {
        setSavingInspection(true);
        await saveInspectionNote(building.address, user.uid, {
          rating: inspectionRating,
          unitsInspected: inspectionUnits,
          comments: inspectionComments
        });
        toast.success('Inspection note saved');
        setInspectionRating(3);
        setInspectionUnits('');
        setInspectionComments('');
        const notes = await getInspectionNotes(building.address);
        setInspectionNotes(notes);
      } catch (err) {
        toast.error('Save failed: ' + err.message);
      } finally {
        setSavingInspection(false);
      }
    };

    // Group documents by type
    const docsByType = {};
    buildingDocuments.forEach(d => {
      if (!docsByType[d.documentType]) docsByType[d.documentType] = [];
      docsByType[d.documentType].push(d);
    });

    // Sort inspection notes by date (newest first)
    const sortedNotes = [...inspectionNotes].sort((a, b) => {
      const aTime = a.createdAt?.seconds || 0;
      const bTime = b.createdAt?.seconds || 0;
      return bTime - aTime;
    });

    // Collect all review photos
    const allPhotos = (building.reviews || []).reduce((photos, review) => {
      if (review.photoURLs && Array.isArray(review.photoURLs)) {
        review.photoURLs.forEach(url => photos.push({ url, review }));
      }
      if (review.photoURL) {
        photos.push({ url: review.photoURL, review });
      }
      return photos;
    }, []);

    // Remediation handler
    const handleSubmitRemediation = async (reviewIdx) => {
      if (demoMode) { toast('Sign up to use this feature', { icon: '🔒' }); return; }
      if (!remediationDesc.trim()) { toast.error('Please enter a remediation description'); return; }
      try {
        setUploading(true);
        const review = building.reviews[reviewIdx];
        await createImprovement(
          building.address,
          user.uid,
          remediationDesc.trim(),
          review.id || null,
          remediationFile
        );
        await loadManagerBuildings();
        setRemediationOpenIdx(null);
        setRemediationDesc('');
        setRemediationFile(null);
        toast.success('Remediation submitted successfully');
      } catch (err) {
        toast.error('Failed to submit: ' + err.message);
      } finally {
        setUploading(false);
      }
    };

    // Severity helper
    const getSeverityStyle = (severity) => {
      switch (severity) {
        case 'high': return { backgroundColor: '#fee2e2', color: '#991b1b' };
        case 'medium': return { backgroundColor: '#fed7aa', color: '#9a3412' };
        default: return { backgroundColor: '#d1fae5', color: '#065f46' };
      }
    };

    // Status helper
    const getStatusStyle = (status) => {
      switch (status) {
        case 'approved': case 'completed': return { backgroundColor: '#d1fae5', color: '#065f46' };
        case 'rejected': return { backgroundColor: '#fee2e2', color: '#991b1b' };
        default: return { backgroundColor: '#fef3c7', color: '#92400e' };
      }
    };

    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
        <Navigation
          currentView={currentView}
          setCurrentView={setCurrentView}
          user={user}
          colors={colors}
          onLogout={handleLogout}
          accountType={accountType}
          demoMode={demoMode}
          onExitDemo={exitDemoMode}
        />

        <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '2rem' }}>
          {/* Back Button */}
          <button
            onClick={handleBackToDashboard}
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem', backgroundColor: 'transparent', border: '1px solid #d1d5db', borderRadius: '0.375rem', cursor: 'pointer', color: '#475569', fontSize: '0.875rem', marginBottom: '1.5rem' }}
          >
            <ArrowLeft size={16} /> Back to Dashboard
          </button>

          {/* Building Header */}
          <div style={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '0.75rem', padding: '2rem', marginBottom: '1.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
              <div>
                <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#0f172a', marginBottom: '0.5rem' }}>{building.address}</h2>
                <p style={{ color: '#64748b', fontSize: '0.875rem' }}>{building.reviews?.length || 0} reviews | {building.improvements?.length || 0} improvements</p>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <div style={{
                  padding: '0.75rem 1.5rem',
                  borderRadius: '0.75rem',
                  fontSize: '1.5rem',
                  fontWeight: '800',
                  backgroundColor: building.riskScore >= 70 ? '#fee2e2' : building.riskScore >= 50 ? '#fed7aa' : '#d1fae5',
                  color: building.riskScore >= 70 ? '#991b1b' : building.riskScore >= 50 ? '#9a3412' : '#065f46'
                }}>
                  {building.riskScore}
                </div>
                <span style={{
                  padding: '0.375rem 1rem',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  fontWeight: '700',
                  backgroundColor: building.riskLevel === 'CRITICAL' ? '#fee2e2' : building.riskLevel === 'HIGH' ? '#fed7aa' : building.riskLevel === 'MODERATE' ? '#fef3c7' : '#d1fae5',
                  color: building.riskLevel === 'CRITICAL' ? '#991b1b' : building.riskLevel === 'HIGH' ? '#9a3412' : building.riskLevel === 'MODERATE' ? '#92400e' : '#065f46'
                }}>
                  {building.riskLevel || 'LOW'}
                </span>
              </div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '1.5rem', overflowX: 'auto', backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '0.5rem', padding: '0.375rem' }}>
            {tabs.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                style={{
                  padding: '0.625rem 1.25rem',
                  backgroundColor: activeTab === tab.key ? colors.primary : 'transparent',
                  color: activeTab === tab.key ? 'white' : '#475569',
                  border: 'none',
                  borderRadius: '0.375rem',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '0.875rem',
                  whiteSpace: 'nowrap',
                  transition: 'all 0.15s'
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* ===== OVERVIEW TAB ===== */}
          {activeTab === 'overview' && (
            <div>
              {/* Risk Score Breakdown */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
                <div style={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '0.5rem', padding: '1.5rem', textAlign: 'center' }}>
                  <div style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '0.5rem' }}>Risk Score</div>
                  <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: building.riskScore >= 70 ? '#dc2626' : building.riskScore >= 50 ? '#ea580c' : '#10b981' }}>{building.riskScore}</div>
                </div>
                <div style={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '0.5rem', padding: '1.5rem', textAlign: 'center' }}>
                  <div style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '0.5rem' }}>Total Issues</div>
                  <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#0f172a' }}>{building.totalIssues || building.reviews?.length || 0}</div>
                </div>
                <div style={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '0.5rem', padding: '1.5rem', textAlign: 'center' }}>
                  <div style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '0.5rem' }}>Outstanding</div>
                  <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#ea580c' }}>{building.outstandingIssues || 0}</div>
                </div>
                <div style={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '0.5rem', padding: '1.5rem', textAlign: 'center' }}>
                  <div style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '0.5rem' }}>Critical Issues</div>
                  <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#dc2626' }}>{building.criticalIssues || 0}</div>
                </div>
                <div style={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '0.5rem', padding: '1.5rem', textAlign: 'center' }}>
                  <div style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '0.5rem' }}>Avg Remediation</div>
                  <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: colors.primary }}>{building.avgRemediationTime != null ? building.avgRemediationTime : 'N/A'}</div>
                  {building.avgRemediationTime != null && <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>days</div>}
                </div>
              </div>

              {/* Issue Categories */}
              {building.reviews && building.reviews.length > 0 && (
                <div style={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '0.5rem', padding: '1.5rem', marginBottom: '2rem' }}>
                  <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#0f172a', marginBottom: '1rem' }}>Issue Categories</h3>
                  <IssuesByCategoryChart
                    data={(() => {
                      const categoryCounts = {};
                      building.reviews.forEach(review => {
                        const category = review.category || 'Other';
                        categoryCounts[category] = (categoryCounts[category] || 0) + 1;
                      });
                      return Object.entries(categoryCounts)
                        .map(([category, count]) => ({ category, count }))
                        .sort((a, b) => b.count - a.count)
                        .slice(0, 10);
                    })()}
                  />
                </div>
              )}
            </div>
          )}

          {/* ===== REVIEWS TAB ===== */}
          {activeTab === 'reviews' && (
            <div style={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '0.5rem', padding: '1.5rem' }}>
              <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#0f172a', marginBottom: '1.5rem' }}>Tenant Reviews ({building.reviews?.length || 0})</h3>
              {(!building.reviews || building.reviews.length === 0) ? (
                <p style={{ color: '#64748b', textAlign: 'center', padding: '2rem' }}>No reviews yet for this building.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {building.reviews.map((review, idx) => (
                    <div key={idx} style={{ border: '1px solid #e2e8f0', borderRadius: '0.5rem', padding: '1.25rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                          <span style={{ ...getSeverityStyle(review.severity), padding: '0.25rem 0.75rem', borderRadius: '0.375rem', fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase' }}>
                            {review.severity || 'low'}
                          </span>
                          <span style={{ fontSize: '0.75rem', color: '#94a3b8', backgroundColor: '#f1f5f9', padding: '0.25rem 0.75rem', borderRadius: '0.375rem' }}>
                            {review.category || 'General'}
                          </span>
                        </div>
                        <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                          {review.createdAt?.seconds ? new Date(review.createdAt.seconds * 1000).toLocaleDateString() : 'N/A'}
                        </span>
                      </div>
                      <p style={{ color: '#334155', fontSize: '0.875rem', lineHeight: '1.5', margin: 0 }}>
                        {review.description || review.comment || review.text || 'No description provided'}
                      </p>
                      {review.unitNumber && (
                        <p style={{ color: '#64748b', fontSize: '0.75rem', marginTop: '0.5rem' }}>Unit: {review.unitNumber}</p>
                      )}

                      {/* Add Remediation Button */}
                      <div style={{ marginTop: '0.75rem' }}>
                        {remediationOpenIdx !== idx ? (
                          <button
                            onClick={() => {
                              if (demoMode) { toast('Sign up to use this feature', { icon: '🔒' }); return; }
                              setRemediationOpenIdx(idx);
                              setRemediationDesc('');
                              setRemediationFile(null);
                            }}
                            style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', padding: '0.375rem 0.75rem', backgroundColor: 'transparent', border: `1px solid ${colors.primary}`, borderRadius: '0.375rem', color: colors.primary, fontSize: '0.75rem', fontWeight: '600', cursor: 'pointer' }}
                          >
                            <Plus size={14} /> Add Remediation
                          </button>
                        ) : (
                          <div style={{ marginTop: '0.5rem', padding: '1rem', backgroundColor: '#f8fafc', borderRadius: '0.5rem', border: '1px solid #e2e8f0' }}>
                            <textarea
                              value={remediationDesc}
                              onChange={(e) => setRemediationDesc(e.target.value)}
                              placeholder="Describe the remediation action taken..."
                              rows={3}
                              style={{ width: '100%', padding: '0.625rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '0.875rem', resize: 'vertical', boxSizing: 'border-box' }}
                            />
                            <div style={{ marginTop: '0.5rem' }}>
                              <label style={{ fontSize: '0.75rem', color: '#64748b', display: 'block', marginBottom: '0.25rem' }}>Evidence (optional)</label>
                              <input
                                type="file"
                                onChange={(e) => setRemediationFile(e.target.files[0] || null)}
                                style={{ fontSize: '0.75rem' }}
                              />
                            </div>
                            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
                              <button
                                onClick={() => handleSubmitRemediation(idx)}
                                style={{ padding: '0.5rem 1rem', backgroundColor: colors.primary, color: 'white', border: 'none', borderRadius: '0.375rem', fontSize: '0.8rem', fontWeight: '600', cursor: 'pointer' }}
                              >
                                Submit
                              </button>
                              <button
                                onClick={() => { setRemediationOpenIdx(null); setRemediationDesc(''); setRemediationFile(null); }}
                                style={{ padding: '0.5rem 1rem', backgroundColor: 'transparent', color: '#64748b', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '0.8rem', cursor: 'pointer' }}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ===== IMPROVEMENTS TAB ===== */}
          {activeTab === 'improvements' && (
            <div style={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '0.5rem', padding: '1.5rem' }}>
              <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#0f172a', marginBottom: '1.5rem' }}>Improvements ({building.improvements?.length || 0})</h3>
              {(!building.improvements || building.improvements.length === 0) ? (
                <p style={{ color: '#64748b', textAlign: 'center', padding: '2rem' }}>No improvements submitted for this building.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {building.improvements.map((improvement, idx) => (
                    <div key={idx} style={{ border: '1px solid #e2e8f0', borderRadius: '0.5rem', padding: '1.25rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                        <h4 style={{ fontSize: '0.9375rem', fontWeight: '600', color: '#0f172a', margin: 0 }}>
                          {improvement.title || improvement.category || 'Improvement'}
                        </h4>
                        <span style={{ ...getStatusStyle(improvement.status), padding: '0.25rem 0.75rem', borderRadius: '0.375rem', fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase' }}>
                          {improvement.status || 'pending'}
                        </span>
                      </div>
                      <p style={{ color: '#334155', fontSize: '0.875rem', lineHeight: '1.5', margin: 0 }}>
                        {improvement.description || 'No description provided'}
                      </p>
                      <div style={{ display: 'flex', gap: '1rem', marginTop: '0.75rem', fontSize: '0.75rem', color: '#94a3b8' }}>
                        {improvement.createdAt?.seconds && (
                          <span>Submitted: {new Date(improvement.createdAt.seconds * 1000).toLocaleDateString()}</span>
                        )}
                        {improvement.verifiedAt?.seconds && (
                          <span>Verified: {new Date(improvement.verifiedAt.seconds * 1000).toLocaleDateString()}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ===== DOCUMENTS TAB ===== */}
          {activeTab === 'documents' && (
            <div>
              {/* Upload Section */}
              <div style={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '0.5rem', padding: '1.5rem', marginBottom: '1.5rem' }}>
                <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#0f172a', marginBottom: '1rem' }}>Upload Document</h3>
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  <div style={{ flex: '1', minWidth: '200px' }}>
                    <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', color: '#475569', marginBottom: '0.375rem' }}>Document Type</label>
                    <select
                      value={docType}
                      onChange={(e) => setDocType(e.target.value)}
                      style={{ width: '100%', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '0.875rem', backgroundColor: 'white' }}
                    >
                      {documentTypes.map(dt => (
                        <option key={dt.key} value={dt.key}>{dt.label}</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ flex: '1', minWidth: '200px' }}>
                    <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', color: '#475569', marginBottom: '0.375rem' }}>File (PDF or image, max 50MB)</label>
                    <input
                      type="file"
                      accept=".pdf,image/*"
                      onChange={handleFileUpload}
                      disabled={uploading}
                      style={{ width: '100%', padding: '0.625rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '0.875rem', backgroundColor: uploading ? '#f1f5f9' : 'white' }}
                    />
                  </div>
                </div>
                {uploading && (
                  <div style={{ marginTop: '1rem', padding: '0.75rem', backgroundColor: '#eff6ff', borderRadius: '0.375rem', color: '#1d4ed8', fontSize: '0.875rem' }}>
                    Uploading document...
                  </div>
                )}
              </div>

              {/* Existing Documents */}
              <div style={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '0.5rem', padding: '1.5rem' }}>
                <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#0f172a', marginBottom: '1.5rem' }}>Uploaded Documents ({buildingDocuments.length})</h3>
                {buildingDocuments.length === 0 ? (
                  <p style={{ color: '#64748b', textAlign: 'center', padding: '2rem' }}>No documents uploaded for this building yet.</p>
                ) : (
                  <div>
                    {documentTypes.map(dt => {
                      const docs = docsByType[dt.key];
                      if (!docs || docs.length === 0) return null;
                      return (
                        <div key={dt.key} style={{ marginBottom: '1.5rem' }}>
                          <h4 style={{ fontSize: '0.9375rem', fontWeight: '600', color: '#334155', marginBottom: '0.75rem', paddingBottom: '0.5rem', borderBottom: '1px solid #e2e8f0' }}>
                            {dt.label}
                          </h4>
                          {docs.map((docItem, idx) => (
                            <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem', borderRadius: '0.375rem', backgroundColor: '#f8fafc', marginBottom: '0.5rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', minWidth: 0 }}>
                                <FileText size={18} style={{ color: '#64748b', flexShrink: 0 }} />
                                <div style={{ minWidth: 0 }}>
                                  <p style={{ fontSize: '0.875rem', fontWeight: '500', color: '#0f172a', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{docItem.fileName}</p>
                                  <p style={{ fontSize: '0.75rem', color: '#94a3b8', margin: 0 }}>
                                    {docItem.uploadedAt?.seconds ? new Date(docItem.uploadedAt.seconds * 1000).toLocaleDateString() : 'N/A'}
                                    {docItem.fileSize ? ` | ${(docItem.fileSize / 1024 / 1024).toFixed(2)} MB` : ''}
                                  </p>
                                </div>
                              </div>
                              <div style={{ display: 'flex', gap: '0.5rem' }}>
                                <a
                                  href={docItem.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ padding: '0.375rem 0.75rem', backgroundColor: colors.primary, color: 'white', borderRadius: '0.375rem', fontSize: '0.75rem', fontWeight: '500', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '0.25rem' }}
                                >
                                  <Download size={14} /> Download
                                </a>
                                {docItem.uploadedBy === user?.uid && (
                                  <button
                                    onClick={() => handleDeleteDocument(docItem)}
                                    style={{ padding: '0.375rem 0.75rem', backgroundColor: '#fee2e2', color: '#991b1b', border: 'none', borderRadius: '0.375rem', cursor: 'pointer', fontSize: '0.75rem', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '0.25rem' }}
                                  >
                                    <Trash2 size={14} /> Delete
                                  </button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ===== INSPECTION TAB ===== */}
          {activeTab === 'inspection' && (
            <div>
              {/* New Inspection Form */}
              <div style={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '0.5rem', padding: '1.5rem', marginBottom: '1.5rem' }}>
                <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#0f172a', marginBottom: '1.5rem' }}>Log Inspection</h3>

                {/* Rating */}
                <div style={{ marginBottom: '1.25rem' }}>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#475569', marginBottom: '0.5rem' }}>Overall Rating (1-5)</label>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    {[1, 2, 3, 4, 5].map(num => (
                      <button
                        key={num}
                        onClick={() => setInspectionRating(num)}
                        style={{
                          width: '3rem',
                          height: '3rem',
                          borderRadius: '0.5rem',
                          border: inspectionRating === num ? `2px solid ${colors.primary}` : '1px solid #d1d5db',
                          backgroundColor: inspectionRating === num ? colors.primary : 'white',
                          color: inspectionRating === num ? 'white' : '#334155',
                          cursor: 'pointer',
                          fontWeight: '700',
                          fontSize: '1.125rem'
                        }}
                      >
                        {num}
                      </button>
                    ))}
                  </div>
                  <p style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '0.375rem' }}>1 = Poor condition, 5 = Excellent condition</p>
                </div>

                {/* Units Inspected */}
                <div style={{ marginBottom: '1.25rem' }}>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#475569', marginBottom: '0.5rem' }}>Units Inspected</label>
                  <input
                    type="text"
                    value={inspectionUnits}
                    onChange={(e) => setInspectionUnits(e.target.value)}
                    placeholder='e.g., "Units 101, 202, 305" or "All 12 units"'
                    style={{ width: '100%', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '0.875rem', boxSizing: 'border-box' }}
                  />
                </div>

                {/* Comments */}
                <div style={{ marginBottom: '1.25rem' }}>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#475569', marginBottom: '0.5rem' }}>Comments / Observations</label>
                  <textarea
                    value={inspectionComments}
                    onChange={(e) => setInspectionComments(e.target.value)}
                    placeholder="Describe inspection findings, observations, and any concerns..."
                    rows={5}
                    style={{ width: '100%', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '0.875rem', resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box' }}
                  />
                </div>

                <button
                  onClick={handleSaveInspection}
                  disabled={savingInspection}
                  style={{ padding: '0.75rem 2rem', backgroundColor: colors.primary, color: 'white', border: 'none', borderRadius: '0.375rem', cursor: 'pointer', fontWeight: '600', fontSize: '0.875rem', opacity: savingInspection ? 0.7 : 1 }}
                >
                  {savingInspection ? 'Saving...' : 'Save Inspection'}
                </button>
              </div>

              {/* Previous Inspections */}
              <div style={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '0.5rem', padding: '1.5rem' }}>
                <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#0f172a', marginBottom: '1.5rem' }}>Previous Inspections ({sortedNotes.length})</h3>
                {sortedNotes.length === 0 ? (
                  <p style={{ color: '#64748b', textAlign: 'center', padding: '2rem' }}>No inspection notes recorded yet.</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {sortedNotes.map((note, idx) => (
                      <div key={idx} style={{ border: '1px solid #e2e8f0', borderRadius: '0.5rem', padding: '1.25rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                            <div style={{
                              padding: '0.375rem 0.75rem',
                              borderRadius: '0.375rem',
                              fontWeight: '700',
                              fontSize: '0.875rem',
                              backgroundColor: note.rating >= 4 ? '#d1fae5' : note.rating >= 3 ? '#fef3c7' : '#fee2e2',
                              color: note.rating >= 4 ? '#065f46' : note.rating >= 3 ? '#92400e' : '#991b1b'
                            }}>
                              Rating: {note.rating}/5
                            </div>
                            {note.unitsInspected && (
                              <span style={{ fontSize: '0.75rem', color: '#64748b', backgroundColor: '#f1f5f9', padding: '0.25rem 0.75rem', borderRadius: '0.375rem' }}>
                                {note.unitsInspected}
                              </span>
                            )}
                          </div>
                          <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                            {note.createdAt?.seconds ? new Date(note.createdAt.seconds * 1000).toLocaleDateString() : 'N/A'}
                          </span>
                        </div>
                        <p style={{ color: '#334155', fontSize: '0.875rem', lineHeight: '1.6', margin: 0, whiteSpace: 'pre-wrap' }}>
                          {note.comments}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ===== PHOTOS TAB ===== */}
          {activeTab === 'photos' && (
            <div style={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '0.5rem', padding: '1.5rem' }}>
              <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#0f172a', marginBottom: '1.5rem' }}>Review Photos ({allPhotos.length})</h3>
              {allPhotos.length === 0 ? (
                <p style={{ color: '#64748b', textAlign: 'center', padding: '2rem' }}>No photos have been submitted with reviews for this building.</p>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '1rem' }}>
                  {allPhotos.map((photo, idx) => (
                    <div key={idx} style={{ borderRadius: '0.5rem', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                      <img
                        src={photo.url}
                        alt={`Review photo ${idx + 1}`}
                        style={{ width: '100%', height: '200px', objectFit: 'cover', display: 'block' }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                      <div style={{ padding: '0.75rem', backgroundColor: '#f8fafc' }}>
                        <p style={{ fontSize: '0.75rem', color: '#64748b', margin: 0 }}>
                          {photo.review.category || 'General'} | {photo.review.severity || 'low'} severity
                        </p>
                        <p style={{ fontSize: '0.75rem', color: '#94a3b8', margin: '0.25rem 0 0 0' }}>
                          {photo.review.createdAt?.seconds ? new Date(photo.review.createdAt.seconds * 1000).toLocaleDateString() : ''}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ===== IMMEDIATE REPAIRS TAB (Lender Only) ===== */}
          {/* ===== PCA TRACKER TAB ===== */}
          {activeTab === 'pca-tracker' && (
            <div style={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '0.5rem', padding: '1.5rem' }}>
              <PCATracker
                building={building}
                user={user}
                accountType={accountType}
                demoMode={demoMode}
                colors={colors}
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  function AdvantagePage() {
    // PDF download handler for portal previews
    const handleDownloadBuildingPDF = async (buildingData) => {
      try {
        const { pdf } = await import('@react-pdf/renderer');
        const BuildingReportPDF = (await import('./BuildingReportPDF')).default;
        const { calculateBuildingRiskScore } = await import('./riskScoringEngine.js');

        const riskData = calculateBuildingRiskScore(buildingData);
        const blob = await pdf(<BuildingReportPDF building={buildingData} riskData={riskData} />).toBlob();
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Toice_Risk_Report_${buildingData.address.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
        link.click();
        URL.revokeObjectURL(url);
      } catch (err) {
        console.error('Error generating PDF:', err);
        toast.error('Failed to generate PDF. Please try again.');
      }
    };

    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(to bottom right, #0f172a, #1e293b, #0f172a)',
        backgroundImage: `
          linear-gradient(to bottom right, #0f172a, #1e293b, #0f172a),
          repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(23, 139, 155, 0.03) 35px, rgba(23, 139, 155, 0.03) 70px),
          repeating-linear-gradient(-45deg, transparent, transparent 35px, rgba(255, 133, 51, 0.02) 35px, rgba(255, 133, 51, 0.02) 70px)
        `,
        padding: '2rem'
      }}>
        <button onClick={() => setCurrentView('landing')} style={{ color: '#94a3b8', background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem', marginBottom: '2rem' }}>
          ← Back to Home
        </button>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <h2 style={{ fontSize: '3rem', fontWeight: 'bold', color: 'white', textAlign: 'center', marginBottom: '1rem' }}>
            The Toice Advantage
          </h2>
          <p style={{ color: '#94a3b8', textAlign: 'center', marginBottom: '3rem', fontSize: '1.25rem' }}>
            Comprehensive risk intelligence solutions for every stakeholder
          </p>

          {/* Risk Intelligence Report */}
          <div style={{ backgroundColor: 'rgba(30, 41, 59, 0.8)', border: '1px solid #334155', borderRadius: '1rem', padding: '2.5rem', marginBottom: '2rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
              <FileText style={{ color: colors.primary }} size={40} />
              <div>
                <h3 style={{ fontSize: '2rem', fontWeight: 'bold', color: 'white', margin: 0 }}>Risk Intelligence Report</h3>
                <p style={{ color: colors.primary, fontSize: '1.25rem', fontWeight: '600', margin: 0 }}>$499 per building</p>
              </div>
            </div>

            <div style={{ marginBottom: '2rem' }}>
              <h4 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: colors.primary, marginBottom: '1rem' }}>Why You Should Buy the Toice Risk Intelligence Report</h4>
              <p style={{ color: '#cbd5e1', lineHeight: '1.7', marginBottom: '1rem' }}>
                When underwriting or acquiring a property, you're forced to rely on:
              </p>
              <ul style={{ color: '#cbd5e1', lineHeight: '1.7', paddingLeft: '1.5rem', marginBottom: '1rem' }}>
                <li>Management-provided information</li>
                <li>Broker packages</li>
                <li>Inspections that capture only a single point in time and a small percentage of the building's units</li>
                <li>Old rent rolls and unreliable maintenance logs</li>
              </ul>
              <p style={{ color: '#cbd5e1', lineHeight: '1.7', marginBottom: '1rem' }}>
                Meanwhile, tenants — the people living there every day — have critical knowledge about building conditions that never reaches your desk.
              </p>
              <p style={{ color: 'white', lineHeight: '1.7', fontWeight: '600', marginBottom: '1rem' }}>
                Toice bridges that gap.
              </p>
            </div>

            <div style={{ marginBottom: '2rem' }}>
              <h4 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: colors.primary, marginBottom: '1rem' }}>What the Risk Intelligence Report Delivers</h4>
              <ul style={{ color: '#cbd5e1', lineHeight: '1.8', paddingLeft: '1.5rem' }}>
                <li>A complete, anonymized condition report based on verified tenant submissions</li>
                <li>Risk scoring for mold, safety hazards, heating/cooling issues, pests, water leaks</li>
                <li>An issue-by-issue profile of unresolved criticisms</li>
                <li>A "Risk Score" indicating how well the operator is addressing problems</li>
                <li>A building health summary benchmarked against similar properties in your market</li>
                <li>PDF-ready report standardized for credit committees or IC memoranda</li>
              </ul>
            </div>

            <div style={{ marginBottom: '2rem' }}>
              <h4 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: colors.primary, marginBottom: '1rem' }}>Why Risk Intelligence Report Beats Traditional Due Diligence</h4>
              <ul style={{ color: '#cbd5e1', lineHeight: '1.8', paddingLeft: '1.5rem' }}>
                <li><strong style={{ color: 'white' }}>Faster:</strong> Delivered immediately, not within weeks</li>
                <li><strong style={{ color: 'white' }}>Cheaper:</strong> At $499, it's a fraction of the cost of inspections, environmental reports, or engineering studies</li>
                <li><strong style={{ color: 'white' }}>Unbiased:</strong> Data comes from tenants, not from parties with financial stakes. It allows for in-depth knowledge of the asset to materialize, rather than brief inspections led by management trying to minimize disturbances</li>
                <li><strong style={{ color: 'white' }}>High-value risk flagging:</strong> Identifies patterns that inspectors often miss by quantifying time stamped issues and responses</li>
              </ul>
            </div>

            <div style={{ marginBottom: '2rem' }}>
              <h4 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: colors.primary, marginBottom: '1rem' }}>Perfect For</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                <div style={{ backgroundColor: 'rgba(23, 139, 155, 0.1)', border: '1px solid ' + colors.primary, borderRadius: '0.5rem', padding: '1rem' }}>
                  <p style={{ color: 'white', fontWeight: '600', marginBottom: '0.5rem' }}>Lenders</p>
                  <p style={{ color: '#94a3b8', fontSize: '0.875rem', margin: 0 }}>Make data-driven underwriting decisions</p>
                </div>
                <div style={{ backgroundColor: 'rgba(23, 139, 155, 0.1)', border: '1px solid ' + colors.primary, borderRadius: '0.5rem', padding: '1rem' }}>
                  <p style={{ color: 'white', fontWeight: '600', marginBottom: '0.5rem' }}>Insurance Companies</p>
                  <p style={{ color: '#94a3b8', fontSize: '0.875rem', margin: 0 }}>Assess risk for accurate pricing</p>
                </div>
                <div style={{ backgroundColor: 'rgba(23, 139, 155, 0.1)', border: '1px solid ' + colors.primary, borderRadius: '0.5rem', padding: '1rem' }}>
                  <p style={{ color: 'white', fontWeight: '600', marginBottom: '0.5rem' }}>Property Buyers</p>
                  <p style={{ color: '#94a3b8', fontSize: '0.875rem', margin: 0 }}>Due diligence beyond inspections</p>
                </div>
              </div>
            </div>

            <button
              onClick={() => setShowPurchaseModal(true)}
              style={{ padding: '1rem 2rem', backgroundColor: colors.primary, color: 'white', border: 'none', borderRadius: '0.5rem', fontSize: '1.125rem', fontWeight: '600', cursor: 'pointer' }}
            >
              Purchase Risk Intelligence Report
            </button>
          </div>

          {/* Portfolio Subscription */}
          <div style={{ backgroundColor: 'rgba(30, 41, 59, 0.8)', border: '1px solid #334155', borderRadius: '1rem', padding: '2.5rem', marginBottom: '2rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
              <Building2 style={{ color: colors.orange }} size={40} />
              <div>
                <h3 style={{ fontSize: '2rem', fontWeight: 'bold', color: 'white', margin: 0 }}>Portfolio Subscription</h3>
              </div>
            </div>

            <div style={{ marginBottom: '2rem' }}>
              <h4 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: colors.orange, marginBottom: '1rem' }}>Why You Should Subscribe to Portfolio Monitoring</h4>
              <p style={{ color: '#cbd5e1', lineHeight: '1.7', marginBottom: '1rem' }}>
                For portfolio lenders, insurers, and institutional investors, the status quo is a black box:
              </p>
              <ul style={{ color: '#cbd5e1', lineHeight: '1.7', paddingLeft: '1.5rem', marginBottom: '1rem' }}>
                <li>You can't inspect every property every quarter</li>
                <li>Borrower-provided financial statements lag by months</li>
                <li>Problem properties surface only after defaults or claims</li>
                <li>Cross-collateral risks spread undetected across your book</li>
              </ul>
              <p style={{ color: '#cbd5e1', lineHeight: '1.7', marginBottom: '1rem' }}>
                Meanwhile, condition deterioration, tenant turnover from poor management, and deferred maintenance accumulate silently—often for years before triggering financial distress.
              </p>
              <p style={{ color: 'white', lineHeight: '1.7', fontWeight: '600', marginBottom: '1rem' }}>
                The Toice Portfolio Subscription gives you continuous, real-time monitoring across every building in your portfolio.
              </p>
            </div>

            <div style={{ marginBottom: '2rem' }}>
              <h4 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: colors.orange, marginBottom: '1rem' }}>What Portfolio Monitoring Delivers</h4>
              <ul style={{ color: '#cbd5e1', lineHeight: '1.8', paddingLeft: '1.5rem' }}>
                <li>Live dashboard tracking tenant-reported issues across all properties</li>
                <li>Automated alerts when critical issues emerge (e.g., mold, safety violations)</li>
                <li>Trend analysis showing which properties are deteriorating vs. improving</li>
                <li>Benchmarking: compare risk scores across your portfolio and against market averages</li>
                <li>Multi-user access for asset managers, underwriters, and workout teams</li>
                <li>Unlimited Risk Intelligence Report exports for credit committees and regulators</li>
                <li>Historical data retention for covenant compliance and audits</li>
              </ul>
            </div>

            <div style={{ marginBottom: '2rem' }}>
              <h4 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: colors.orange, marginBottom: '1rem' }}>Why Portfolio Monitoring Beats Quarterly Inspections</h4>
              <ul style={{ color: '#cbd5e1', lineHeight: '1.8', paddingLeft: '1.5rem' }}>
                <li><strong style={{ color: 'white' }}>Continuous, not periodic:</strong> Catch problems in real-time, not months after they start</li>
                <li><strong style={{ color: 'white' }}>Scalable:</strong> Monitor 10 or 1,000 properties without proportionally increasing inspection budgets</li>
                <li><strong style={{ color: 'white' }}>Tenant-sourced:</strong> Bypass management spin and get unfiltered ground truth</li>
                <li><strong style={{ color: 'white' }}>Defensible:</strong> Auditable third-party data proves you're monitoring collateral quality</li>
                <li><strong style={{ color: 'white' }}>Predictive:</strong> Identify high-risk properties before defaults or insurance claims materialize</li>
              </ul>
            </div>

            <div style={{ marginBottom: '2rem' }}>
              <h4 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: colors.orange, marginBottom: '1rem' }}>Perfect For</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                <div style={{ backgroundColor: 'rgba(255, 133, 51, 0.1)', border: '1px solid ' + colors.orange, borderRadius: '0.5rem', padding: '1rem' }}>
                  <p style={{ color: 'white', fontWeight: '600', marginBottom: '0.5rem' }}>Portfolio Lenders</p>
                  <p style={{ color: '#94a3b8', fontSize: '0.875rem', margin: 0 }}>Monitor loan collateral continuously</p>
                </div>
                <div style={{ backgroundColor: 'rgba(255, 133, 51, 0.1)', border: '1px solid ' + colors.orange, borderRadius: '0.5rem', padding: '1rem' }}>
                  <p style={{ color: 'white', fontWeight: '600', marginBottom: '0.5rem' }}>Insurers</p>
                  <p style={{ color: '#94a3b8', fontSize: '0.875rem', margin: 0 }}>Track claims risk across portfolios</p>
                </div>
                <div style={{ backgroundColor: 'rgba(255, 133, 51, 0.1)', border: '1px solid ' + colors.orange, borderRadius: '0.5rem', padding: '1rem' }}>
                  <p style={{ color: 'white', fontWeight: '600', marginBottom: '0.5rem' }}>Real Estate Investors</p>
                  <p style={{ color: '#94a3b8', fontSize: '0.875rem', margin: 0 }}>Asset management at scale</p>
                </div>
              </div>
            </div>

            <button
              onClick={() => setCurrentView('pricing')}
              style={{ padding: '1rem 2rem', backgroundColor: colors.orange, color: 'white', border: 'none', borderRadius: '0.5rem', fontSize: '1.125rem', fontWeight: '600', cursor: 'pointer' }}
            >
              View Portfolio Pricing
            </button>
          </div>

          {/* Property Management Dashboard */}
          <div style={{ backgroundColor: 'rgba(30, 41, 59, 0.8)', border: '1px solid #334155', borderRadius: '1rem', padding: '2.5rem', marginBottom: '2rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
              <Users style={{ color: colors.success }} size={40} />
              <div>
                <h3 style={{ fontSize: '2rem', fontWeight: 'bold', color: 'white', margin: 0 }}>Property Management Dashboard</h3>
              </div>
            </div>

            <div style={{ marginBottom: '2rem' }}>
              <h4 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: colors.success, marginBottom: '1rem' }}>Why Property Managers Should Subscribe</h4>
              <p style={{ color: '#cbd5e1', lineHeight: '1.7', marginBottom: '1rem' }}>
                Here's the reality: tenants are already reporting issues about your buildings—just not to you.
              </p>
              <ul style={{ color: '#cbd5e1', lineHeight: '1.7', paddingLeft: '1.5rem', marginBottom: '1rem' }}>
                <li>They file 311 complaints with the city</li>
                <li>They post negative reviews on Google and Yelp</li>
                <li>They submit condition reports to Toice and similar platforms</li>
                <li>They tell prospective tenants during building tours</li>
              </ul>
              <p style={{ color: '#cbd5e1', lineHeight: '1.7', marginBottom: '1rem' }}>
                These complaints directly affect your building's Toice Risk Score—which lenders and insurers use to price risk. A high score means higher insurance premiums, stricter loan covenants, and lower property valuations.
              </p>
              <p style={{ color: 'white', lineHeight: '1.7', fontWeight: '600', marginBottom: '1rem' }}>
                The Property Management Dashboard lets you see what tenants are saying, fix problems proactively, and prove to financial stakeholders that you're managing the property well.
              </p>
            </div>

            <div style={{ marginBottom: '2rem' }}>
              <h4 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: colors.success, marginBottom: '1rem' }}>What the Dashboard Delivers</h4>
              <ul style={{ color: '#cbd5e1', lineHeight: '1.8', paddingLeft: '1.5rem' }}>
                <li>Real-time visibility into anonymized tenant complaints and issue reports</li>
                <li>Upload photos and documentation of completed repairs to resolve open issues</li>
                <li>Track which issues are reducing your Risk Score the most</li>
                <li>Trend analysis showing improvement over time (or deterioration if unaddressed)</li>
                <li>Proof of proactive management for lenders, insurers, and ownership groups</li>
                <li>Benchmarking against similar buildings to identify gaps in maintenance quality</li>
              </ul>
            </div>

            <div style={{ marginBottom: '2rem' }}>
              <h4 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: colors.success, marginBottom: '1rem' }}>Why This Beats Reactive Management</h4>
              <ul style={{ color: '#cbd5e1', lineHeight: '1.8', paddingLeft: '1.5rem' }}>
                <li><strong style={{ color: 'white' }}>Lower insurance costs:</strong> Documenting improvements reduces your Risk Score, which insurers use to set premiums</li>
                <li><strong style={{ color: 'white' }}>Better loan terms:</strong> Lenders reward proactive operators with lower rates and looser covenants</li>
                <li><strong style={{ color: 'white' }}>Higher valuations:</strong> Well-maintained buildings with documented improvement histories command premium pricing</li>
                <li><strong style={{ color: 'white' }}>Tenant retention:</strong> Addressing issues before they escalate reduces turnover and vacancy losses</li>
                <li><strong style={{ color: 'white' }}>Legal protection:</strong> Documented responses to complaints provide defensibility in disputes</li>
              </ul>
            </div>

            <div style={{ marginBottom: '2rem' }}>
              <h4 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: colors.success, marginBottom: '1rem' }}>Perfect For</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                <div style={{ backgroundColor: 'rgba(16, 185, 129, 0.1)', border: '1px solid ' + colors.success, borderRadius: '0.5rem', padding: '1rem' }}>
                  <p style={{ color: 'white', fontWeight: '600', marginBottom: '0.5rem' }}>Property Managers</p>
                  <p style={{ color: '#94a3b8', fontSize: '0.875rem', margin: 0 }}>Proactive issue resolution</p>
                </div>
                <div style={{ backgroundColor: 'rgba(16, 185, 129, 0.1)', border: '1px solid ' + colors.success, borderRadius: '0.5rem', padding: '1rem' }}>
                  <p style={{ color: 'white', fontWeight: '600', marginBottom: '0.5rem' }}>Building Owners</p>
                  <p style={{ color: '#94a3b8', fontSize: '0.875rem', margin: 0 }}>Protect property value</p>
                </div>
                <div style={{ backgroundColor: 'rgba(16, 185, 129, 0.1)', border: '1px solid ' + colors.success, borderRadius: '0.5rem', padding: '1rem' }}>
                  <p style={{ color: 'white', fontWeight: '600', marginBottom: '0.5rem' }}>Landlords</p>
                  <p style={{ color: '#94a3b8', fontSize: '0.875rem', margin: 0 }}>Lower insurance costs</p>
                </div>
              </div>
            </div>

            <button
              onClick={() => setCurrentView('login')}
              style={{ padding: '1rem 2rem', backgroundColor: colors.success, color: 'white', border: 'none', borderRadius: '0.5rem', fontSize: '1.125rem', fontWeight: '600', cursor: 'pointer' }}
            >
              Sign In to Dashboard
            </button>
          </div>

          {/* Portal Previews */}
          <div style={{ textAlign: 'center', margin: '4rem 0 2rem' }}>
            <h3 style={{ fontSize: '2.5rem', fontWeight: 'bold', color: 'white', marginBottom: '0.5rem' }}>Client Portal Previews</h3>
            <p style={{ color: '#94a3b8', fontSize: '1.125rem' }}>See what your dashboard will look like</p>
          </div>

          {/* Property Manager Portal Preview */}
          <div style={{ backgroundColor: 'rgba(30, 41, 59, 0.8)', border: '2px solid ' + colors.success, borderRadius: '1rem', padding: '2.5rem', marginBottom: '2rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem' }}>
              <div>
                <h3 style={{ fontSize: '1.75rem', fontWeight: 'bold', color: 'white', margin: 0 }}>Property Manager Portal Preview</h3>
                <p style={{ color: '#94a3b8', fontSize: '0.875rem', margin: '0.5rem 0 0 0' }}>manager@property.com</p>
              </div>
              <button style={{ padding: '0.75rem 1.5rem', backgroundColor: 'rgba(255, 255, 255, 0.1)', color: 'white', border: '1px solid rgba(255, 255, 255, 0.2)', borderRadius: '0.5rem', fontSize: '0.875rem', fontWeight: '600', cursor: 'pointer' }}>
                Account Settings
              </button>
            </div>

            {/* Filters */}
            <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem', flexWrap: 'wrap' }}>
              <select style={{ padding: '0.5rem', borderRadius: '0.375rem', border: '1px solid #475569', backgroundColor: 'rgba(15, 23, 42, 0.5)', color: 'white', fontSize: '0.875rem' }}>
                <option>All Cities</option>
                <option>Boston</option>
                <option>New York</option>
              </select>
              <select style={{ padding: '0.5rem', borderRadius: '0.375rem', border: '1px solid #475569', backgroundColor: 'rgba(15, 23, 42, 0.5)', color: 'white', fontSize: '0.875rem' }}>
                <option>All Owners</option>
                <option>ABC Properties LLC</option>
                <option>XYZ Holdings</option>
              </select>
              <select style={{ padding: '0.5rem', borderRadius: '0.375rem', border: '1px solid #475569', backgroundColor: 'rgba(15, 23, 42, 0.5)', color: 'white', fontSize: '0.875rem' }}>
                <option>All Risk Levels</option>
                <option>Critical</option>
                <option>High</option>
                <option>Moderate</option>
                <option>Low</option>
              </select>
            </div>

            {/* Buildings List */}
            <div style={{ display: 'grid', gap: '1rem' }}>
              {[
                {
                  address: '123 Main Street, Boston MA',
                  risk: 72,
                  level: 'HIGH',
                  issues: 8,
                  outstanding: 5,
                  units: 24,
                  numberOfUnits: 24,
                  reviews: [
                    { category: 'Mold', rating: 1, comment: 'Severe mold issue in bathroom', tenant: 'Tenant A' },
                    { category: 'Heating/Cooling', rating: 2, comment: 'Heating broken for 3 weeks', tenant: 'Tenant B' },
                    { category: 'Plumbing', rating: 2, comment: 'Persistent leak in kitchen', tenant: 'Tenant C' },
                    { category: 'Safety Issue', rating: 1, comment: 'Broken stair railing', tenant: 'Tenant D' },
                    { category: 'Pest Issue', rating: 2, comment: 'Mice in common areas', tenant: 'Tenant E' },
                    { category: 'Maintenance', rating: 3, comment: 'Slow response to requests', tenant: 'Tenant F' },
                    { category: 'Plumbing', rating: 3, comment: 'Low water pressure', tenant: 'Tenant G' },
                    { category: 'Noise', rating: 4, comment: 'Thin walls between units', tenant: 'Tenant H' }
                  ],
                  improvements: [
                    { description: 'Fixed HVAC system in units 201-205', status: 'completed', date: '2024-01-15', remediationDays: 18 },
                    { description: 'Repaired stair railing', status: 'completed', date: '2024-01-20', remediationDays: 12 },
                    { description: 'Pest control treatment', status: 'approved', date: '2024-01-25', remediationDays: 21 }
                  ]
                },
                {
                  address: '456 Oak Avenue, Boston MA',
                  risk: 45,
                  level: 'MODERATE',
                  issues: 6,
                  outstanding: 2,
                  units: 18,
                  numberOfUnits: 18,
                  reviews: [
                    { category: 'Maintenance', rating: 3, comment: 'General wear and tear', tenant: 'Tenant A' },
                    { category: 'Heating/Cooling', rating: 3, comment: 'AC needs servicing', tenant: 'Tenant B' },
                    { category: 'Plumbing', rating: 4, comment: 'Minor leak fixed quickly', tenant: 'Tenant C' },
                    { category: 'Noise', rating: 4, comment: 'Occasional noise from neighbors', tenant: 'Tenant D' },
                    { category: 'Trip Hazard', rating: 3, comment: 'Uneven sidewalk', tenant: 'Tenant E' },
                    { category: 'Maintenance', rating: 4, comment: 'Good response time overall', tenant: 'Tenant F' }
                  ],
                  improvements: [
                    { description: 'HVAC servicing completed', status: 'completed', date: '2024-01-18', remediationDays: 7 },
                    { description: 'Fixed plumbing leak in unit 305', status: 'completed', date: '2024-01-22', remediationDays: 5 },
                    { description: 'Sidewalk repair scheduled', status: 'approved', date: '2024-02-01', remediationDays: 14 },
                    { description: 'Building exterior painting', status: 'approved', date: '2024-02-10', remediationDays: 10 }
                  ]
                },
                {
                  address: '789 Elm Street, Cambridge MA',
                  risk: 28,
                  level: 'LOW',
                  issues: 4,
                  outstanding: 1,
                  units: 32,
                  numberOfUnits: 32,
                  reviews: [
                    { category: 'Maintenance', rating: 5, comment: 'Excellent maintenance team', tenant: 'Tenant A' },
                    { category: 'Noise', rating: 4, comment: 'Very quiet building', tenant: 'Tenant B' },
                    { category: 'Plumbing', rating: 4, comment: 'Quick fix for minor issue', tenant: 'Tenant C' },
                    { category: 'Heating/Cooling', rating: 5, comment: 'Great climate control', tenant: 'Tenant D' }
                  ],
                  improvements: [
                    { description: 'New water heater installed', status: 'completed', date: '2024-01-10', remediationDays: 3 },
                    { description: 'Lobby renovations completed', status: 'completed', date: '2024-01-12', remediationDays: 4 },
                    { description: 'Security system upgrade', status: 'completed', date: '2024-01-20', remediationDays: 6 }
                  ]
                }
              ].map((building, idx) => (
                <div key={idx} style={{ backgroundColor: 'rgba(15, 23, 42, 0.6)', border: '1px solid #334155', borderRadius: '0.75rem', padding: '1.5rem', cursor: 'pointer', transition: 'all 0.2s' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
                    <div style={{ flex: 1 }}>
                      <h4 style={{ fontSize: '1.125rem', fontWeight: '600', color: 'white', marginBottom: '0.5rem' }}>{building.address}</h4>
                      <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
                        <div>
                          <p style={{ fontSize: '0.75rem', color: '#94a3b8', margin: 0 }}>Total Issues</p>
                          <p style={{ fontSize: '1.25rem', fontWeight: 'bold', color: 'white', margin: '0.25rem 0 0 0' }}>{building.issues}</p>
                        </div>
                        <div>
                          <p style={{ fontSize: '0.75rem', color: '#94a3b8', margin: 0 }}>Outstanding</p>
                          <p style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#ea580c', margin: '0.25rem 0 0 0' }}>{building.outstanding}</p>
                        </div>
                        <div>
                          <p style={{ fontSize: '0.75rem', color: '#94a3b8', margin: 0 }}>Units</p>
                          <p style={{ fontSize: '1.25rem', fontWeight: 'bold', color: 'white', margin: '0.25rem 0 0 0' }}>{building.units}</p>
                        </div>
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <p style={{ fontSize: '0.75rem', color: '#94a3b8', marginBottom: '0.25rem' }}>Risk Score</p>
                      <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: building.risk >= 70 ? '#dc2626' : building.risk >= 50 ? '#ea580c' : '#10b981', marginBottom: '0.5rem' }}>
                        {building.risk}
                      </div>
                      <span style={{ padding: '0.25rem 0.75rem', borderRadius: '0.375rem', fontSize: '0.75rem', fontWeight: '600', backgroundColor: building.level === 'CRITICAL' || building.level === 'HIGH' ? '#fee2e2' : building.level === 'MODERATE' ? '#fed7aa' : '#d1fae5', color: building.level === 'CRITICAL' || building.level === 'HIGH' ? '#991b1b' : building.level === 'MODERATE' ? '#9a3412' : '#065f46' }}>
                        {building.level}
                      </span>
                    </div>
                  </div>
                  <button onClick={() => handleDownloadBuildingPDF(building)} style={{ marginTop: '1rem', padding: '0.5rem 1rem', backgroundColor: colors.success, color: 'white', border: 'none', borderRadius: '0.375rem', fontSize: '0.875rem', fontWeight: '600', cursor: 'pointer' }}>
                    View Details & Download PDF Report
                  </button>
                </div>
              ))}
            </div>

            <div style={{ marginTop: '2rem', padding: '1rem', backgroundColor: 'rgba(16, 185, 129, 0.1)', border: '1px solid ' + colors.success, borderRadius: '0.5rem' }}>
              <p style={{ color: '#cbd5e1', fontSize: '0.875rem', margin: 0 }}>
                <strong style={{ color: 'white' }}>Property Manager Features:</strong> Upload photos of completed repairs, document improvements, and reduce your building's risk score to lower insurance premiums.
              </p>
            </div>
          </div>

          {/* Subscription Portal Preview */}
          <div style={{ backgroundColor: 'rgba(30, 41, 59, 0.8)', border: '2px solid ' + colors.orange, borderRadius: '1rem', padding: '2.5rem', marginBottom: '2rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem' }}>
              <div>
                <h3 style={{ fontSize: '1.75rem', fontWeight: 'bold', color: 'white', margin: 0 }}>Portfolio Subscription Portal Preview</h3>
                <p style={{ color: '#94a3b8', fontSize: '0.875rem', margin: '0.5rem 0 0 0' }}>underwriting@lender.com • Professional Plan (50 buildings)</p>
              </div>
              <button style={{ padding: '0.75rem 1.5rem', backgroundColor: 'rgba(255, 255, 255, 0.1)', color: 'white', border: '1px solid rgba(255, 255, 255, 0.2)', borderRadius: '0.5rem', fontSize: '0.875rem', fontWeight: '600', cursor: 'pointer' }}>
                Account Settings
              </button>
            </div>

            {/* Filters */}
            <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem', flexWrap: 'wrap' }}>
              <select style={{ padding: '0.5rem', borderRadius: '0.375rem', border: '1px solid #475569', backgroundColor: 'rgba(15, 23, 42, 0.5)', color: 'white', fontSize: '0.875rem' }}>
                <option>All Cities</option>
                <option>Boston</option>
                <option>New York</option>
                <option>Miami</option>
              </select>
              <select style={{ padding: '0.5rem', borderRadius: '0.375rem', border: '1px solid #475569', backgroundColor: 'rgba(15, 23, 42, 0.5)', color: 'white', fontSize: '0.875rem' }}>
                <option>All Property Managers</option>
                <option>ABC Management</option>
                <option>XYZ Property Services</option>
              </select>
              <select style={{ padding: '0.5rem', borderRadius: '0.375rem', border: '1px solid #475569', backgroundColor: 'rgba(15, 23, 42, 0.5)', color: 'white', fontSize: '0.875rem' }}>
                <option>All Owners</option>
                <option>Smith Holdings LLC</option>
                <option>Johnson Properties</option>
              </select>
              <select style={{ padding: '0.5rem', borderRadius: '0.375rem', border: '1px solid #475569', backgroundColor: 'rgba(15, 23, 42, 0.5)', color: 'white', fontSize: '0.875rem' }}>
                <option>All Risk Levels</option>
                <option>Critical</option>
                <option>High</option>
                <option>Moderate</option>
                <option>Low</option>
              </select>
            </div>

            {/* Portfolio Summary Stats */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
              <div style={{ backgroundColor: 'rgba(15, 23, 42, 0.6)', border: '1px solid #334155', borderRadius: '0.5rem', padding: '1rem' }}>
                <p style={{ fontSize: '0.75rem', color: '#94a3b8', marginBottom: '0.25rem' }}>Total Buildings</p>
                <p style={{ fontSize: '2rem', fontWeight: 'bold', color: 'white', margin: 0 }}>12</p>
              </div>
              <div style={{ backgroundColor: 'rgba(15, 23, 42, 0.6)', border: '1px solid #334155', borderRadius: '0.5rem', padding: '1rem' }}>
                <p style={{ fontSize: '0.75rem', color: '#94a3b8', marginBottom: '0.25rem' }}>Avg Risk Score</p>
                <p style={{ fontSize: '2rem', fontWeight: 'bold', color: '#ea580c', margin: 0 }}>52</p>
              </div>
              <div style={{ backgroundColor: 'rgba(15, 23, 42, 0.6)', border: '1px solid #334155', borderRadius: '0.5rem', padding: '1rem' }}>
                <p style={{ fontSize: '0.75rem', color: '#94a3b8', marginBottom: '0.25rem' }}>High Risk</p>
                <p style={{ fontSize: '2rem', fontWeight: 'bold', color: '#dc2626', margin: 0 }}>3</p>
              </div>
              <div style={{ backgroundColor: 'rgba(15, 23, 42, 0.6)', border: '1px solid #334155', borderRadius: '0.5rem', padding: '1rem' }}>
                <p style={{ fontSize: '0.75rem', color: '#94a3b8', marginBottom: '0.25rem' }}>Total Issues</p>
                <p style={{ fontSize: '2rem', fontWeight: 'bold', color: 'white', margin: 0 }}>87</p>
              </div>
              <div style={{ backgroundColor: 'rgba(15, 23, 42, 0.6)', border: '1px solid #334155', borderRadius: '0.5rem', padding: '1rem' }}>
                <p style={{ fontSize: '0.75rem', color: '#94a3b8', marginBottom: '0.25rem' }}>Avg Remediation</p>
                <p style={{ fontSize: '2rem', fontWeight: 'bold', color: '#10b981', margin: 0 }}>14<span style={{ fontSize: '0.875rem', color: '#94a3b8', marginLeft: '0.25rem' }}>days</span></p>
              </div>
            </div>

            {/* Buildings List */}
            <div style={{ display: 'grid', gap: '1rem' }}>
              {[
                {
                  address: '100 Atlantic Ave, Boston MA',
                  risk: 78,
                  level: 'HIGH',
                  issues: 12,
                  outstanding: 9,
                  manager: 'ABC Management',
                  owner: 'Smith Holdings',
                  numberOfUnits: 45,
                  reviews: [
                    { category: 'Mold', rating: 1, comment: 'Black mold in multiple units', tenant: 'Tenant A' },
                    { category: 'Safety Issue', rating: 1, comment: 'Fire alarm system malfunctioning', tenant: 'Tenant B' },
                    { category: 'Structural Issue', rating: 2, comment: 'Cracks in ceiling', tenant: 'Tenant C' },
                    { category: 'Plumbing', rating: 2, comment: 'Recurring pipe leaks', tenant: 'Tenant D' },
                    { category: 'Heating/Cooling', rating: 2, comment: 'Inconsistent heating', tenant: 'Tenant E' },
                    { category: 'Pest Issue', rating: 2, comment: 'Rodent problem', tenant: 'Tenant F' },
                    { category: 'Moisture', rating: 1, comment: 'Water damage in basement', tenant: 'Tenant G' },
                    { category: 'Security Concern', rating: 3, comment: 'Broken lobby door lock', tenant: 'Tenant H' },
                    { category: 'Trip Hazard', rating: 3, comment: 'Loose tiles in hallway', tenant: 'Tenant I' },
                    { category: 'Maintenance', rating: 2, comment: 'Very slow response times', tenant: 'Tenant J' },
                    { category: 'Noise', rating: 3, comment: 'Poor sound insulation', tenant: 'Tenant K' },
                    { category: 'Maintenance', rating: 3, comment: 'Deferred maintenance issues', tenant: 'Tenant L' }
                  ],
                  improvements: [
                    { description: 'Fire alarm system repair scheduled', status: 'approved', date: '2024-02-05', remediationDays: 28 },
                    { description: 'Mold remediation in units 402-405', status: 'approved', date: '2024-02-10', remediationDays: 35 },
                    { description: 'Basement waterproofing', status: 'approved', date: '2024-02-15', remediationDays: 42 }
                  ]
                },
                {
                  address: '250 Park Avenue, New York NY',
                  risk: 51,
                  level: 'MODERATE',
                  issues: 8,
                  outstanding: 4,
                  manager: 'XYZ Property Services',
                  owner: 'Johnson Properties',
                  numberOfUnits: 60,
                  reviews: [
                    { category: 'Maintenance', rating: 3, comment: 'Needs more proactive maintenance', tenant: 'Tenant A' },
                    { category: 'Heating/Cooling', rating: 3, comment: 'Temperature control issues', tenant: 'Tenant B' },
                    { category: 'Plumbing', rating: 3, comment: 'Old pipes need replacement', tenant: 'Tenant C' },
                    { category: 'Pest Issue', rating: 3, comment: 'Occasional pest sightings', tenant: 'Tenant D' },
                    { category: 'Noise', rating: 4, comment: 'Manageable noise levels', tenant: 'Tenant E' },
                    { category: 'Maintenance', rating: 4, comment: 'Generally responsive', tenant: 'Tenant F' },
                    { category: 'Security Concern', rating: 4, comment: 'Good security overall', tenant: 'Tenant G' },
                    { category: 'Trip Hazard', rating: 4, comment: 'Minor sidewalk issues', tenant: 'Tenant H' }
                  ],
                  improvements: [
                    { description: 'HVAC system upgrade', status: 'completed', date: '2024-01-28', remediationDays: 14 },
                    { description: 'Plumbing inspection and repairs', status: 'completed', date: '2024-01-30', remediationDays: 9 },
                    { description: 'Pest control program initiated', status: 'approved', date: '2024-02-01', remediationDays: 12 },
                    { description: 'Common area renovations', status: 'approved', date: '2024-02-12', remediationDays: 18 }
                  ]
                },
                {
                  address: '500 Ocean Drive, Miami FL',
                  risk: 33,
                  level: 'LOW',
                  issues: 5,
                  outstanding: 2,
                  manager: 'ABC Management',
                  owner: 'Smith Holdings',
                  numberOfUnits: 38,
                  reviews: [
                    { category: 'Maintenance', rating: 5, comment: 'Proactive and thorough', tenant: 'Tenant A' },
                    { category: 'Heating/Cooling', rating: 5, comment: 'Excellent AC maintenance', tenant: 'Tenant B' },
                    { category: 'Plumbing', rating: 4, comment: 'Quick response to minor issues', tenant: 'Tenant C' },
                    { category: 'Noise', rating: 4, comment: 'Well-insulated building', tenant: 'Tenant D' },
                    { category: 'Security Concern', rating: 5, comment: 'Great security system', tenant: 'Tenant E' }
                  ],
                  improvements: [
                    { description: 'Hurricane shutters installed', status: 'completed', date: '2024-01-15', remediationDays: 5 },
                    { description: 'Roof inspection and repairs', status: 'completed', date: '2024-01-18', remediationDays: 4 },
                    { description: 'Pool renovation completed', status: 'completed', date: '2024-01-25', remediationDays: 7 }
                  ]
                }
              ].map((building, idx) => (
                <div key={idx} style={{ backgroundColor: 'rgba(15, 23, 42, 0.6)', border: '1px solid #334155', borderRadius: '0.75rem', padding: '1.5rem', cursor: 'pointer', transition: 'all 0.2s' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
                    <div style={{ flex: 1 }}>
                      <h4 style={{ fontSize: '1.125rem', fontWeight: '600', color: 'white', marginBottom: '0.5rem' }}>{building.address}</h4>
                      <div style={{ marginBottom: '0.75rem' }}>
                        <p style={{ fontSize: '0.75rem', color: '#94a3b8', margin: 0 }}>Manager: <span style={{ color: '#cbd5e1' }}>{building.manager}</span></p>
                        <p style={{ fontSize: '0.75rem', color: '#94a3b8', margin: '0.25rem 0 0 0' }}>Owner: <span style={{ color: '#cbd5e1' }}>{building.owner}</span></p>
                      </div>
                      <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
                        <div>
                          <p style={{ fontSize: '0.75rem', color: '#94a3b8', margin: 0 }}>Total Issues</p>
                          <p style={{ fontSize: '1.25rem', fontWeight: 'bold', color: 'white', margin: '0.25rem 0 0 0' }}>{building.issues}</p>
                        </div>
                        <div>
                          <p style={{ fontSize: '0.75rem', color: '#94a3b8', margin: 0 }}>Outstanding</p>
                          <p style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#ea580c', margin: '0.25rem 0 0 0' }}>{building.outstanding}</p>
                        </div>
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <p style={{ fontSize: '0.75rem', color: '#94a3b8', marginBottom: '0.25rem' }}>Risk Score</p>
                      <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: building.risk >= 70 ? '#dc2626' : building.risk >= 50 ? '#ea580c' : '#10b981', marginBottom: '0.5rem' }}>
                        {building.risk}
                      </div>
                      <span style={{ padding: '0.25rem 0.75rem', borderRadius: '0.375rem', fontSize: '0.75rem', fontWeight: '600', backgroundColor: building.level === 'CRITICAL' || building.level === 'HIGH' ? '#fee2e2' : building.level === 'MODERATE' ? '#fed7aa' : '#d1fae5', color: building.level === 'CRITICAL' || building.level === 'HIGH' ? '#991b1b' : building.level === 'MODERATE' ? '#9a3412' : '#065f46' }}>
                        {building.level}
                      </span>
                    </div>
                  </div>
                  <button onClick={() => handleDownloadBuildingPDF(building)} style={{ marginTop: '1rem', padding: '0.5rem 1rem', backgroundColor: colors.orange, color: 'white', border: 'none', borderRadius: '0.375rem', fontSize: '0.875rem', fontWeight: '600', cursor: 'pointer' }}>
                    View Full Report & Export PDF
                  </button>
                </div>
              ))}
            </div>

            <div style={{ marginTop: '2rem', padding: '1rem', backgroundColor: 'rgba(255, 133, 51, 0.1)', border: '1px solid ' + colors.orange, borderRadius: '0.5rem' }}>
              <p style={{ color: '#cbd5e1', fontSize: '0.875rem', margin: 0 }}>
                <strong style={{ color: 'white' }}>Subscription Features:</strong> Monitor all collateral properties in real-time, receive alerts for critical issues, export unlimited reports, and track risk trends across your entire portfolio.
              </p>
            </div>
          </div>

          {/* Add-On Products Header */}
          <div style={{ textAlign: 'center', margin: '4rem 0 2rem' }}>
            <h3 style={{ fontSize: '2.5rem', fontWeight: 'bold', color: 'white', marginBottom: '0.5rem' }}>Add-On Products</h3>
            <p style={{ color: '#94a3b8', fontSize: '1.125rem' }}>Available for Property Management Dashboard subscribers</p>
          </div>

          {/* FireGuard */}
          <div style={{ backgroundColor: 'rgba(30, 41, 59, 0.8)', border: '1px solid #334155', borderRadius: '1rem', padding: '2.5rem', marginBottom: '2rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
              <AlertTriangle style={{ color: '#ef4444' }} size={40} />
              <div>
                <h3 style={{ fontSize: '2rem', fontWeight: 'bold', color: 'white', margin: 0 }}>FireGuard</h3>
                <p style={{ color: '#ef4444', fontSize: '1.25rem', fontWeight: '600', margin: 0 }}>Wildfire Risk Management Add-On</p>
              </div>
            </div>

            <div style={{ marginBottom: '2rem' }}>
              <h4 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#ef4444', marginBottom: '1rem' }}>Why Management Teams Use FireGuard</h4>
              <p style={{ color: '#cbd5e1', lineHeight: '1.7', marginBottom: '1rem' }}>
                Managing buildings in high-risk regions now requires more than routine maintenance. Operators must stay ahead of:
              </p>
              <ul style={{ color: '#cbd5e1', lineHeight: '1.7', paddingLeft: '1.5rem', marginBottom: '1rem' }}>
                <li>Wildfire defensible-space requirements</li>
                <li>Brush, gutter, and roof-debris management</li>
                <li>Heat-risk preparedness</li>
                <li>State and local safety regulations</li>
                <li>Insurance documentation</li>
                <li>Emergency response planning</li>
                <li>Vendor tracking for mitigation work</li>
              </ul>
              <p style={{ color: '#cbd5e1', lineHeight: '1.7', marginBottom: '1rem' }}>
                Most teams still rely on email chains, spreadsheets, and once-a-year site inspections — leaving gaps that insurers, lenders, and regulators increasingly scrutinize.
              </p>
              <p style={{ color: 'white', lineHeight: '1.7', fontWeight: '600' }}>
                FireGuard closes that compliance gap by giving owners a real-time, evidence-based risk dashboard.
              </p>
            </div>

            <div style={{ marginBottom: '2rem' }}>
              <h4 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#ef4444', marginBottom: '1rem' }}>What FireGuard Provides</h4>

              <div style={{ marginBottom: '1.5rem' }}>
                <p style={{ color: 'white', fontWeight: '600', marginBottom: '0.5rem' }}>1. Defensible-Space Tracking (Wildfire)</p>
                <ul style={{ color: '#cbd5e1', lineHeight: '1.7', paddingLeft: '1.5rem' }}>
                  <li>Satellite-assisted vegetation and roof-debris monitoring</li>
                  <li>Required clearance zone reminders (0–5 ft, 5–30 ft, 30–100 ft depending on jurisdiction)</li>
                  <li>Before/after photo documentation of cleanup work</li>
                  <li>Vendor upload portal for invoices & proof of service</li>
                </ul>
                <p style={{ color: '#94a3b8', fontSize: '0.875rem', marginTop: '0.5rem', fontStyle: 'italic' }}>
                  Perfect for presenting to insurers, lenders, and rating agencies.
                </p>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <p style={{ color: 'white', fontWeight: '600', marginBottom: '0.5rem' }}>2. Compliance Calendar</p>
                <ul style={{ color: '#cbd5e1', lineHeight: '1.7', paddingLeft: '1.5rem' }}>
                  <li>State wildfire laws (CA AB-38, SB-63, defensible-space requirements, etc.)</li>
                  <li>Annual safety certifications</li>
                  <li>Automated reminders & evidence storage</li>
                </ul>
              </div>

              <div>
                <p style={{ color: 'white', fontWeight: '600', marginBottom: '0.5rem' }}>3. Visual Dashboard</p>
                <ul style={{ color: '#cbd5e1', lineHeight: '1.7', paddingLeft: '1.5rem' }}>
                  <li>Heat-map of high-risk areas around the property</li>
                  <li>Timeline of risk trending</li>
                  <li>Quick-view of overdue compliance tasks</li>
                </ul>
              </div>
            </div>

            <div style={{ marginBottom: '2rem' }}>
              <h4 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#ef4444', marginBottom: '1rem' }}>Why It's Superior</h4>

              <div style={{ marginBottom: '1.5rem' }}>
                <p style={{ color: 'white', fontWeight: '600', marginBottom: '0.5rem' }}>Insurance-Ready Documentation</p>
                <ul style={{ color: '#cbd5e1', lineHeight: '1.7', paddingLeft: '1.5rem' }}>
                  <li>Timestamped photos</li>
                  <li>Task logs</li>
                  <li>Satellite imagery</li>
                  <li>Vendor receipts</li>
                  <li>Completed checklists</li>
                </ul>
                <p style={{ color: '#94a3b8', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                  Exactly what carriers and reinsurers want to see during underwriting.
                </p>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <p style={{ color: 'white', fontWeight: '600', marginBottom: '0.5rem' }}>Stronger Lender Relationships</p>
                <p style={{ color: '#cbd5e1', lineHeight: '1.7', marginBottom: '0.5rem' }}>
                  Lenders increasingly price environmental risk into their models. FireGuard provides:
                </p>
                <ul style={{ color: '#cbd5e1', lineHeight: '1.7', paddingLeft: '1.5rem' }}>
                  <li>Quarterly risk snapshots</li>
                  <li>Audit-ready documentation</li>
                  <li>Evidence of proactive stewardship</li>
                </ul>
                <p style={{ color: '#94a3b8', fontSize: '0.875rem', marginTop: '0.5rem', fontStyle: 'italic' }}>
                  Higher transparency → better terms → less scrutiny.
                </p>
              </div>

              <div>
                <p style={{ color: 'white', fontWeight: '600', marginBottom: '0.5rem' }}>Potentially Reduced Insurance Premiums</p>
                <p style={{ color: '#cbd5e1', lineHeight: '1.7', marginBottom: '0.5rem' }}>
                  Mitigation actions completed on schedule lower:
                </p>
                <ul style={{ color: '#cbd5e1', lineHeight: '1.7', paddingLeft: '1.5rem' }}>
                  <li>Wildfire surcharges</li>
                  <li>Named-storm deductibles</li>
                  <li>General hazard risk ratings</li>
                </ul>
                <p style={{ color: '#94a3b8', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                  This improves NOI and protects valuations.
                </p>
              </div>
            </div>

            <div style={{ marginBottom: '2rem' }}>
              <h4 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#ef4444', marginBottom: '1rem' }}>Ideal For</h4>
              <ul style={{ color: '#cbd5e1', lineHeight: '1.7', paddingLeft: '1.5rem' }}>
                <li>Multifamily owners in CA, OR, WA, AZ, CO, TX, FL, GA, NC, SC</li>
                <li>Property management teams seeking lower insurance costs</li>
                <li>Developers preparing for new defensible-space rules</li>
                <li>Investors needing risk transparency across portfolios</li>
                <li>Lenders tracking hazard exposure in their collateral base</li>
              </ul>
            </div>

            <button
              disabled
              style={{ padding: '1rem 2rem', backgroundColor: '#cbd5e1', color: '#64748b', border: 'none', borderRadius: '0.5rem', fontSize: '1.125rem', fontWeight: '600', cursor: 'not-allowed' }}
            >
              Coming Soon
            </button>
          </div>

          {/* HurricaneDefense */}
          <div style={{ backgroundColor: 'rgba(30, 41, 59, 0.8)', border: '1px solid #334155', borderRadius: '1rem', padding: '2.5rem', marginBottom: '2rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
              <AlertTriangle style={{ color: '#3b82f6' }} size={40} />
              <div>
                <h3 style={{ fontSize: '2rem', fontWeight: 'bold', color: 'white', margin: 0 }}>HurricaneDefense</h3>
                <p style={{ color: '#3b82f6', fontSize: '1.25rem', fontWeight: '600', margin: 0 }}>Hurricane Preparedness Add-On</p>
              </div>
            </div>

            <div style={{ marginBottom: '2rem' }}>
              <h4 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#3b82f6', marginBottom: '1rem' }}>Why Management Teams Use HurricaneDefense</h4>
              <p style={{ color: '#cbd5e1', lineHeight: '1.7', marginBottom: '1rem' }}>
                In hurricane-exposed regions, operators are expected to maintain year-round readiness — not just scramble when a named storm forms. Owners must manage:
              </p>
              <ul style={{ color: '#cbd5e1', lineHeight: '1.7', paddingLeft: '1.5rem', marginBottom: '1rem' }}>
                <li>Windborne-debris exposure</li>
                <li>Roof, drainage, and gutter integrity</li>
                <li>Tree-trimming and fall-hazard compliance</li>
                <li>Flood-risk preparation</li>
                <li>Generator & emergency power checks</li>
                <li>Insurer-required seasonal documentation</li>
                <li>Post-storm inspections and evidence for claims</li>
                <li>Vendor coordination under severe time pressure</li>
              </ul>
              <p style={{ color: '#cbd5e1', lineHeight: '1.7', marginBottom: '1rem' }}>
                Most property teams rely on email reminders, ad-hoc PDFs, and inconsistent vendor updates. This creates huge gaps in insurance readiness and regulatory compliance exactly when insurers are tightening standards.
              </p>
              <p style={{ color: 'white', lineHeight: '1.7', fontWeight: '600' }}>
                HurricaneDefense gives owners a unified, evidence-based readiness dashboard insurers actually trust.
              </p>
            </div>

            <div style={{ marginBottom: '2rem' }}>
              <h4 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#3b82f6', marginBottom: '1rem' }}>What HurricaneDefense Provides</h4>

              <div style={{ marginBottom: '1.5rem' }}>
                <p style={{ color: 'white', fontWeight: '600', marginBottom: '0.5rem' }}>1. Pre-Season Hardening Compliance</p>
                <ul style={{ color: '#cbd5e1', lineHeight: '1.7', paddingLeft: '1.5rem' }}>
                  <li>Smart checklists tailored to local codes</li>
                  <li>Roof-condition & drainage documentation</li>
                  <li>Tree-trimming, loose-item, and fall-hazard tracking</li>
                  <li>Gutter & scupper clearing logs</li>
                  <li>Vendor portal for uploading receipts, photos, and proof of work</li>
                </ul>
                <p style={{ color: '#94a3b8', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                  All automatically stored with timestamps for underwriting.
                </p>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <p style={{ color: 'white', fontWeight: '600', marginBottom: '0.5rem' }}>2. Storm-Window Monitoring & Alerts</p>
                <ul style={{ color: '#cbd5e1', lineHeight: '1.7', paddingLeft: '1.5rem' }}>
                  <li>NOAA feed integration</li>
                  <li>Property-specific alerts when storms enter watch/warning zones</li>
                  <li>Auto-assign tasks to maintenance before landfall</li>
                  <li>Owner visibility across entire portfolios</li>
                </ul>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <p style={{ color: 'white', fontWeight: '600', marginBottom: '0.5rem' }}>3. Post-Storm Reporting Toolkit</p>
                <ul style={{ color: '#cbd5e1', lineHeight: '1.7', paddingLeft: '1.5rem' }}>
                  <li>Required photos (roof, windows, siding, mechanicals, landscape, debris, etc.)</li>
                  <li>Automatic timestamping & geolocation</li>
                  <li>Instant export for insurance claims or lender notifications</li>
                  <li>One click generates complete evidence-backed after-action packets</li>
                </ul>
              </div>

              <div>
                <p style={{ color: 'white', fontWeight: '600', marginBottom: '0.5rem' }}>4. Evidence & Compliance Hub</p>
                <ul style={{ color: '#cbd5e1', lineHeight: '1.7', paddingLeft: '1.5rem' }}>
                  <li>Stores documentation by quarter & storm season</li>
                  <li>Includes vendor invoices, certificates, photos, and inspection records</li>
                  <li>Organizes everything into clean, audit-friendly folders</li>
                </ul>
              </div>
            </div>

            <div style={{ marginBottom: '2rem' }}>
              <h4 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#3b82f6', marginBottom: '1rem' }}>Why It's Superior</h4>

              <div style={{ marginBottom: '1.5rem' }}>
                <p style={{ color: 'white', fontWeight: '600', marginBottom: '0.5rem' }}>Insurance Negotiation Power</p>
                <p style={{ color: '#cbd5e1', lineHeight: '1.7', marginBottom: '0.5rem' }}>
                  By showing insurers:
                </p>
                <ul style={{ color: '#cbd5e1', lineHeight: '1.7', paddingLeft: '1.5rem' }}>
                  <li>Completed risk-mitigation tasks</li>
                  <li>Condition evidence</li>
                  <li>Seasonal readiness logs</li>
                  <li>Fast post-storm documentation</li>
                </ul>
                <p style={{ color: '#94a3b8', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                  …owners strengthen their position at renewal and potentially lower windstorm deductibles and surcharges.
                </p>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <p style={{ color: 'white', fontWeight: '600', marginBottom: '0.5rem' }}>Lender-Grade Transparency</p>
                <p style={{ color: '#cbd5e1', lineHeight: '1.7', marginBottom: '0.5rem' }}>
                  Many lenders now track physical-climate exposure across collateral. HurricaneDefense provides:
                </p>
                <ul style={{ color: '#cbd5e1', lineHeight: '1.7', paddingLeft: '1.5rem' }}>
                  <li>Quarterly hazard snapshots</li>
                  <li>Pre- and post-storm evidence</li>
                  <li>Portfolio-level trend charts</li>
                  <li>Automated reporting for environmental risk audits</li>
                </ul>
                <p style={{ color: '#94a3b8', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                  This increases lender trust and can reduce document requests.
                </p>
              </div>

              <div>
                <p style={{ color: 'white', fontWeight: '600', marginBottom: '0.5rem' }}>Improves NOI</p>
                <p style={{ color: '#cbd5e1', lineHeight: '1.7' }}>
                  Better storm prep → fewer insurance claims → better carrier relationships → lower premiums.
                </p>
              </div>
            </div>

            <div style={{ marginBottom: '2rem' }}>
              <h4 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#3b82f6', marginBottom: '1rem' }}>Ideal For</h4>
              <ul style={{ color: '#cbd5e1', lineHeight: '1.7', paddingLeft: '1.5rem' }}>
                <li>Multifamily properties in coastal FL, TX, LA, MS, AL, GA, SC, NC, VA</li>
                <li>Owners seeking to lower windstorm premiums</li>
                <li>Managers needing structured readiness proof</li>
              </ul>
            </div>

            <button
              disabled
              style={{ padding: '1rem 2rem', backgroundColor: '#cbd5e1', color: '#64748b', border: 'none', borderRadius: '0.5rem', fontSize: '1.125rem', fontWeight: '600', cursor: 'not-allowed' }}
            >
              Coming Soon
            </button>
          </div>
        </div>
      </div>
    );
  }

  function PricingPage() {
    // Scroll to top when pricing page loads
    useEffect(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    // PDF download handler for portal previews
    const handleDownloadBuildingPDF = async (buildingData) => {
      try {
        const { pdf } = await import('@react-pdf/renderer');
        const BuildingReportPDF = (await import('./BuildingReportPDF')).default;
        const { calculateBuildingRiskScore } = await import('./riskScoringEngine.js');

        const riskData = calculateBuildingRiskScore(buildingData);
        const blob = await pdf(<BuildingReportPDF building={buildingData} riskData={riskData} />).toBlob();
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Toice_Risk_Report_${buildingData.address.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
        link.click();
        URL.revokeObjectURL(url);
      } catch (err) {
        console.error('Error generating PDF:', err);
        toast.error('Failed to generate PDF. Please try again.');
      }
    };

    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(to bottom right, #0f172a, #1e293b, #0f172a)',
        backgroundImage: `
          linear-gradient(to bottom right, #0f172a, #1e293b, #0f172a),
          repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(23, 139, 155, 0.03) 35px, rgba(23, 139, 155, 0.03) 70px),
          repeating-linear-gradient(-45deg, transparent, transparent 35px, rgba(255, 133, 51, 0.02) 35px, rgba(255, 133, 51, 0.02) 70px)
        `,
        padding: '2rem'
      }}>
        <button onClick={() => setCurrentView('landing')} style={{ color: '#94a3b8', background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem', marginBottom: '2rem' }}>
          ← Back
        </button>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <h2 style={{ fontSize: '2.5rem', fontWeight: 'bold', color: 'white', textAlign: 'center', marginBottom: '1rem' }}>Choose Your Product</h2>
          <p style={{ color: '#94a3b8', textAlign: 'center', marginBottom: '2rem', fontSize: '1.125rem' }}>Different solutions for different stakeholders</p>

          {/* Customer Type Selector */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem', marginBottom: '4rem', maxWidth: '900px', margin: '0 auto 4rem' }}>
            <div
              onClick={() => {
                const section = document.getElementById('lenders-section');
                if (section) section.scrollIntoView({ behavior: 'smooth' });
              }}
              style={{
                background: 'linear-gradient(135deg, rgba(23, 139, 155, 0.2) 0%, rgba(23, 139, 155, 0.05) 100%)',
                border: '2px solid ' + colors.primary,
                borderRadius: '1rem',
                padding: '2rem',
                cursor: 'pointer',
                transition: 'all 0.3s',
                textAlign: 'center'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 8px 16px rgba(23, 139, 155, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <Building2 size={48} style={{ color: colors.primary, margin: '0 auto 1rem' }} />
              <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'white', marginBottom: '0.5rem' }}>
                For Lenders & Insurers
              </h3>
              <p style={{ color: '#cbd5e1', fontSize: '0.875rem', marginBottom: '1rem' }}>
                Portfolio monitoring and risk intelligence reports
              </p>
              <div style={{ fontSize: '0.75rem', color: colors.primary, fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                View Products →
              </div>
            </div>

            <div
              onClick={() => {
                const section = document.getElementById('pm-section');
                if (section) section.scrollIntoView({ behavior: 'smooth' });
              }}
              style={{
                background: 'linear-gradient(135deg, rgba(255, 133, 51, 0.2) 0%, rgba(255, 133, 51, 0.05) 100%)',
                border: '2px solid ' + colors.orange,
                borderRadius: '1rem',
                padding: '2rem',
                cursor: 'pointer',
                transition: 'all 0.3s',
                textAlign: 'center'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 8px 16px rgba(255, 133, 51, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <Users size={48} style={{ color: colors.orange, margin: '0 auto 1rem' }} />
              <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'white', marginBottom: '0.5rem' }}>
                For Property Managers
              </h3>
              <p style={{ color: '#cbd5e1', fontSize: '0.875rem', marginBottom: '1rem' }}>
                Tenant feedback platform and operations dashboard
              </p>
              <div style={{ fontSize: '0.75rem', color: colors.orange, fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                View Products →
              </div>
            </div>
          </div>

          {/* Lenders & Investors Section */}
          <div id="lenders-section" style={{ marginBottom: '5rem', paddingTop: '2rem' }}>
            <div style={{
              textAlign: 'center',
              marginBottom: '3rem',
              paddingBottom: '1.5rem',
              borderBottom: '2px solid ' + colors.primary
            }}>
              <Building2 size={40} style={{ color: colors.primary, margin: '0 auto 1rem' }} />
              <h2 style={{ fontSize: '2.5rem', fontWeight: 'bold', color: 'white', marginBottom: '0.5rem' }}>
                For Lenders & Insurers
              </h2>
              <p style={{ color: '#94a3b8', fontSize: '1.125rem' }}>
                Monitor portfolio risk and make data-driven lending decisions
              </p>
            </div>

          {/* Portfolio Dashboard Pricing */}
          <div id="portfolio-pricing" style={{ marginBottom: '3rem' }}>
            <h3 style={{ fontSize: '2rem', fontWeight: 'bold', color: 'white', textAlign: 'center', marginBottom: '0.5rem' }}>Portfolio Dashboard</h3>
            <p style={{ color: '#94a3b8', textAlign: 'center', marginBottom: '2rem' }}>For lenders and insurers managing multiple properties</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1.5rem' }}>
              {[
                { name: 'Starter', buildings: '1-10', price: '$499', users: '2 users', period: '/quarter' },
                { name: 'Professional', buildings: '11-50', price: '$1,499', users: '5 users', popular: true, period: '/quarter' },
                { name: 'Enterprise', buildings: '51-100', price: '$3,999', users: '10 users', period: '/quarter' },
                { name: 'Portfolio', buildings: '101-250', price: '$8,999', users: '25 users', period: '/quarter' },
                { name: 'Institutional', buildings: '250+', price: 'Custom', users: 'Unlimited', period: '' },
              ].map((tier, idx) => (
                <div key={idx} style={{ backgroundColor: 'rgba(30, 41, 59, 0.5)', border: tier.popular ? `2px solid ${colors.orange}` : '1px solid #334155', borderRadius: '0.75rem', padding: '1.5rem', position: 'relative' }}>
                  {tier.popular && <div style={{ position: 'absolute', top: '-12px', left: '50%', transform: 'translateX(-50%)', backgroundColor: colors.orange, color: 'white', fontSize: '0.75rem', fontWeight: 'bold', padding: '0.25rem 0.75rem', borderRadius: '1rem' }}>MOST POPULAR</div>}
                  <h4 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: 'white', marginBottom: '0.5rem', marginTop: tier.popular ? '0.5rem' : 0 }}>{tier.name}</h4>
                  <div style={{ color: '#94a3b8', fontSize: '0.875rem', marginBottom: '1rem' }}>{tier.buildings} buildings</div>
                  <div style={{ color: '#94a3b8', fontSize: '0.875rem', marginBottom: '1.5rem' }}>{tier.users}</div>
                  <button
                    disabled
                    style={{ width: '100%', padding: '0.75rem', backgroundColor: '#cbd5e1', color: '#64748b', border: 'none', borderRadius: '0.5rem', fontWeight: '600', cursor: 'not-allowed', transition: 'all 0.2s' }}
                  >
                    Coming Soon
                  </button>
                </div>
              ))}
            </div>

            {/* Detailed Portfolio Description */}
            <div style={{ maxWidth: '900px', margin: '3rem auto 0', backgroundColor: 'rgba(30, 41, 59, 0.5)', border: '1px solid #334155', borderRadius: '1rem', padding: '2.5rem' }}>
              <div style={{ marginBottom: '2rem' }}>
                <h4 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: colors.orange, marginBottom: '1rem' }}>Why You Should Subscribe to Portfolio Monitoring</h4>
                <p style={{ color: '#cbd5e1', lineHeight: '1.7', marginBottom: '1rem' }}>
                  For portfolio lenders, insurers, and institutional investors, the status quo is a black box:
                </p>
                <ul style={{ color: '#cbd5e1', lineHeight: '1.7', paddingLeft: '1.5rem', marginBottom: '1rem' }}>
                  <li>You can't inspect every property every quarter</li>
                  <li>Borrower-provided financial statements lag by months</li>
                  <li>Problem properties surface only after defaults or claims</li>
                  <li>Cross-collateral risks spread undetected across your book</li>
                </ul>
                <p style={{ color: '#cbd5e1', lineHeight: '1.7', marginBottom: '1rem' }}>
                  Meanwhile, condition deterioration, tenant turnover from poor management, and deferred maintenance accumulate silently—often for years before triggering financial distress.
                </p>
                <p style={{ color: 'white', lineHeight: '1.7', fontWeight: '600', marginBottom: '1rem' }}>
                  The Toice Portfolio Subscription gives you continuous, real-time monitoring across every building in your portfolio.
                </p>
              </div>

              <div style={{ marginBottom: '2rem' }}>
                <h4 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: colors.orange, marginBottom: '1rem' }}>What Portfolio Monitoring Delivers</h4>
                <ul style={{ color: '#cbd5e1', lineHeight: '1.8', paddingLeft: '1.5rem' }}>
                  <li>Live dashboard tracking tenant-reported issues across all properties</li>
                  <li>Automated alerts when critical issues emerge (e.g., mold, safety violations)</li>
                  <li>Trend analysis showing which properties are deteriorating vs. improving</li>
                  <li>Benchmarking: compare risk scores across your portfolio and against market averages</li>
                  <li>Multi-user access for asset managers, underwriters, and workout teams</li>
                  <li>Unlimited Risk Intelligence Report exports for credit committees and regulators</li>
                  <li>Historical data retention for covenant compliance and audits</li>
                </ul>
              </div>

              <div style={{ marginBottom: '2rem' }}>
                <h4 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: colors.orange, marginBottom: '1rem' }}>Why Portfolio Monitoring Beats Quarterly Inspections</h4>
                <ul style={{ color: '#cbd5e1', lineHeight: '1.8', paddingLeft: '1.5rem' }}>
                  <li><strong style={{ color: 'white' }}>Continuous, not periodic:</strong> Catch problems in real-time, not months after they start</li>
                  <li><strong style={{ color: 'white' }}>Scalable:</strong> Monitor 10 or 1,000 properties without proportionally increasing inspection budgets</li>
                  <li><strong style={{ color: 'white' }}>Tenant-sourced:</strong> Bypass management spin and get unfiltered ground truth</li>
                  <li><strong style={{ color: 'white' }}>Defensible:</strong> Auditable third-party data proves you're monitoring collateral quality</li>
                  <li><strong style={{ color: 'white' }}>Predictive:</strong> Identify high-risk properties before defaults or insurance claims materialize</li>
                </ul>
              </div>

              <div>
                <h4 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: colors.orange, marginBottom: '1rem' }}>Perfect For</h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                  <div style={{ backgroundColor: 'rgba(255, 133, 51, 0.1)', border: '1px solid ' + colors.orange, borderRadius: '0.5rem', padding: '1rem' }}>
                    <p style={{ color: 'white', fontWeight: '600', marginBottom: '0.5rem' }}>Portfolio Lenders</p>
                    <p style={{ color: '#94a3b8', fontSize: '0.875rem', margin: 0 }}>Monitor loan collateral continuously</p>
                  </div>
                  <div style={{ backgroundColor: 'rgba(255, 133, 51, 0.1)', border: '1px solid ' + colors.orange, borderRadius: '0.5rem', padding: '1rem' }}>
                    <p style={{ color: 'white', fontWeight: '600', marginBottom: '0.5rem' }}>Insurers</p>
                    <p style={{ color: '#94a3b8', fontSize: '0.875rem', margin: 0 }}>Track claims risk across portfolios</p>
                  </div>
                  <div style={{ backgroundColor: 'rgba(255, 133, 51, 0.1)', border: '1px solid ' + colors.orange, borderRadius: '0.5rem', padding: '1rem' }}>
                    <p style={{ color: 'white', fontWeight: '600', marginBottom: '0.5rem' }}>Real Estate Investors</p>
                    <p style={{ color: '#94a3b8', fontSize: '0.875rem', margin: 0 }}>Asset management at scale</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Risk Intelligence Report */}
          <div id="snapshot-pricing" style={{ marginBottom: '3rem' }}>
            <h3 style={{ fontSize: '2rem', fontWeight: 'bold', color: 'white', textAlign: 'center', marginBottom: '0.5rem' }}>Risk Intelligence Report</h3>
            <p style={{ color: '#94a3b8', textAlign: 'center', marginBottom: '2rem' }}>Perfect for loan underwriting or property purchases</p>
            <div style={{ maxWidth: '500px', margin: '0 auto' }}>
              <div style={{ backgroundColor: 'rgba(30, 41, 59, 0.5)', border: '1px solid #334155', borderRadius: '0.75rem', padding: '2rem' }}>
                <h4 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'white', marginBottom: '0.5rem' }}>Building Risk Report</h4>
                <div style={{ fontSize: '3rem', fontWeight: 'bold', color: 'white', marginBottom: '0.5rem' }}>$499</div>
                <div style={{ color: '#94a3b8', fontSize: '0.875rem', marginBottom: '2rem' }}>Per building</div>
                <ul style={{ color: '#cbd5e1', marginBottom: '2rem', paddingLeft: '1.5rem' }}>
                  <li style={{ marginBottom: '0.5rem' }}>Complete Toice Risk Intelligence report</li>
                  <li style={{ marginBottom: '0.5rem' }}>All tenant-reported issues</li>
                  <li style={{ marginBottom: '0.5rem' }}>Issue severity breakdown</li>
                  <li style={{ marginBottom: '0.5rem' }}>Historical trend analysis</li>
                  <li style={{ marginBottom: '0.5rem' }}>PDF export for underwriting</li>
                </ul>
                <p style={{ color: '#94a3b8', fontSize: '0.875rem', marginBottom: '1.5rem', fontStyle: 'italic' }}>
                  Available to: Lenders, insurers, and buyers with signed purchase agreements
                </p>
                <button
                  disabled
                  style={{ width: '100%', padding: '0.75rem', backgroundColor: '#cbd5e1', color: '#64748b', border: 'none', borderRadius: '0.5rem', fontWeight: '600', cursor: 'not-allowed', fontSize: '1.125rem' }}
                >
                  Coming Soon
                </button>
              </div>
            </div>

            {/* Detailed Risk Intelligence Report Description */}
            <div style={{ maxWidth: '900px', margin: '2rem auto 0', backgroundColor: 'rgba(30, 41, 59, 0.5)', border: '1px solid #334155', borderRadius: '1rem', padding: '2.5rem' }}>
              <div style={{ marginBottom: '2rem' }}>
                <h4 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: colors.primary, marginBottom: '1rem' }}>Why You Should Buy the Toice Risk Intelligence Report</h4>
                <p style={{ color: '#cbd5e1', lineHeight: '1.7', marginBottom: '1rem' }}>
                  When underwriting or acquiring a property, you're forced to rely on:
                </p>
                <ul style={{ color: '#cbd5e1', lineHeight: '1.7', paddingLeft: '1.5rem', marginBottom: '1rem' }}>
                  <li>Management-provided information</li>
                  <li>Broker packages</li>
                  <li>Inspections that capture only a single point in time and a small percentage of the building's units</li>
                  <li>Old rent rolls and unreliable maintenance logs</li>
                </ul>
                <p style={{ color: '#cbd5e1', lineHeight: '1.7', marginBottom: '1rem' }}>
                  Meanwhile, tenants — the people living there every day — have critical knowledge about building conditions that never reaches your desk.
                </p>
                <p style={{ color: 'white', lineHeight: '1.7', fontWeight: '600', marginBottom: '1rem' }}>
                  Toice bridges that gap.
                </p>
              </div>

              <div style={{ marginBottom: '2rem' }}>
                <h4 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: colors.primary, marginBottom: '1rem' }}>What the Risk Intelligence Report Delivers</h4>
                <ul style={{ color: '#cbd5e1', lineHeight: '1.8', paddingLeft: '1.5rem' }}>
                  <li>A complete, anonymized condition report based on verified tenant submissions</li>
                  <li>Risk scoring for mold, safety hazards, heating/cooling issues, pests, water leaks</li>
                  <li>An issue-by-issue profile of unresolved criticisms</li>
                  <li>A "Risk Score" indicating how well the operator is addressing problems</li>
                  <li>A building health summary benchmarked against similar properties in your market</li>
                  <li>PDF-ready report standardized for credit committees or IC memoranda</li>
                </ul>
              </div>

              <div style={{ marginBottom: '2rem' }}>
                <h4 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: colors.primary, marginBottom: '1rem' }}>Why Risk Intelligence Report Beats Traditional Due Diligence</h4>
                <ul style={{ color: '#cbd5e1', lineHeight: '1.8', paddingLeft: '1.5rem' }}>
                  <li><strong style={{ color: 'white' }}>Faster:</strong> Delivered immediately, not within weeks</li>
                  <li><strong style={{ color: 'white' }}>Cheaper:</strong> At $499, it's a fraction of the cost of inspections, environmental reports, or engineering studies</li>
                  <li><strong style={{ color: 'white' }}>Unbiased:</strong> Data comes from tenants, not from parties with financial stakes. It allows for in-depth knowledge of the asset to materialize, rather than brief inspections led by management trying to minimize disturbances</li>
                  <li><strong style={{ color: 'white' }}>High-value risk flagging:</strong> Identifies patterns that inspectors often miss by quantifying time stamped issues and responses</li>
                </ul>
              </div>

              <div>
                <h4 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: colors.primary, marginBottom: '1rem' }}>Perfect For</h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                  <div style={{ backgroundColor: 'rgba(23, 139, 155, 0.1)', border: '1px solid ' + colors.primary, borderRadius: '0.5rem', padding: '1rem' }}>
                    <p style={{ color: 'white', fontWeight: '600', marginBottom: '0.5rem' }}>Lenders</p>
                    <p style={{ color: '#94a3b8', fontSize: '0.875rem', margin: 0 }}>Make data-driven underwriting decisions</p>
                  </div>
                  <div style={{ backgroundColor: 'rgba(23, 139, 155, 0.1)', border: '1px solid ' + colors.primary, borderRadius: '0.5rem', padding: '1rem' }}>
                    <p style={{ color: 'white', fontWeight: '600', marginBottom: '0.5rem' }}>Insurance Companies</p>
                    <p style={{ color: '#94a3b8', fontSize: '0.875rem', margin: 0 }}>Assess risk for accurate pricing</p>
                  </div>
                  <div style={{ backgroundColor: 'rgba(23, 139, 155, 0.1)', border: '1px solid ' + colors.primary, borderRadius: '0.5rem', padding: '1rem' }}>
                    <p style={{ color: 'white', fontWeight: '600', marginBottom: '0.5rem' }}>Property Buyers</p>
                    <p style={{ color: '#94a3b8', fontSize: '0.875rem', margin: 0 }}>Due diligence beyond inspections</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          </div>

          {/* Property Managers Section */}
          <div id="pm-section" style={{ paddingTop: '2rem' }}>
            <div style={{
              textAlign: 'center',
              marginBottom: '3rem',
              paddingBottom: '1.5rem',
              borderBottom: '2px solid ' + colors.orange
            }}>
              <Users size={40} style={{ color: colors.orange, margin: '0 auto 1rem' }} />
              <h2 style={{ fontSize: '2.5rem', fontWeight: 'bold', color: 'white', marginBottom: '0.5rem' }}>
                For Property Managers
              </h2>
              <p style={{ color: '#94a3b8', fontSize: '1.125rem' }}>
                Stay ahead of tenant issues and reduce property risk scores
              </p>
            </div>

          {/* Property Management Dashboard Pricing */}
          <div id="pm-pricing">
            <h3 style={{ fontSize: '2rem', fontWeight: 'bold', color: 'white', textAlign: 'center', marginBottom: '0.5rem' }}>Property Management Dashboard</h3>
            <p style={{ color: '#94a3b8', textAlign: 'center', marginBottom: '2rem' }}>Monitor and resolve tenant issues proactively</p>
            <div style={{ maxWidth: '500px', margin: '0 auto' }}>
              <div style={{ backgroundColor: 'rgba(30, 41, 59, 0.5)', border: '1px solid #334155', borderRadius: '0.75rem', padding: '2rem' }}>
                <h4 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'white', marginBottom: '1.5rem' }}>PM Dashboard</h4>
                <ul style={{ color: '#cbd5e1', marginBottom: '2rem', paddingLeft: '1.5rem' }}>
                  <li style={{ marginBottom: '0.5rem' }}>See anonymized tenant issues in real-time</li>
                  <li style={{ marginBottom: '0.5rem' }}>Upload evidence of repairs</li>
                  <li style={{ marginBottom: '0.5rem' }}>Reduce your building's risk score</li>
                  <li style={{ marginBottom: '0.5rem' }}>Issue trending and analytics</li>
                  <li style={{ marginBottom: '0.5rem' }}>Tenant communication tools</li>
                </ul>
                <div style={{ backgroundColor: 'rgba(23, 139, 155, 0.1)', border: '1px solid ' + colors.primary, borderRadius: '0.5rem', padding: '1rem', marginBottom: '1.5rem' }}>
                  <p style={{ color: colors.primary, fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>Add-On Products Available:</p>
                  <ul style={{ color: '#cbd5e1', fontSize: '0.875rem', paddingLeft: '1.5rem' }}>
                    <li><strong>FireGuard</strong> – Defensible-space tracking, compliance calendar, and visual risk dashboard</li>
                    <li><strong>HurricaneDefense</strong> – Storm preparedness and recovery tracking</li>
                  </ul>
                </div>
                <button
                  disabled
                  style={{ width: '100%', padding: '0.75rem', backgroundColor: '#cbd5e1', color: '#64748b', border: 'none', borderRadius: '0.5rem', fontWeight: '600', cursor: 'not-allowed', fontSize: '1.125rem' }}
                >
                  Coming Soon
                </button>
              </div>
            </div>

            {/* Detailed Property Management Description */}
            <div style={{ maxWidth: '900px', margin: '2rem auto 0', backgroundColor: 'rgba(30, 41, 59, 0.5)', border: '1px solid #334155', borderRadius: '1rem', padding: '2.5rem' }}>
              <div style={{ marginBottom: '2rem' }}>
                <h4 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: colors.success, marginBottom: '1rem' }}>Why Property Managers Should Subscribe</h4>
                <p style={{ color: '#cbd5e1', lineHeight: '1.7', marginBottom: '1rem' }}>
                  Here's the reality: tenants are already reporting issues about your buildings—just not to you.
                </p>
                <ul style={{ color: '#cbd5e1', lineHeight: '1.7', paddingLeft: '1.5rem', marginBottom: '1rem' }}>
                  <li>They file 311 complaints with the city</li>
                  <li>They post negative reviews on Google and Yelp</li>
                  <li>They submit condition reports to Toice and similar platforms</li>
                  <li>They tell prospective tenants during building tours</li>
                </ul>
                <p style={{ color: '#cbd5e1', lineHeight: '1.7', marginBottom: '1rem' }}>
                  These complaints directly affect your building's Toice Risk Score—which lenders and insurers use to price risk. A high score means higher insurance premiums, stricter loan covenants, and lower property valuations.
                </p>
                <p style={{ color: 'white', lineHeight: '1.7', fontWeight: '600', marginBottom: '1rem' }}>
                  The Property Management Dashboard lets you see what tenants are saying, fix problems proactively, and prove to financial stakeholders that you're managing the property well.
                </p>
              </div>

              <div style={{ marginBottom: '2rem' }}>
                <h4 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: colors.success, marginBottom: '1rem' }}>What the Dashboard Delivers</h4>
                <ul style={{ color: '#cbd5e1', lineHeight: '1.8', paddingLeft: '1.5rem' }}>
                  <li>Real-time visibility into anonymized tenant complaints and issue reports</li>
                  <li>Upload photos and documentation of completed repairs to resolve open issues</li>
                  <li>Track which issues are reducing your Risk Score the most</li>
                  <li>Trend analysis showing improvement over time (or deterioration if unaddressed)</li>
                  <li>Proof of proactive management for lenders, insurers, and ownership groups</li>
                  <li>Benchmarking against similar buildings to identify gaps in maintenance quality</li>
                </ul>
              </div>

              <div style={{ marginBottom: '2rem' }}>
                <h4 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: colors.success, marginBottom: '1rem' }}>Why This Beats Reactive Management</h4>
                <ul style={{ color: '#cbd5e1', lineHeight: '1.8', paddingLeft: '1.5rem' }}>
                  <li><strong style={{ color: 'white' }}>Lower insurance costs:</strong> Documenting improvements reduces your Risk Score, which insurers use to set premiums</li>
                  <li><strong style={{ color: 'white' }}>Better loan terms:</strong> Lenders reward proactive operators with lower rates and looser covenants</li>
                  <li><strong style={{ color: 'white' }}>Higher valuations:</strong> Well-maintained buildings with documented improvement histories command premium pricing</li>
                  <li><strong style={{ color: 'white' }}>Tenant retention:</strong> Addressing issues before they escalate reduces turnover and vacancy losses</li>
                  <li><strong style={{ color: 'white' }}>Legal protection:</strong> Documented responses to complaints provide defensibility in disputes</li>
                </ul>
              </div>

              <div>
                <h4 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: colors.success, marginBottom: '1rem' }}>Perfect For</h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                  <div style={{ backgroundColor: 'rgba(16, 185, 129, 0.1)', border: '1px solid ' + colors.success, borderRadius: '0.5rem', padding: '1rem' }}>
                    <p style={{ color: 'white', fontWeight: '600', marginBottom: '0.5rem' }}>Property Managers</p>
                    <p style={{ color: '#94a3b8', fontSize: '0.875rem', margin: 0 }}>Proactive issue resolution</p>
                  </div>
                  <div style={{ backgroundColor: 'rgba(16, 185, 129, 0.1)', border: '1px solid ' + colors.success, borderRadius: '0.5rem', padding: '1rem' }}>
                    <p style={{ color: 'white', fontWeight: '600', marginBottom: '0.5rem' }}>Building Owners</p>
                    <p style={{ color: '#94a3b8', fontSize: '0.875rem', margin: 0 }}>Protect property value</p>
                  </div>
                  <div style={{ backgroundColor: 'rgba(16, 185, 129, 0.1)', border: '1px solid ' + colors.success, borderRadius: '0.5rem', padding: '1rem' }}>
                    <p style={{ color: 'white', fontWeight: '600', marginBottom: '0.5rem' }}>Landlords</p>
                    <p style={{ color: '#94a3b8', fontSize: '0.875rem', margin: 0 }}>Lower insurance costs</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Portal Previews */}
          <div style={{ textAlign: 'center', margin: '4rem 0 2rem' }}>
            <h3 style={{ fontSize: '2.5rem', fontWeight: 'bold', color: 'white', marginBottom: '0.5rem' }}>Client Portal Previews</h3>
            <p style={{ color: '#94a3b8', fontSize: '1.125rem' }}>See what your dashboard will look like</p>
          </div>

          {/* Property Manager Portal Preview */}
          <div style={{ backgroundColor: 'rgba(30, 41, 59, 0.8)', border: '2px solid ' + colors.success, borderRadius: '1rem', padding: '2.5rem', marginBottom: '2rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem' }}>
              <div>
                <h3 style={{ fontSize: '1.75rem', fontWeight: 'bold', color: 'white', margin: 0 }}>Property Manager Portal Preview</h3>
                <p style={{ color: '#94a3b8', fontSize: '0.875rem', margin: '0.5rem 0 0 0' }}>manager@property.com</p>
              </div>
              <button style={{ padding: '0.75rem 1.5rem', backgroundColor: 'rgba(255, 255, 255, 0.1)', color: 'white', border: '1px solid rgba(255, 255, 255, 0.2)', borderRadius: '0.5rem', fontSize: '0.875rem', fontWeight: '600', cursor: 'pointer' }}>
                Account Settings
              </button>
            </div>

            {/* Filters */}
            <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem', flexWrap: 'wrap' }}>
              <select style={{ padding: '0.5rem', borderRadius: '0.375rem', border: '1px solid #475569', backgroundColor: 'rgba(15, 23, 42, 0.5)', color: 'white', fontSize: '0.875rem' }}>
                <option>All Cities</option>
                <option>Boston</option>
                <option>New York</option>
              </select>
              <select style={{ padding: '0.5rem', borderRadius: '0.375rem', border: '1px solid #475569', backgroundColor: 'rgba(15, 23, 42, 0.5)', color: 'white', fontSize: '0.875rem' }}>
                <option>All Owners</option>
                <option>ABC Properties LLC</option>
                <option>XYZ Holdings</option>
              </select>
              <select style={{ padding: '0.5rem', borderRadius: '0.375rem', border: '1px solid #475569', backgroundColor: 'rgba(15, 23, 42, 0.5)', color: 'white', fontSize: '0.875rem' }}>
                <option>All Risk Levels</option>
                <option>Critical</option>
                <option>High</option>
                <option>Moderate</option>
                <option>Low</option>
              </select>
            </div>

            {/* Buildings List */}
            <div style={{ display: 'grid', gap: '1rem' }}>
              {[
                {
                  address: '123 Main Street, Boston MA',
                  risk: 72,
                  level: 'HIGH',
                  issues: 8,
                  outstanding: 5,
                  units: 24,
                  numberOfUnits: 24,
                  reviews: [
                    { category: 'Mold', rating: 1, comment: 'Severe mold issue in bathroom', tenant: 'Tenant A' },
                    { category: 'Heating/Cooling', rating: 2, comment: 'Heating broken for 3 weeks', tenant: 'Tenant B' },
                    { category: 'Plumbing', rating: 2, comment: 'Persistent leak in kitchen', tenant: 'Tenant C' },
                    { category: 'Safety Issue', rating: 1, comment: 'Broken stair railing', tenant: 'Tenant D' },
                    { category: 'Pest Issue', rating: 2, comment: 'Mice in common areas', tenant: 'Tenant E' },
                    { category: 'Maintenance', rating: 3, comment: 'Slow response to requests', tenant: 'Tenant F' },
                    { category: 'Plumbing', rating: 3, comment: 'Low water pressure', tenant: 'Tenant G' },
                    { category: 'Noise', rating: 4, comment: 'Thin walls between units', tenant: 'Tenant H' }
                  ],
                  improvements: [
                    { description: 'Fixed HVAC system in units 201-205', status: 'completed', date: '2024-01-15', remediationDays: 18 },
                    { description: 'Repaired stair railing', status: 'completed', date: '2024-01-20', remediationDays: 12 },
                    { description: 'Pest control treatment', status: 'approved', date: '2024-01-25', remediationDays: 21 }
                  ]
                },
                {
                  address: '456 Oak Avenue, Boston MA',
                  risk: 45,
                  level: 'MODERATE',
                  issues: 6,
                  outstanding: 2,
                  units: 18,
                  numberOfUnits: 18,
                  reviews: [
                    { category: 'Maintenance', rating: 3, comment: 'General wear and tear', tenant: 'Tenant A' },
                    { category: 'Heating/Cooling', rating: 3, comment: 'AC needs servicing', tenant: 'Tenant B' },
                    { category: 'Plumbing', rating: 4, comment: 'Minor leak fixed quickly', tenant: 'Tenant C' },
                    { category: 'Noise', rating: 4, comment: 'Occasional noise from neighbors', tenant: 'Tenant D' },
                    { category: 'Trip Hazard', rating: 3, comment: 'Uneven sidewalk', tenant: 'Tenant E' },
                    { category: 'Maintenance', rating: 4, comment: 'Good response time overall', tenant: 'Tenant F' }
                  ],
                  improvements: [
                    { description: 'HVAC servicing completed', status: 'completed', date: '2024-01-18', remediationDays: 7 },
                    { description: 'Fixed plumbing leak in unit 305', status: 'completed', date: '2024-01-22', remediationDays: 5 },
                    { description: 'Sidewalk repair scheduled', status: 'approved', date: '2024-02-01', remediationDays: 14 },
                    { description: 'Building exterior painting', status: 'approved', date: '2024-02-10', remediationDays: 10 }
                  ]
                },
                {
                  address: '789 Elm Street, Cambridge MA',
                  risk: 28,
                  level: 'LOW',
                  issues: 4,
                  outstanding: 1,
                  units: 32,
                  numberOfUnits: 32,
                  reviews: [
                    { category: 'Maintenance', rating: 5, comment: 'Excellent maintenance team', tenant: 'Tenant A' },
                    { category: 'Noise', rating: 4, comment: 'Very quiet building', tenant: 'Tenant B' },
                    { category: 'Plumbing', rating: 4, comment: 'Quick fix for minor issue', tenant: 'Tenant C' },
                    { category: 'Heating/Cooling', rating: 5, comment: 'Great climate control', tenant: 'Tenant D' }
                  ],
                  improvements: [
                    { description: 'New water heater installed', status: 'completed', date: '2024-01-10', remediationDays: 3 },
                    { description: 'Lobby renovations completed', status: 'completed', date: '2024-01-12', remediationDays: 4 },
                    { description: 'Security system upgrade', status: 'completed', date: '2024-01-20', remediationDays: 6 }
                  ]
                }
              ].map((building, idx) => (
                <div key={idx} style={{ backgroundColor: 'rgba(15, 23, 42, 0.6)', border: '1px solid #334155', borderRadius: '0.75rem', padding: '1.5rem', cursor: 'pointer', transition: 'all 0.2s' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
                    <div style={{ flex: 1 }}>
                      <h4 style={{ fontSize: '1.125rem', fontWeight: '600', color: 'white', marginBottom: '0.5rem' }}>{building.address}</h4>
                      <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
                        <div>
                          <p style={{ fontSize: '0.75rem', color: '#94a3b8', margin: 0 }}>Total Issues</p>
                          <p style={{ fontSize: '1.25rem', fontWeight: 'bold', color: 'white', margin: '0.25rem 0 0 0' }}>{building.issues}</p>
                        </div>
                        <div>
                          <p style={{ fontSize: '0.75rem', color: '#94a3b8', margin: 0 }}>Outstanding</p>
                          <p style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#ea580c', margin: '0.25rem 0 0 0' }}>{building.outstanding}</p>
                        </div>
                        <div>
                          <p style={{ fontSize: '0.75rem', color: '#94a3b8', margin: 0 }}>Units</p>
                          <p style={{ fontSize: '1.25rem', fontWeight: 'bold', color: 'white', margin: '0.25rem 0 0 0' }}>{building.units}</p>
                        </div>
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <p style={{ fontSize: '0.75rem', color: '#94a3b8', marginBottom: '0.25rem' }}>Risk Score</p>
                      <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: building.risk >= 70 ? '#dc2626' : building.risk >= 50 ? '#ea580c' : '#10b981', marginBottom: '0.5rem' }}>
                        {building.risk}
                      </div>
                      <span style={{ padding: '0.25rem 0.75rem', borderRadius: '0.375rem', fontSize: '0.75rem', fontWeight: '600', backgroundColor: building.level === 'CRITICAL' || building.level === 'HIGH' ? '#fee2e2' : building.level === 'MODERATE' ? '#fed7aa' : '#d1fae5', color: building.level === 'CRITICAL' || building.level === 'HIGH' ? '#991b1b' : building.level === 'MODERATE' ? '#9a3412' : '#065f46' }}>
                        {building.level}
                      </span>
                    </div>
                  </div>
                  <button onClick={() => handleDownloadBuildingPDF(building)} style={{ marginTop: '1rem', padding: '0.5rem 1rem', backgroundColor: colors.success, color: 'white', border: 'none', borderRadius: '0.375rem', fontSize: '0.875rem', fontWeight: '600', cursor: 'pointer' }}>
                    View Details & Download PDF Report
                  </button>
                </div>
              ))}
            </div>

            <div style={{ marginTop: '2rem', padding: '1rem', backgroundColor: 'rgba(16, 185, 129, 0.1)', border: '1px solid ' + colors.success, borderRadius: '0.5rem' }}>
              <p style={{ color: '#cbd5e1', fontSize: '0.875rem', margin: 0 }}>
                <strong style={{ color: 'white' }}>Property Manager Features:</strong> Upload photos of completed repairs, document improvements, and reduce your building's risk score to lower insurance premiums.
              </p>
            </div>
          </div>

          {/* Subscription Portal Preview */}
          <div style={{ backgroundColor: 'rgba(30, 41, 59, 0.8)', border: '2px solid ' + colors.orange, borderRadius: '1rem', padding: '2.5rem', marginBottom: '2rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem' }}>
              <div>
                <h3 style={{ fontSize: '1.75rem', fontWeight: 'bold', color: 'white', margin: 0 }}>Portfolio Subscription Portal Preview</h3>
                <p style={{ color: '#94a3b8', fontSize: '0.875rem', margin: '0.5rem 0 0 0' }}>underwriting@lender.com • Professional Plan (50 buildings)</p>
              </div>
              <button style={{ padding: '0.75rem 1.5rem', backgroundColor: 'rgba(255, 255, 255, 0.1)', color: 'white', border: '1px solid rgba(255, 255, 255, 0.2)', borderRadius: '0.5rem', fontSize: '0.875rem', fontWeight: '600', cursor: 'pointer' }}>
                Account Settings
              </button>
            </div>

            {/* Filters */}
            <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem', flexWrap: 'wrap' }}>
              <select style={{ padding: '0.5rem', borderRadius: '0.375rem', border: '1px solid #475569', backgroundColor: 'rgba(15, 23, 42, 0.5)', color: 'white', fontSize: '0.875rem' }}>
                <option>All Cities</option>
                <option>Boston</option>
                <option>New York</option>
                <option>Miami</option>
              </select>
              <select style={{ padding: '0.5rem', borderRadius: '0.375rem', border: '1px solid #475569', backgroundColor: 'rgba(15, 23, 42, 0.5)', color: 'white', fontSize: '0.875rem' }}>
                <option>All Property Managers</option>
                <option>ABC Management</option>
                <option>XYZ Property Services</option>
              </select>
              <select style={{ padding: '0.5rem', borderRadius: '0.375rem', border: '1px solid #475569', backgroundColor: 'rgba(15, 23, 42, 0.5)', color: 'white', fontSize: '0.875rem' }}>
                <option>All Owners</option>
                <option>Smith Holdings LLC</option>
                <option>Johnson Properties</option>
              </select>
              <select style={{ padding: '0.5rem', borderRadius: '0.375rem', border: '1px solid #475569', backgroundColor: 'rgba(15, 23, 42, 0.5)', color: 'white', fontSize: '0.875rem' }}>
                <option>All Risk Levels</option>
                <option>Critical</option>
                <option>High</option>
                <option>Moderate</option>
                <option>Low</option>
              </select>
            </div>

            {/* Portfolio Summary Stats */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
              <div style={{ backgroundColor: 'rgba(15, 23, 42, 0.6)', border: '1px solid #334155', borderRadius: '0.5rem', padding: '1rem' }}>
                <p style={{ fontSize: '0.75rem', color: '#94a3b8', marginBottom: '0.25rem' }}>Total Buildings</p>
                <p style={{ fontSize: '2rem', fontWeight: 'bold', color: 'white', margin: 0 }}>12</p>
              </div>
              <div style={{ backgroundColor: 'rgba(15, 23, 42, 0.6)', border: '1px solid #334155', borderRadius: '0.5rem', padding: '1rem' }}>
                <p style={{ fontSize: '0.75rem', color: '#94a3b8', marginBottom: '0.25rem' }}>Avg Risk Score</p>
                <p style={{ fontSize: '2rem', fontWeight: 'bold', color: '#ea580c', margin: 0 }}>52</p>
              </div>
              <div style={{ backgroundColor: 'rgba(15, 23, 42, 0.6)', border: '1px solid #334155', borderRadius: '0.5rem', padding: '1rem' }}>
                <p style={{ fontSize: '0.75rem', color: '#94a3b8', marginBottom: '0.25rem' }}>High Risk</p>
                <p style={{ fontSize: '2rem', fontWeight: 'bold', color: '#dc2626', margin: 0 }}>3</p>
              </div>
              <div style={{ backgroundColor: 'rgba(15, 23, 42, 0.6)', border: '1px solid #334155', borderRadius: '0.5rem', padding: '1rem' }}>
                <p style={{ fontSize: '0.75rem', color: '#94a3b8', marginBottom: '0.25rem' }}>Total Issues</p>
                <p style={{ fontSize: '2rem', fontWeight: 'bold', color: 'white', margin: 0 }}>87</p>
              </div>
              <div style={{ backgroundColor: 'rgba(15, 23, 42, 0.6)', border: '1px solid #334155', borderRadius: '0.5rem', padding: '1rem' }}>
                <p style={{ fontSize: '0.75rem', color: '#94a3b8', marginBottom: '0.25rem' }}>Avg Remediation</p>
                <p style={{ fontSize: '2rem', fontWeight: 'bold', color: '#10b981', margin: 0 }}>14<span style={{ fontSize: '0.875rem', color: '#94a3b8', marginLeft: '0.25rem' }}>days</span></p>
              </div>
            </div>

            {/* Buildings List */}
            <div style={{ display: 'grid', gap: '1rem' }}>
              {[
                {
                  address: '100 Atlantic Ave, Boston MA',
                  risk: 78,
                  level: 'HIGH',
                  issues: 12,
                  outstanding: 9,
                  manager: 'ABC Management',
                  owner: 'Smith Holdings',
                  numberOfUnits: 45,
                  reviews: [
                    { category: 'Mold', rating: 1, comment: 'Black mold in multiple units', tenant: 'Tenant A' },
                    { category: 'Safety Issue', rating: 1, comment: 'Fire alarm system malfunctioning', tenant: 'Tenant B' },
                    { category: 'Structural Issue', rating: 2, comment: 'Cracks in ceiling', tenant: 'Tenant C' },
                    { category: 'Plumbing', rating: 2, comment: 'Recurring pipe leaks', tenant: 'Tenant D' },
                    { category: 'Heating/Cooling', rating: 2, comment: 'Inconsistent heating', tenant: 'Tenant E' },
                    { category: 'Pest Issue', rating: 2, comment: 'Rodent problem', tenant: 'Tenant F' },
                    { category: 'Moisture', rating: 1, comment: 'Water damage in basement', tenant: 'Tenant G' },
                    { category: 'Security Concern', rating: 3, comment: 'Broken lobby door lock', tenant: 'Tenant H' },
                    { category: 'Trip Hazard', rating: 3, comment: 'Loose tiles in hallway', tenant: 'Tenant I' },
                    { category: 'Maintenance', rating: 2, comment: 'Very slow response times', tenant: 'Tenant J' },
                    { category: 'Noise', rating: 3, comment: 'Poor sound insulation', tenant: 'Tenant K' },
                    { category: 'Maintenance', rating: 3, comment: 'Deferred maintenance issues', tenant: 'Tenant L' }
                  ],
                  improvements: [
                    { description: 'Fire alarm system repair scheduled', status: 'approved', date: '2024-02-05', remediationDays: 28 },
                    { description: 'Mold remediation in units 402-405', status: 'approved', date: '2024-02-10', remediationDays: 35 },
                    { description: 'Basement waterproofing', status: 'approved', date: '2024-02-15', remediationDays: 42 }
                  ]
                },
                {
                  address: '250 Park Avenue, New York NY',
                  risk: 51,
                  level: 'MODERATE',
                  issues: 8,
                  outstanding: 4,
                  manager: 'XYZ Property Services',
                  owner: 'Johnson Properties',
                  numberOfUnits: 60,
                  reviews: [
                    { category: 'Maintenance', rating: 3, comment: 'Needs more proactive maintenance', tenant: 'Tenant A' },
                    { category: 'Heating/Cooling', rating: 3, comment: 'Temperature control issues', tenant: 'Tenant B' },
                    { category: 'Plumbing', rating: 3, comment: 'Old pipes need replacement', tenant: 'Tenant C' },
                    { category: 'Pest Issue', rating: 3, comment: 'Occasional pest sightings', tenant: 'Tenant D' },
                    { category: 'Noise', rating: 4, comment: 'Manageable noise levels', tenant: 'Tenant E' },
                    { category: 'Maintenance', rating: 4, comment: 'Generally responsive', tenant: 'Tenant F' },
                    { category: 'Security Concern', rating: 4, comment: 'Good security overall', tenant: 'Tenant G' },
                    { category: 'Trip Hazard', rating: 4, comment: 'Minor sidewalk issues', tenant: 'Tenant H' }
                  ],
                  improvements: [
                    { description: 'HVAC system upgrade', status: 'completed', date: '2024-01-28', remediationDays: 14 },
                    { description: 'Plumbing inspection and repairs', status: 'completed', date: '2024-01-30', remediationDays: 9 },
                    { description: 'Pest control program initiated', status: 'approved', date: '2024-02-01', remediationDays: 12 },
                    { description: 'Common area renovations', status: 'approved', date: '2024-02-12', remediationDays: 18 }
                  ]
                },
                {
                  address: '500 Ocean Drive, Miami FL',
                  risk: 33,
                  level: 'LOW',
                  issues: 5,
                  outstanding: 2,
                  manager: 'ABC Management',
                  owner: 'Smith Holdings',
                  numberOfUnits: 38,
                  reviews: [
                    { category: 'Maintenance', rating: 5, comment: 'Proactive and thorough', tenant: 'Tenant A' },
                    { category: 'Heating/Cooling', rating: 5, comment: 'Excellent AC maintenance', tenant: 'Tenant B' },
                    { category: 'Plumbing', rating: 4, comment: 'Quick response to minor issues', tenant: 'Tenant C' },
                    { category: 'Noise', rating: 4, comment: 'Well-insulated building', tenant: 'Tenant D' },
                    { category: 'Security Concern', rating: 5, comment: 'Great security system', tenant: 'Tenant E' }
                  ],
                  improvements: [
                    { description: 'Hurricane shutters installed', status: 'completed', date: '2024-01-15', remediationDays: 5 },
                    { description: 'Roof inspection and repairs', status: 'completed', date: '2024-01-18', remediationDays: 4 },
                    { description: 'Pool renovation completed', status: 'completed', date: '2024-01-25', remediationDays: 7 }
                  ]
                }
              ].map((building, idx) => (
                <div key={idx} style={{ backgroundColor: 'rgba(15, 23, 42, 0.6)', border: '1px solid #334155', borderRadius: '0.75rem', padding: '1.5rem', cursor: 'pointer', transition: 'all 0.2s' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
                    <div style={{ flex: 1 }}>
                      <h4 style={{ fontSize: '1.125rem', fontWeight: '600', color: 'white', marginBottom: '0.5rem' }}>{building.address}</h4>
                      <div style={{ marginBottom: '0.75rem' }}>
                        <p style={{ fontSize: '0.75rem', color: '#94a3b8', margin: 0 }}>Manager: <span style={{ color: '#cbd5e1' }}>{building.manager}</span></p>
                        <p style={{ fontSize: '0.75rem', color: '#94a3b8', margin: '0.25rem 0 0 0' }}>Owner: <span style={{ color: '#cbd5e1' }}>{building.owner}</span></p>
                      </div>
                      <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
                        <div>
                          <p style={{ fontSize: '0.75rem', color: '#94a3b8', margin: 0 }}>Total Issues</p>
                          <p style={{ fontSize: '1.25rem', fontWeight: 'bold', color: 'white', margin: '0.25rem 0 0 0' }}>{building.issues}</p>
                        </div>
                        <div>
                          <p style={{ fontSize: '0.75rem', color: '#94a3b8', margin: 0 }}>Outstanding</p>
                          <p style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#ea580c', margin: '0.25rem 0 0 0' }}>{building.outstanding}</p>
                        </div>
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <p style={{ fontSize: '0.75rem', color: '#94a3b8', marginBottom: '0.25rem' }}>Risk Score</p>
                      <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: building.risk >= 70 ? '#dc2626' : building.risk >= 50 ? '#ea580c' : '#10b981', marginBottom: '0.5rem' }}>
                        {building.risk}
                      </div>
                      <span style={{ padding: '0.25rem 0.75rem', borderRadius: '0.375rem', fontSize: '0.75rem', fontWeight: '600', backgroundColor: building.level === 'CRITICAL' || building.level === 'HIGH' ? '#fee2e2' : building.level === 'MODERATE' ? '#fed7aa' : '#d1fae5', color: building.level === 'CRITICAL' || building.level === 'HIGH' ? '#991b1b' : building.level === 'MODERATE' ? '#9a3412' : '#065f46' }}>
                        {building.level}
                      </span>
                    </div>
                  </div>
                  <button onClick={() => handleDownloadBuildingPDF(building)} style={{ marginTop: '1rem', padding: '0.5rem 1rem', backgroundColor: colors.orange, color: 'white', border: 'none', borderRadius: '0.375rem', fontSize: '0.875rem', fontWeight: '600', cursor: 'pointer' }}>
                    View Full Report & Export PDF
                  </button>
                </div>
              ))}
            </div>

            <div style={{ marginTop: '2rem', padding: '1rem', backgroundColor: 'rgba(255, 133, 51, 0.1)', border: '1px solid ' + colors.orange, borderRadius: '0.5rem' }}>
              <p style={{ color: '#cbd5e1', fontSize: '0.875rem', margin: 0 }}>
                <strong style={{ color: 'white' }}>Subscription Features:</strong> Monitor all collateral properties in real-time, receive alerts for critical issues, export unlimited reports, and track risk trends across your entire portfolio.
              </p>
            </div>
          </div>

          {/* Add-On Products Header */}
          <div style={{ textAlign: 'center', margin: '4rem 0 2rem' }}>
            <h3 style={{ fontSize: '2.5rem', fontWeight: 'bold', color: 'white', marginBottom: '0.5rem' }}>Add-On Products</h3>
            <p style={{ color: '#94a3b8', fontSize: '1.125rem' }}>Available for Property Management Dashboard subscribers</p>
          </div>

          {/* FireGuard */}
          <div style={{ backgroundColor: 'rgba(30, 41, 59, 0.8)', border: '1px solid #334155', borderRadius: '1rem', padding: '2.5rem', marginBottom: '2rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
              <AlertTriangle style={{ color: '#ef4444' }} size={40} />
              <div>
                <h3 style={{ fontSize: '2rem', fontWeight: 'bold', color: 'white', margin: 0 }}>FireGuard</h3>
                <p style={{ color: '#ef4444', fontSize: '1.25rem', fontWeight: '600', margin: 0 }}>Wildfire Risk Management Add-On</p>
              </div>
            </div>

            <div style={{ marginBottom: '2rem' }}>
              <h4 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#ef4444', marginBottom: '1rem' }}>Why Management Teams Use FireGuard</h4>
              <p style={{ color: '#cbd5e1', lineHeight: '1.7', marginBottom: '1rem' }}>
                Managing buildings in high-risk regions now requires more than routine maintenance. Operators must stay ahead of:
              </p>
              <ul style={{ color: '#cbd5e1', lineHeight: '1.7', paddingLeft: '1.5rem', marginBottom: '1rem' }}>
                <li>Wildfire defensible-space requirements</li>
                <li>Brush, gutter, and roof-debris management</li>
                <li>Heat-risk preparedness</li>
                <li>State and local safety regulations</li>
                <li>Insurance documentation</li>
                <li>Emergency response planning</li>
                <li>Vendor tracking for mitigation work</li>
              </ul>
              <p style={{ color: '#cbd5e1', lineHeight: '1.7', marginBottom: '1rem' }}>
                Most teams still rely on email chains, spreadsheets, and once-a-year site inspections — leaving gaps that insurers, lenders, and regulators increasingly scrutinize.
              </p>
              <p style={{ color: 'white', lineHeight: '1.7', fontWeight: '600' }}>
                FireGuard closes that compliance gap by giving owners a real-time, evidence-based risk dashboard.
              </p>
            </div>

            <div style={{ marginBottom: '2rem' }}>
              <h4 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#ef4444', marginBottom: '1rem' }}>What FireGuard Provides</h4>

              <div style={{ marginBottom: '1.5rem' }}>
                <p style={{ color: 'white', fontWeight: '600', marginBottom: '0.5rem' }}>1. Defensible-Space Tracking (Wildfire)</p>
                <ul style={{ color: '#cbd5e1', lineHeight: '1.7', paddingLeft: '1.5rem' }}>
                  <li>Satellite-assisted vegetation and roof-debris monitoring</li>
                  <li>Required clearance zone reminders (0–5 ft, 5–30 ft, 30–100 ft depending on jurisdiction)</li>
                  <li>Before/after photo documentation of cleanup work</li>
                  <li>Vendor upload portal for invoices & proof of service</li>
                </ul>
                <p style={{ color: '#94a3b8', fontSize: '0.875rem', marginTop: '0.5rem', fontStyle: 'italic' }}>
                  Perfect for presenting to insurers, lenders, and rating agencies.
                </p>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <p style={{ color: 'white', fontWeight: '600', marginBottom: '0.5rem' }}>2. Compliance Calendar</p>
                <ul style={{ color: '#cbd5e1', lineHeight: '1.7', paddingLeft: '1.5rem' }}>
                  <li>State wildfire laws (CA AB-38, SB-63, defensible-space requirements, etc.)</li>
                  <li>Annual safety certifications</li>
                  <li>Automated reminders & evidence storage</li>
                </ul>
              </div>

              <div>
                <p style={{ color: 'white', fontWeight: '600', marginBottom: '0.5rem' }}>3. Visual Dashboard</p>
                <ul style={{ color: '#cbd5e1', lineHeight: '1.7', paddingLeft: '1.5rem' }}>
                  <li>Heat-map of high-risk areas around the property</li>
                  <li>Timeline of risk trending</li>
                  <li>Quick-view of overdue compliance tasks</li>
                </ul>
              </div>
            </div>

            <div style={{ marginBottom: '2rem' }}>
              <h4 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#ef4444', marginBottom: '1rem' }}>Why It's Superior</h4>

              <div style={{ marginBottom: '1.5rem' }}>
                <p style={{ color: 'white', fontWeight: '600', marginBottom: '0.5rem' }}>Insurance-Ready Documentation</p>
                <ul style={{ color: '#cbd5e1', lineHeight: '1.7', paddingLeft: '1.5rem' }}>
                  <li>Timestamped photos</li>
                  <li>Task logs</li>
                  <li>Satellite imagery</li>
                  <li>Vendor receipts</li>
                  <li>Completed checklists</li>
                </ul>
                <p style={{ color: '#94a3b8', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                  Exactly what carriers and reinsurers want to see during underwriting.
                </p>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <p style={{ color: 'white', fontWeight: '600', marginBottom: '0.5rem' }}>Stronger Lender Relationships</p>
                <p style={{ color: '#cbd5e1', lineHeight: '1.7', marginBottom: '0.5rem' }}>
                  Lenders increasingly price environmental risk into their models. FireGuard provides:
                </p>
                <ul style={{ color: '#cbd5e1', lineHeight: '1.7', paddingLeft: '1.5rem' }}>
                  <li>Quarterly risk snapshots</li>
                  <li>Audit-ready documentation</li>
                  <li>Evidence of proactive stewardship</li>
                </ul>
                <p style={{ color: '#94a3b8', fontSize: '0.875rem', marginTop: '0.5rem', fontStyle: 'italic' }}>
                  Higher transparency → better terms → less scrutiny.
                </p>
              </div>

              <div>
                <p style={{ color: 'white', fontWeight: '600', marginBottom: '0.5rem' }}>Potentially Reduced Insurance Premiums</p>
                <p style={{ color: '#cbd5e1', lineHeight: '1.7', marginBottom: '0.5rem' }}>
                  Mitigation actions completed on schedule lower:
                </p>
                <ul style={{ color: '#cbd5e1', lineHeight: '1.7', paddingLeft: '1.5rem' }}>
                  <li>Wildfire surcharges</li>
                  <li>Named-storm deductibles</li>
                  <li>General hazard risk ratings</li>
                </ul>
                <p style={{ color: '#94a3b8', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                  This improves NOI and protects valuations.
                </p>
              </div>
            </div>

            <div style={{ marginBottom: '2rem' }}>
              <h4 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#ef4444', marginBottom: '1rem' }}>Ideal For</h4>
              <ul style={{ color: '#cbd5e1', lineHeight: '1.7', paddingLeft: '1.5rem' }}>
                <li>Multifamily owners in CA, OR, WA, AZ, CO, TX, FL, GA, NC, SC</li>
                <li>Property management teams seeking lower insurance costs</li>
                <li>Developers preparing for new defensible-space rules</li>
                <li>Investors needing risk transparency across portfolios</li>
                <li>Lenders tracking hazard exposure in their collateral base</li>
              </ul>
            </div>

            <button
              disabled
              style={{ padding: '1rem 2rem', backgroundColor: '#cbd5e1', color: '#64748b', border: 'none', borderRadius: '0.5rem', fontSize: '1.125rem', fontWeight: '600', cursor: 'not-allowed' }}
            >
              Coming Soon
            </button>
          </div>

          {/* HurricaneDefense */}
          <div style={{ backgroundColor: 'rgba(30, 41, 59, 0.8)', border: '1px solid #334155', borderRadius: '1rem', padding: '2.5rem', marginBottom: '2rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
              <AlertTriangle style={{ color: '#3b82f6' }} size={40} />
              <div>
                <h3 style={{ fontSize: '2rem', fontWeight: 'bold', color: 'white', margin: 0 }}>HurricaneDefense</h3>
                <p style={{ color: '#3b82f6', fontSize: '1.25rem', fontWeight: '600', margin: 0 }}>Hurricane Preparedness Add-On</p>
              </div>
            </div>

            <div style={{ marginBottom: '2rem' }}>
              <h4 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#3b82f6', marginBottom: '1rem' }}>Why Management Teams Use HurricaneDefense</h4>
              <p style={{ color: '#cbd5e1', lineHeight: '1.7', marginBottom: '1rem' }}>
                In hurricane-exposed regions, operators are expected to maintain year-round readiness — not just scramble when a named storm forms. Owners must manage:
              </p>
              <ul style={{ color: '#cbd5e1', lineHeight: '1.7', paddingLeft: '1.5rem', marginBottom: '1rem' }}>
                <li>Windborne-debris exposure</li>
                <li>Roof, drainage, and gutter integrity</li>
                <li>Tree-trimming and fall-hazard compliance</li>
                <li>Flood-risk preparation</li>
                <li>Generator & emergency power checks</li>
                <li>Insurer-required seasonal documentation</li>
                <li>Post-storm inspections and evidence for claims</li>
                <li>Vendor coordination under severe time pressure</li>
              </ul>
              <p style={{ color: '#cbd5e1', lineHeight: '1.7', marginBottom: '1rem' }}>
                Most property teams rely on email reminders, ad-hoc PDFs, and inconsistent vendor updates. This creates huge gaps in insurance readiness and regulatory compliance exactly when insurers are tightening standards.
              </p>
              <p style={{ color: 'white', lineHeight: '1.7', fontWeight: '600' }}>
                HurricaneDefense gives owners a unified, evidence-based readiness dashboard insurers actually trust.
              </p>
            </div>

            <div style={{ marginBottom: '2rem' }}>
              <h4 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#3b82f6', marginBottom: '1rem' }}>What HurricaneDefense Provides</h4>

              <div style={{ marginBottom: '1.5rem' }}>
                <p style={{ color: 'white', fontWeight: '600', marginBottom: '0.5rem' }}>1. Pre-Season Hardening Compliance</p>
                <ul style={{ color: '#cbd5e1', lineHeight: '1.7', paddingLeft: '1.5rem' }}>
                  <li>Smart checklists tailored to local codes</li>
                  <li>Roof-condition & drainage documentation</li>
                  <li>Tree-trimming, loose-item, and fall-hazard tracking</li>
                  <li>Gutter & scupper clearing logs</li>
                  <li>Vendor portal for uploading receipts, photos, and proof of work</li>
                </ul>
                <p style={{ color: '#94a3b8', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                  All automatically stored with timestamps for underwriting.
                </p>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <p style={{ color: 'white', fontWeight: '600', marginBottom: '0.5rem' }}>2. Storm-Window Monitoring & Alerts</p>
                <ul style={{ color: '#cbd5e1', lineHeight: '1.7', paddingLeft: '1.5rem' }}>
                  <li>NOAA feed integration</li>
                  <li>Property-specific alerts when storms enter watch/warning zones</li>
                  <li>Auto-assign tasks to maintenance before landfall</li>
                  <li>Owner visibility across entire portfolios</li>
                </ul>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <p style={{ color: 'white', fontWeight: '600', marginBottom: '0.5rem' }}>3. Post-Storm Reporting Toolkit</p>
                <ul style={{ color: '#cbd5e1', lineHeight: '1.7', paddingLeft: '1.5rem' }}>
                  <li>Required photos (roof, windows, siding, mechanicals, landscape, debris, etc.)</li>
                  <li>Automatic timestamping & geolocation</li>
                  <li>Instant export for insurance claims or lender notifications</li>
                  <li>One click generates complete evidence-backed after-action packets</li>
                </ul>
              </div>

              <div>
                <p style={{ color: 'white', fontWeight: '600', marginBottom: '0.5rem' }}>4. Evidence & Compliance Hub</p>
                <ul style={{ color: '#cbd5e1', lineHeight: '1.7', paddingLeft: '1.5rem' }}>
                  <li>Stores documentation by quarter & storm season</li>
                  <li>Includes vendor invoices, certificates, photos, and inspection records</li>
                  <li>Organizes everything into clean, audit-friendly folders</li>
                </ul>
              </div>
            </div>

            <div style={{ marginBottom: '2rem' }}>
              <h4 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#3b82f6', marginBottom: '1rem' }}>Why It's Superior</h4>

              <div style={{ marginBottom: '1.5rem' }}>
                <p style={{ color: 'white', fontWeight: '600', marginBottom: '0.5rem' }}>Insurance Negotiation Power</p>
                <p style={{ color: '#cbd5e1', lineHeight: '1.7', marginBottom: '0.5rem' }}>
                  By showing insurers:
                </p>
                <ul style={{ color: '#cbd5e1', lineHeight: '1.7', paddingLeft: '1.5rem' }}>
                  <li>Completed risk-mitigation tasks</li>
                  <li>Condition evidence</li>
                  <li>Seasonal readiness logs</li>
                  <li>Fast post-storm documentation</li>
                </ul>
                <p style={{ color: '#94a3b8', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                  …owners strengthen their position at renewal and potentially lower windstorm deductibles and surcharges.
                </p>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <p style={{ color: 'white', fontWeight: '600', marginBottom: '0.5rem' }}>Lender-Grade Transparency</p>
                <p style={{ color: '#cbd5e1', lineHeight: '1.7', marginBottom: '0.5rem' }}>
                  Many lenders now track physical-climate exposure across collateral. HurricaneDefense provides:
                </p>
                <ul style={{ color: '#cbd5e1', lineHeight: '1.7', paddingLeft: '1.5rem' }}>
                  <li>Quarterly hazard snapshots</li>
                  <li>Pre- and post-storm evidence</li>
                  <li>Portfolio-level trend charts</li>
                  <li>Automated reporting for environmental risk audits</li>
                </ul>
                <p style={{ color: '#94a3b8', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                  This increases lender trust and can reduce document requests.
                </p>
              </div>

              <div>
                <p style={{ color: 'white', fontWeight: '600', marginBottom: '0.5rem' }}>Improves NOI</p>
                <p style={{ color: '#cbd5e1', lineHeight: '1.7' }}>
                  Better storm prep → fewer insurance claims → better carrier relationships → lower premiums.
                </p>
              </div>
            </div>

            <div style={{ marginBottom: '2rem' }}>
              <h4 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#3b82f6', marginBottom: '1rem' }}>Ideal For</h4>
              <ul style={{ color: '#cbd5e1', lineHeight: '1.7', paddingLeft: '1.5rem' }}>
                <li>Multifamily properties in coastal FL, TX, LA, MS, AL, GA, SC, NC, VA</li>
                <li>Owners seeking to lower windstorm premiums</li>
                <li>Managers needing structured readiness proof</li>
              </ul>
            </div>

            <button
              disabled
              style={{ padding: '1rem 2rem', backgroundColor: '#cbd5e1', color: '#64748b', border: 'none', borderRadius: '0.5rem', fontSize: '1.125rem', fontWeight: '600', cursor: 'not-allowed' }}
            >
              Coming Soon
            </button>
          </div>

          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Demo Mode Banner */}
      {demoMode && (currentView === 'pm-dashboard' || currentView === 'lender-dashboard' || currentView === 'building-detail') && (
        <div style={{
          backgroundColor: colors.primary,
          color: 'white',
          padding: '0.625rem 1.5rem',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '1.5rem',
          fontSize: '0.875rem',
          fontWeight: '500',
          position: 'sticky',
          top: 0,
          zIndex: 100
        }}>
          <span>Demo Mode — You're viewing sample data</span>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button
              onClick={() => setCurrentView('pricing')}
              style={{ padding: '0.35rem 1rem', backgroundColor: 'rgba(255,255,255,0.2)', color: 'white', border: '1px solid rgba(255,255,255,0.4)', borderRadius: '0.375rem', cursor: 'pointer', fontWeight: '600', fontSize: '0.8rem' }}
            >
              See Pricing
            </button>
            <button
              onClick={exitDemoMode}
              style={{ padding: '0.35rem 1rem', backgroundColor: 'white', color: colors.primary, border: 'none', borderRadius: '0.375rem', cursor: 'pointer', fontWeight: '600', fontSize: '0.8rem' }}
            >
              Exit Demo
            </button>
          </div>
        </div>
      )}

      {currentView === 'landing' && <LandingPage />}
      {currentView === 'demo' && <DemoPage />}
      {currentView === 'tenants' && <TenantsPage />}
      {currentView === 'privacy' && <PrivacyPolicyPage />}
      {currentView === 'terms' && <TermsPage />}
      {currentView === 'delete-account' && <DeleteAccountPage />}
      {currentView === 'advantage' && <PricingPage />}
      {(currentView === 'pricing' || currentView === 'pricing-portfolio' || currentView === 'pricing-snapshot' || currentView === 'pricing-pm') && <PricingPage />}
      {currentView === 'login' && <LoginPage />}
      {currentView === 'admin' && <AdminPanel />}
      {currentView === 'dashboard' && <Dashboard />}
      {currentView === 'pm-dashboard' && <PropertyManagerDashboard />}
      {currentView === 'lender-dashboard' && <LenderDashboard />}
      {currentView === 'building-detail' && <BuildingDetailView />}

      {/* Schedule Demo Modal */}
      {showScheduleModal && <ScheduleDemoModal />}

      {/* Risk Intelligence Report Purchase Modal */}
      {showPurchaseModal && (
        <SnapshotPurchaseModal
          onClose={() => setShowPurchaseModal(false)}
          colors={colors}
        />
      )}

      {/* Toast Notifications */}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
            padding: '16px',
            borderRadius: '8px',
          },
          success: {
            duration: 3000,
            iconTheme: {
              primary: '#10b981',
              secondary: '#fff',
            },
          },
          error: {
            duration: 4000,
            iconTheme: {
              primary: '#dc2626',
              secondary: '#fff',
            },
          },
        }}
      />
    </div>
  );
}

export default ToiceB2BPlatform;